import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import conf from "../../../config.js";

// Helper: convert Roman numeral string to number for proper sorting
const romanToNum = (str) => {
    const roman = { I: 1, V: 5, X: 10, L: 50, C: 100 };
    const s = String(str).toUpperCase().trim();
    let result = 0;
    for (let i = 0; i < s.length; i++) {
        const cur = roman[s[i]];
        const next = roman[s[i + 1]];
        if (!cur) return NaN;
        if (next && cur < next) { result -= cur; } else { result += cur; }
    }
    return result || NaN;
};

// Sort kelas/tingkat values: handles numeric ("1","2"), Roman ("VII","VIII"), and alpha fallback
const sortKelas = (a, b) => {
    const numA = parseInt(a), numB = parseInt(b);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    const romA = romanToNum(a), romB = romanToNum(b);
    if (!isNaN(romA) && !isNaN(romB)) return romA - romB;
    return String(a).localeCompare(String(b));
};

// Sort rombel names: "VII A" < "VII B" < "VIII A", handles sub-class letters
const sortRombel = (a, b) => {
    const parseRombel = (name) => {
        const match = String(name).match(/^([IVXLC]+|\d+)\s*(.*)$/i);
        if (!match) return { tingkat: name, sub: '' };
        return { tingkat: match[1], sub: match[2].trim() };
    };
    const pa = parseRombel(a), pb = parseRombel(b);
    const cmp = sortKelas(pa.tingkat, pb.tingkat);
    if (cmp !== 0) return cmp;
    return pa.sub.localeCompare(pb.sub);
};

/**
 * WizardPenjadwalan - 5 Step Wizard for Smart Scheduling
 * Step 0: Pengaturan (Tahun Ajaran, Kelas, Kurikulum, Semester)
 * Step 1: Atur Waktu & Hari
 * Step 2: Atur Mapel & Guru per Rombel (Grouped by Rombel)
 * Step 3: Atur Preferensi & Constraint
 * Step 4: Generate & Review
 */
export default class WizardPenjadwalan extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            currentStep: 0,
            institusi_id: "",
            tahun_ajaran: "",
            semester: "",
            loading: false,
            saving: false,
            generating: false,
            message: null,
            messageType: "",

            // Step 0 - Pengaturan
            kelas_options: [],    // available kelas from rombel data
            selected_kelas: [],   // user-picked kelas to schedule
            kurikulum: "merdeka",
            settings_locked: false,
            data_loading: false, // true saat reload sedang menunggu semua data step ter-fetch
            tahun_ajaran_options: [],
            waktu_saved: true,
            waktu_saved_per_tingkat: {}, // { tingkat: true/false }
            pengampu_options: [],
            waktu_mode: 'sama', // 'sama' = same for all tingkat, 'per_tingkat' = different per tingkat

            // Step 1 - Waktu
            waktu_list: [],
            hari_aktif: [1, 2, 3, 4, 5],
            default_jam_mulai: "07:00",
            default_durasi: 45,
            default_jumlah_jam: 8,
            waktu_tab_hari: 1,
            waktu_active_tingkat: null, // currently selected tingkat tab in Step 1
            adding_entry: false,
            new_entry: { tipe: "istirahat", durasi: 15, keterangan: "Istirahat" },
            show_apply_modal: false,
            apply_target_days: [],
            default_istirahat_list: [{ setelah_jam: 4, durasi: 15 }],

            // Step 2 - Penugasan
            penugasan_list: [],
            rombel_options: [], guru_options: [], mapel_options: [],
            jenjang_id: null,
            importing_penugasan: false,
            show_manual_form: false,
            expanded_rombels: {},     // { rombel_id: true/false }
            penugasan_filter_kelas: "",
            penugasan_search: "",
            new_penugasan: {
                rombel_id: "", rombel_nama: "", kelas: "",
                mapel_id: "", mapel_nama: "", mapel_kode: "",
                guru_id: "", guru_nama: "", guru_nip: "",
                jumlah_jam_per_minggu: 2, kategori_mapel: "umum"
            },
            mapel_search: "",
            mapel_dropdown_open: false,
            penugasan_saved: true, // track save status for Step 2

            // Step 3 - Ketersediaan Guru
            constraint_list: [],
            constraint_guru: "", // currently selected guru for grid
            constraint_tingkat: "", // currently selected tingkat filter for Step 3
            preview_hari_tab: null, // active day tab in Step 4 timetable
            preview_tingkat: '', // active tingkat filter in Step 4 timetable3
            constraint_tab_hari: null, // active day tab in Step 3
            constraint_saved: true, // track save status
            guru_detail_modal: false, // show guru availability detail modal
            // Max jam per minggu
            max_jam_enabled: false,
            max_jam_default: 24,
            max_jam_custom: {}, // { guru_nama: max_jam }
            max_jam_panel_open: false,

            // Step 4 - Generate
            step4_mode: null, // null | 'generate' | 'input'
            mode_penyebaran: 'acak', // acak | berurutan_2 | berurutan_3 | berurutan_4 | berurutan_5 | sejumlah_jam
            generate_result: null,
            jadwal_preview: [],
            preview_hari_tab: 1,
            versi_list: [],
            versi_draft_id: null,
            show_save_versi_modal: false,
            new_versi_name: "",
            rename_versi_id: null,
            rename_versi_name: "",

            // Step 4 - Input Manual
            input_rombel: '', // currently selected rombel for input
            input_jadwal: {}, // { rombel_nama: { "hari_jamKe": { mapel_nama, mapel_kode, guru_nama, guru_nip } } }
            input_editing_cell: null, // { hari, jam_ke }
            input_saving: false,

            // Delete modal
            delete_modal: null, // { mapel_nama, guru_list, all_idxs, rombel_nama }

            // Step 4 - Manual Adjustment / Move / Swap in Generate Mode
            step4_active_cell_modal: null, // { slot, id, versi_id, rombel_nama, hari, jam_ke, target_hari, target_jam_ke, waktuLabel, is_empty, action_tab, selected_penugasan, mapel_nama, guru_nama, bentrokan_error }
            step4_is_visual_swap: false,
            step4_visual_swap_source: null, // { id, versi_id, rombel_nama, hari, jam_ke, mapel_nama, guru_nama, waktuLabel }
            step4_swap_confirm_modal: null, // { slot_a, slot_b, bentrokan_error }
            step4_cell_saving: false,
        };
    }

    componentDidMount() {
        this.getSession();
    }

    getSession() {
        const tokenApi = "Bearer " + localStorage.getItem("token");
        axios
            .post(
                `${conf.uri_backend}api/v1/auth/sso/user`,
                { token: localStorage.getItem("token") },
                { headers: { Authorization: tokenApi } }
            )
            .then((res) => {
                // Auto-compute tahun_ajaran and semester from current date
                const now = new Date();
                const month = now.getMonth() + 1;
                const year = now.getFullYear();
                let tahun_ajaran, semester;
                if (month >= 7) {
                    tahun_ajaran = `${year} / ${year + 1}`;
                    semester = "1";
                } else {
                    tahun_ajaran = `${year - 1} / ${year}`;
                    semester = "2";
                }
                // Generate tahun ajaran options
                const taOptions = [];
                for (let i = year + 1; i >= 2005; i--) {
                    taOptions.push(`${i - 1} / ${i}`);
                }

                // Resolve NPSN (access_to) to actual user_institusi.id and jenjang_id
                const npsn = res.data.access_to;
                axios.get(`${conf.uri_backend}api/v1/select-institusi/${npsn}`, { headers: { Authorization: tokenApi } })
                    .then((instRes) => {
                        this.setState(
                            {
                                institusi_id: instRes.data.id,
                                jenjang_id: instRes.data.m_struktur_jenjang_id,
                                tahun_ajaran: sessionStorage.getItem('pc_tahun_ajaran') || tahun_ajaran,
                                semester: sessionStorage.getItem('pc_semester') || semester,
                                tahun_ajaran_options: taOptions,
                            },
                            () => {
                                // Tunggu selected_kelas terisi dulu, baru restore step,
                                // supaya loadPengampuOptions tahu kelas apa yang dipilih (TK/SD/dll)
                                Promise.resolve(this.loadKelasOptions()).then(() => this.restoreStep());
                            }
                        );
                    })
                    .catch(() => {
                        this.setState(
                            {
                                institusi_id: npsn,
                                tahun_ajaran: sessionStorage.getItem('pc_tahun_ajaran') || tahun_ajaran,
                                semester: sessionStorage.getItem('pc_semester') || semester,
                                tahun_ajaran_options: taOptions,
                            },
                            () => this.loadKelasOptions()
                        );
                    });
            });
    }

    restoreStep() {
        const savedStep = sessionStorage.getItem('pc_wizard_step');
        if (savedStep && parseInt(savedStep) > 0) {
            this.lockSettings(parseInt(savedStep));
        }
    }

    loadKelasOptions() {
        const h = this.getApiHeaders();
        const base = this.getBaseParams();
        return axios.get(`${conf.uri_backend}api/v1/jadwal-v2/data/rombel?${base}`, h)
            .then((res) => {
                if (res.data.status && res.data.data) {
                    const rombels = res.data.data;
                    const uniqueKelas = [...new Set(rombels.map(r => r.kelas))].sort();
                    return new Promise((resolve) => this.setState({
                        rombel_options: rombels,
                        kelas_options: uniqueKelas,
                        selected_kelas: uniqueKelas, // auto-select all
                    }, resolve));
                }
            })
            .catch(() => { /* biarkan state apa adanya */ });
    }

    lockSettings(targetStep) {
        const step = targetStep || 1;
        sessionStorage.setItem('pc_wizard_step', step);
        // data_loading dipasang true kalau kita lompat langsung ke Step 4 (mis. reload halaman),
        // supaya renderStep4() tidak jalan sebelum penugasan_list/jadwal_preview/waktu_per_tingkat
        // selesai di-fetch — itu sumber "a.forEach is not a function" saat reload.
        const needsDataGuard = step === 4;
        this.setState({ settings_locked: true, currentStep: step, data_loading: needsDataGuard }, () => {
            // Restore persisted generate_result
            try {
                const saved = sessionStorage.getItem('pc_generate_result');
                if (saved) this.setState({ generate_result: JSON.parse(saved) });
            } catch (e) { /* ignore parse errors */ }

            const loaders = [
                this.loadWaktu(),
                this.loadPenugasan(),
                this.loadConstraint(),
                this.loadHelpers(),
                this.loadPengampuOptions(),
                this.loadVersiList(),
            ];

            if (needsDataGuard) {
                Promise.allSettled(loaders).then(() => {
                    this.setState({ data_loading: false });
                });
            }
        });
    }

    unlockSettings() {
        sessionStorage.setItem('pc_wizard_step', '0');
        sessionStorage.removeItem('pc_generate_result');
        this.setState({
            settings_locked: false,
            currentStep: 0,
            // Reset downstream data
            waktu_list: [],
            penugasan_list: [],
            constraint_list: [],
            generate_result: null,
            jadwal_preview: [],
        });
    }

    showMessage(text, type = "success") {
        this.setState({ message: text, messageType: type });
        setTimeout(() => this.setState({ message: null }), 4000);
    }

    getApiHeaders() {
        return { headers: { Authorization: "Bearer " + localStorage.getItem("token") } };
    }

    getBaseParams() {
        return `institusi=${this.state.institusi_id}&tahun_ajaran=${this.state.tahun_ajaran}&semester=${this.state.semester}&kurikulum=${this.state.kurikulum}`;
    }

    // ---- Daftar penugasan yang SENGAJA dihapus user ----
    // Auto-import/auto-sync dari Penugasan Guru tidak boleh memunculkan lagi item yang sudah dihapus
    // (mis. guru+mapel yang sama masih dipakai rombel lain, sehingga import tetap mengembalikannya).
    penugasanKey(p) {
        return `${p.rombel_id}_${(p.mapel_nama || '').toLowerCase().trim()}_${p.guru_id}`;
    }

    excludedStorageKey() {
        const s = this.state;
        return `wizard_penugasan_excluded_${s.institusi_id}_${s.tahun_ajaran}_${s.semester}_${s.kurikulum}`;
    }

    getExcludedSet() {
        try {
            const raw = localStorage.getItem(this.excludedStorageKey());
            const arr = raw ? JSON.parse(raw) : [];
            return new Set(Array.isArray(arr) ? arr : []);
        } catch (e) {
            return new Set();
        }
    }

    saveExcludedSet(set) {
        try {
            localStorage.setItem(this.excludedStorageKey(), JSON.stringify([...set]));
        } catch (e) { /* abaikan jika storage tidak tersedia */ }
    }

    markExcluded(items) {
        const set = this.getExcludedSet();
        items.forEach((it) => set.add(this.penugasanKey(it)));
        this.saveExcludedSet(set);
    }

    unmarkExcluded(items) {
        const set = this.getExcludedSet();
        items.forEach((it) => set.delete(this.penugasanKey(it)));
        this.saveExcludedSet(set);
    }

    clearExcluded() {
        try { localStorage.removeItem(this.excludedStorageKey()); } catch (e) { /* abaikan */ }
    }

    // ========== STEP 1: WAKTU ==========
    loadWaktu() {
        return axios.get(`${conf.uri_backend}api/v1/jadwal-v2/waktu?${this.getBaseParams()}`, this.getApiHeaders())
            .then((res) => {
                if (res.data.status && res.data.data.length > 0) {
                    const allData = res.data.data;

                    // Check if data has different tingkat values
                    const tingkatSet = new Set();
                    allData.forEach(w => { if (w.tingkat) tingkatSet.add(w.tingkat); });
                    const hasMixedTingkat = tingkatSet.size > 1;

                    if (this.state.waktu_mode === 'per_tingkat' || hasMixedTingkat) {
                        // Per-tingkat mode: group by tingkat, each tingkat has its own waktu_list
                        const perTingkat = {};
                        const firstTingkat = this.state.selected_kelas[0] || null;

                        // Group entries by tingkat
                        allData.forEach(w => {
                            const t = w.tingkat || 'shared';
                            if (!perTingkat[t]) perTingkat[t] = [];
                            perTingkat[t].push(w);
                        });

                        // Determine active tingkat
                        const activeTingkat = this.state.waktu_active_tingkat ||
                            (this.state.selected_kelas.length > 0 ? this.state.selected_kelas[0] : Object.keys(perTingkat)[0]);

                        // Set waktu_list to the active tingkat's entries
                        const activeEntries = perTingkat[activeTingkat] || perTingkat['shared'] || Object.values(perTingkat)[0] || [];

                        // Derive hari_aktif
                        const hariSet = new Set();
                        activeEntries.forEach(w => hariSet.add(Number(w.hari)));
                        const derivedHari = [...hariSet].sort((a, b) => a - b);

                        this.setState({
                            waktu_list: activeEntries,
                            waktu_per_tingkat: perTingkat,
                            waktu_active_tingkat: activeTingkat,
                            waktu_mode: hasMixedTingkat ? 'per_tingkat' : this.state.waktu_mode,
                            hari_aktif: derivedHari.length > 0 ? derivedHari : this.state.hari_aktif,
                            waktu_saved: true,
                            waktu_saved_per_tingkat: Object.keys(perTingkat).reduce((acc, k) => { acc[k] = true; return acc; }, {}),
                        });
                    } else {
                        // Sama mode: deduplicate entries (all tingkat have same data)
                        const seen = {};
                        const data = allData.filter(w => {
                            const key = `${w.hari}_${w.jam_ke || ''}_${w.waktu_mulai || ''}_${w.tipe || ''}_${w.urutan || ''}`;
                            if (seen[key]) return false;
                            seen[key] = true;
                            return true;
                        });
                        // Derive hari_aktif
                        const hariSet = new Set();
                        data.forEach(w => hariSet.add(Number(w.hari)));
                        const derivedHari = [...hariSet].sort((a, b) => a - b);

                        this.setState({
                            waktu_list: data,
                            hari_aktif: derivedHari.length > 0 ? derivedHari : this.state.hari_aktif,
                            waktu_saved: true,
                        });
                    }
                } else {
                    this.setState({ waktu_list: [], waktu_saved: true });
                }
            });
    }

    formatMinutes(totalMin) {
        return `${Math.floor(totalMin / 60).toString().padStart(2, "0")}:${(totalMin % 60).toString().padStart(2, "0")}`;
    }

    generateDefaultWaktu() {
        const { hari_aktif, default_jam_mulai, default_durasi, default_jumlah_jam, default_istirahat_list } = this.state;

        const waktu = [];
        const [startH, startM] = default_jam_mulai.split(":").map(Number);

        // Build a lookup of break positions: { jam_number: durasi }
        const breakMap = {};
        (default_istirahat_list || []).forEach(b => {
            if (b.setelah_jam > 0) breakMap[b.setelah_jam] = b.durasi || 15;
        });

        for (const hari of hari_aktif) {
            let currentMinutes = startH * 60 + startM;
            let jamKe = 1;
            let istirahatCounter = 0;
            for (let slot = 1; slot <= default_jumlah_jam; slot++) {
                if (jamKe > 1 && breakMap[jamKe - 1]) {
                    const bDurasi = breakMap[jamKe - 1];
                    const iMulai = this.formatMinutes(currentMinutes);
                    currentMinutes += bDurasi;
                    const iSelesai = this.formatMinutes(currentMinutes);
                    istirahatCounter++;
                    waktu.push({
                        hari, jam_ke: 0,
                        waktu_mulai: iMulai, waktu_selesai: iSelesai,
                        is_istirahat: true, tipe: "istirahat", keterangan: `Istirahat ${istirahatCounter}`,
                    });
                }
                const wMulai = this.formatMinutes(currentMinutes);
                currentMinutes += default_durasi;
                const wSelesai = this.formatMinutes(currentMinutes);
                waktu.push({
                    hari, jam_ke: jamKe,
                    waktu_mulai: wMulai, waktu_selesai: wSelesai,
                    is_istirahat: false, tipe: "pelajaran", keterangan: "",
                });
                jamKe++;
            }
        }

        this.setState({ waktu_list: waktu, waktu_tab_hari: hari_aktif[0] || 1, waktu_saved: false });
    }

    addEntryToDay(hari) {
        const { waktu_list, new_entry, default_durasi } = this.state;
        const dayEntries = waktu_list.filter(w => w.hari === hari).sort((a, b) => {
            const aIdx = waktu_list.indexOf(a);
            const bIdx = waktu_list.indexOf(b);
            return aIdx - bIdx;
        });
        // Calculate start time from last entry
        let startMin = 7 * 60;
        if (dayEntries.length > 0) {
            const last = dayEntries[dayEntries.length - 1];
            const [h, m] = last.waktu_selesai.split(":").map(Number);
            startMin = h * 60 + m;
        }
        const dur = new_entry.tipe === "pelajaran" ? default_durasi : (new_entry.durasi || 15);
        const endMin = startMin + dur;

        const entry = {
            hari,
            jam_ke: new_entry.tipe === "pelajaran" ? (dayEntries.filter(w => !w.is_istirahat && w.tipe !== "upacara" && w.tipe !== "kegiatan").length + 1) : 0,
            waktu_mulai: this.formatMinutes(startMin),
            waktu_selesai: this.formatMinutes(endMin),
            is_istirahat: new_entry.tipe === "istirahat",
            tipe: new_entry.tipe,
            keterangan: new_entry.tipe === "istirahat" ? `Istirahat ${dayEntries.filter(w => w.tipe === 'istirahat').length + 1}` : (new_entry.keterangan || ""),

        };

        this.setState({
            waktu_list: [...waktu_list, entry],
            adding_entry: false,
            new_entry: { tipe: "istirahat", durasi: 15, keterangan: "Istirahat" },
        }, () => this.recalculateJamKe(hari));
    }

    removeEntryFromDay(hari, entryIndex) {
        const { waktu_list } = this.state;
        // find the Nth entry for this day
        let count = -1;
        const newList = waktu_list.filter((w) => {
            if (w.hari === hari) {
                count++;
                return count !== entryIndex;
            }
            return true;
        });
        this.setState({ waktu_list: newList }, () => this.recalculateJamKe(hari));
    }

    moveEntry(hari, entryIndex, direction) {
        const { waktu_list } = this.state;
        const dayEntries = [];
        const otherEntries = [];
        waktu_list.forEach((w) => {
            if (w.hari === hari) dayEntries.push({ ...w });
            else otherEntries.push(w);
        });

        const swapIdx = entryIndex + direction;
        if (swapIdx < 0 || swapIdx >= dayEntries.length) return;

        [dayEntries[entryIndex], dayEntries[swapIdx]] = [dayEntries[swapIdx], dayEntries[entryIndex]];

        // Rebuild list keeping other days in place, and recalculate times
        const rebuilt = [...otherEntries, ...dayEntries];
        this.setState({ waktu_list: rebuilt }, () => this.recalculateTimesAndJam(hari));
    }

    recalculateJamKe(hari) {
        const { waktu_list } = this.state;
        let jamKe = 1;
        let istirahatNum = 1;
        const updated = waktu_list.map((w) => {
            if (w.hari === hari) {
                if (w.tipe === "pelajaran" || (!w.is_istirahat && !w.tipe)) {
                    return { ...w, jam_ke: jamKe++, is_istirahat: false };
                } else if (w.tipe === "istirahat" || w.is_istirahat) {
                    return { ...w, jam_ke: 0, is_istirahat: true, keterangan: `Istirahat ${istirahatNum++}` };
                } else {
                    return { ...w, jam_ke: 0, is_istirahat: false };
                }
            }
            return w;
        });
        this.setState({ waktu_list: updated, waktu_saved: false });
    }

    recalculateTimesAndJam(hari) {
        const { waktu_list, default_jam_mulai } = this.state;
        const [startH, startM] = default_jam_mulai.split(":").map(Number);
        let currentMin = startH * 60 + startM;
        let jamKe = 1;

        const updated = waktu_list.map((w) => {
            if (w.hari === hari) {
                const [mH, mM] = w.waktu_mulai.split(":").map(Number);
                const [eH, eM] = w.waktu_selesai.split(":").map(Number);
                const dur = (eH * 60 + eM) - (mH * 60 + mM);
                const newStart = this.formatMinutes(currentMin);
                currentMin += dur;
                const newEnd = this.formatMinutes(currentMin);
                const isPelajaran = w.tipe === "pelajaran" || (!w.is_istirahat && !w.tipe);
                return {
                    ...w,
                    waktu_mulai: newStart,
                    waktu_selesai: newEnd,
                    jam_ke: isPelajaran ? jamKe++ : 0,
                    is_istirahat: w.tipe === "istirahat" || w.is_istirahat,
                };
            }
            return w;
        });
        this.setState({ waktu_list: updated, waktu_saved: false });
    }

    updateWaktuField(hari, entryIndex, field, value) {
        const { waktu_list } = this.state;
        // Get day entries indices in order
        const dayIndices = [];
        waktu_list.forEach((w, i) => { if (w.hari === hari) dayIndices.push(i); });

        const globalIdx = dayIndices[entryIndex];
        if (globalIdx === undefined) return;

        const updated = [...waktu_list];
        updated[globalIdx] = { ...updated[globalIdx], [field]: value };

        // Auto-cascade: when waktu_selesai changes, shift all subsequent entries
        if (field === "waktu_selesai") {
            const [eH, eM] = value.split(":").map(Number);
            let nextStart = eH * 60 + eM;
            for (let i = entryIndex + 1; i < dayIndices.length; i++) {
                const gi = dayIndices[i];
                const entry = updated[gi];
                const [mH, mM] = entry.waktu_mulai.split(":").map(Number);
                const [sH, sM] = entry.waktu_selesai.split(":").map(Number);
                const dur = (sH * 60 + sM) - (mH * 60 + mM);
                updated[gi] = { ...entry, waktu_mulai: this.formatMinutes(nextStart), waktu_selesai: this.formatMinutes(nextStart + dur) };
                nextStart += dur;
            }
        }
        // Auto-cascade: when waktu_mulai changes, adjust this entry's end (keep duration), then cascade rest
        if (field === "waktu_mulai") {
            const entry = updated[globalIdx];
            const origEntry = waktu_list[globalIdx];
            const [omH, omM] = origEntry.waktu_mulai.split(":").map(Number);
            const [osH, osM] = origEntry.waktu_selesai.split(":").map(Number);
            const dur = (osH * 60 + osM) - (omH * 60 + omM);
            const [nH, nM] = value.split(":").map(Number);
            const newEnd = nH * 60 + nM + dur;
            updated[globalIdx] = { ...updated[globalIdx], waktu_selesai: this.formatMinutes(newEnd) };
            // cascade rest
            let nextStart = newEnd;
            for (let i = entryIndex + 1; i < dayIndices.length; i++) {
                const gi = dayIndices[i];
                const e = updated[gi];
                const [mH2, mM2] = e.waktu_mulai.split(":").map(Number);
                const [sH2, sM2] = e.waktu_selesai.split(":").map(Number);
                const d = (sH2 * 60 + sM2) - (mH2 * 60 + mM2);
                updated[gi] = { ...e, waktu_mulai: this.formatMinutes(nextStart), waktu_selesai: this.formatMinutes(nextStart + d) };
                nextStart += d;
            }
        }

        this.setState({ waktu_list: updated, waktu_saved: false });
    }

    changeEntryTipe(hari, entryIndex, newTipe) {
        const { waktu_list } = this.state;
        let count = -1;
        const updated = waktu_list.map((w) => {
            if (w.hari === hari) {
                count++;
                if (count === entryIndex) {
                    const keteranganMap = { istirahat: "Istirahat", upacara: "Upacara", kegiatan: w.keterangan || "", pelajaran: "" };
                    // Note: istirahat numbering will be corrected by recalculateJamKe
                    return {
                        ...w,
                        tipe: newTipe,
                        is_istirahat: newTipe === "istirahat",
                        keterangan: keteranganMap[newTipe] ?? "",
                    };
                }
            }
            return w;
        });
        this.setState({ waktu_list: updated, waktu_saved: false }, () => this.recalculateJamKe(hari));
    }

    applyDayToOtherDays() {
        const { waktu_list, waktu_tab_hari, apply_target_days } = this.state;
        if (apply_target_days.length === 0) return;
        // Get source day entries
        const sourceEntries = waktu_list.filter(w => w.hari === waktu_tab_hari);
        // Remove target days entries, keep everything else
        let newList = waktu_list.filter(w => !apply_target_days.includes(w.hari));
        // Clone source entries to target days
        for (const targetDay of apply_target_days) {
            sourceEntries.forEach(e => {
                newList.push({ ...e, hari: targetDay });
            });
        }
        this.setState({ waktu_list: newList, show_apply_modal: false, apply_target_days: [], waktu_saved: false });
    }

    saveWaktu() {
        this.setState({ saving: true });
        const { waktu_list, selected_kelas, waktu_mode, waktu_active_tingkat } = this.state;

        // Get distinct tingkat values from selected_kelas
        const tingkatValues = [...new Set(selected_kelas)].sort(sortKelas);

        if (waktu_mode === 'per_tingkat' && waktu_active_tingkat) {
            // Per-tingkat mode: save only the active tingkat
            const hariCounters = {};
            const waktuFinal = waktu_list.map((w) => {
                if (!hariCounters[w.hari]) hariCounters[w.hari] = 0;
                return { ...w, tingkat: String(waktu_active_tingkat), urutan: hariCounters[w.hari]++ };
            });
            axios.post(`${conf.uri_backend}api/v1/jadwal-v2/waktu/save`, {
                institusi: this.state.institusi_id,
                tahun_ajaran: this.state.tahun_ajaran,
                semester: this.state.semester,
                kurikulum: this.state.kurikulum,
                tingkat: String(waktu_active_tingkat),
                waktu: waktuFinal,
            }, this.getApiHeaders())
                .then((res) => {
                    // Update waktu_per_tingkat with current data
                    const perTingkat = { ...(this.state.waktu_per_tingkat || {}) };
                    perTingkat[waktu_active_tingkat] = waktu_list;
                    const savedMap = { ...(this.state.waktu_saved_per_tingkat || {}) };
                    savedMap[waktu_active_tingkat] = true;
                    this.setState({ saving: false, waktu_saved: true, waktu_per_tingkat: perTingkat, waktu_saved_per_tingkat: savedMap });
                    this.loadWaktu();
                    if (res.data.status) this.showMessage(`Waktu untuk kelas ${waktu_active_tingkat} berhasil disimpan!`);
                })
                .catch(() => {
                    this.setState({ saving: false });
                    this.showMessage("Gagal menyimpan waktu", "error");
                });
        } else if (tingkatValues.length > 0) {
            // Sama mode: save the same waktu entries for each selected tingkat
            const savePromises = tingkatValues.map(tingkat => {
                const hariCounters = {};
                const waktuFinal = waktu_list.map((w) => {
                    if (!hariCounters[w.hari]) hariCounters[w.hari] = 0;
                    return { ...w, tingkat: String(tingkat), urutan: hariCounters[w.hari]++ };
                });
                return axios.post(`${conf.uri_backend}api/v1/jadwal-v2/waktu/save`, {
                    institusi: this.state.institusi_id,
                    tahun_ajaran: this.state.tahun_ajaran,
                    semester: this.state.semester,
                    kurikulum: this.state.kurikulum,
                    tingkat: String(tingkat),
                    waktu: waktuFinal,
                }, this.getApiHeaders());
            });

            Promise.all(savePromises)
                .then(() => {
                    this.setState({ saving: false, waktu_saved: true });
                    this.loadWaktu();
                    this.showMessage("Waktu berhasil disimpan untuk semua tingkat!");
                })
                .catch(() => {
                    this.setState({ saving: false });
                    this.showMessage("Gagal menyimpan waktu", "error");
                });
        } else {
            // Fallback: save as shared (no tingkat)
            const hariCounters = {};
            const waktuFinal = waktu_list.map((w) => {
                if (!hariCounters[w.hari]) hariCounters[w.hari] = 0;
                return { ...w, tingkat: null, urutan: hariCounters[w.hari]++ };
            });
            axios.post(`${conf.uri_backend}api/v1/jadwal-v2/waktu/save`, {
                institusi: this.state.institusi_id,
                tahun_ajaran: this.state.tahun_ajaran,
                semester: this.state.semester,
                kurikulum: this.state.kurikulum,
                tingkat: null,
                waktu: waktuFinal,
            }, this.getApiHeaders())
                .then((res) => {
                    this.setState({ saving: false, waktu_saved: true });
                    this.loadWaktu();
                    if (res.data.status) this.showMessage("Waktu berhasil disimpan!");
                })
                .catch(() => {
                    this.setState({ saving: false });
                    this.showMessage("Gagal menyimpan waktu", "error");
                });
        }
    }

    // ========== STEP 2: PENUGASAN ==========
    loadPenugasan() {
        return axios.get(`${conf.uri_backend}api/v1/jadwal-v2/penugasan?${this.getBaseParams()}`, this.getApiHeaders())
            .then((res) => {

                if (res.data.status && res.data.data && res.data.data.length > 0) {
                    // Deduplicate saved data (may have pre-existing dupes)
                    const deduped = this.deduplicateImport(res.data.data);
                    this.setState({ penugasan_list: deduped, penugasan_saved: true }, () => {
                        // Auto-sync: import penugasan for any missing rombels silently
                        this.autoSyncMissingRombels();
                    });
                } else {
                    // No saved data - auto-import from PenugasanGuru
                    this.autoImportFromPenugasanGuru();
                }
            });
    }

    autoImportFromPenugasanGuru() {
        this.setState({ importing_penugasan: true });
        axios.get(`${conf.uri_backend}api/v1/jadwal-v2/data/import-penugasan?${this.getBaseParams()}`, this.getApiHeaders())
            .then((res) => {
                this.setState({ importing_penugasan: false });

                if (res.data.status && res.data.data) {
                    const selectedKelas = this.state.selected_kelas;
                    const excluded = this.getExcludedSet();
                    const imported = this.deduplicateImport(res.data.data
                        .filter((item) => selectedKelas.length === 0 || selectedKelas.includes(item.kelas))
                        .filter((item) => !excluded.has(this.penugasanKey(item)))
                        .map((item) => ({
                            rombel_id: item.rombel_id,
                            rombel_nama: item.rombel_nama,
                            kelas: item.kelas,
                            mapel_id: item.mapel_id,
                            mapel_nama: item.mapel_nama,
                            mapel_kode: item.mapel_kode,
                            guru_id: item.guru_id,
                            guru_nama: item.guru_nama,
                            guru_nip: item.guru_nip,
                            jumlah_jam_per_minggu: item.jumlah_jam_per_minggu || 1,
                            kategori_mapel: item.kategori_mapel || 'umum',
                            _temp_id: `import_${Date.now()}_${Math.random()}`
                        })));
                    this.setState({ penugasan_list: imported });
                    if (imported.length > 0) {
                        this.showMessage(`${imported.length} penugasan dimuat otomatis dari Penugasan Guru`);
                    }
                }
            })
            .catch(() => {
                this.setState({ importing_penugasan: false });
            });
    }

    resetAndReimport() {
        if (!window.confirm('Reset penugasan dan import ulang dari data Penugasan Guru?')) return;
        this.setState({ importing_penugasan: true });
        axios.post(`${conf.uri_backend}api/v1/jadwal-v2/penugasan/reset`, {
            institusi: this.state.institusi_id,
            tahun_ajaran: this.state.tahun_ajaran,
            semester: this.state.semester,
            kurikulum: this.state.kurikulum,
        }, this.getApiHeaders())
            .then(() => {
                this.clearExcluded();
                this.setState({ penugasan_list: [], penugasan_saved: false });
                this.autoImportFromPenugasanGuru();
            })
            .catch(() => {
                this.setState({ importing_penugasan: false });
                this.showMessage('Gagal mereset penugasan', 'error');
            });
    }

    /**
     * Deduplicate imported items by (rombel_id, mapel_nama, guru_id)
     * Uses mapel_nama (not mapel_id) because SMK may have multiple
     * m_struktur_mpk entries for the same subject across different jurusan
     */
    deduplicateImport(items) {
        const seen = new Set();
        return items.filter(item => {
            const key = `${item.rombel_id}_${(item.mapel_nama || '').toLowerCase().trim()}_${item.guru_id}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }

    /**
     * Auto-sync: import penugasan for rombels that exist in
     * rombel_options but don't have any penugasan_list entries yet,
     * AND also detect new penugasan in existing rombels
     */
    autoSyncMissingRombels() {
        const { rombel_options, penugasan_list, selected_kelas } = this.state;
        if (!rombel_options || rombel_options.length === 0) return;

        axios.get(`${conf.uri_backend}api/v1/jadwal-v2/data/import-penugasan?${this.getBaseParams()}`, this.getApiHeaders())
            .then((res) => {

                if (res.data.status && res.data.data) {
                    const excluded = this.getExcludedSet();
                    const importedAll = res.data.data
                        .filter((item) => selected_kelas.length === 0 || selected_kelas.includes(item.kelas))
                        .filter((item) => !excluded.has(this.penugasanKey(item)))
                        .map((item) => ({
                            rombel_id: item.rombel_id,
                            rombel_nama: item.rombel_nama,
                            kelas: item.kelas,
                            mapel_id: item.mapel_id,
                            mapel_nama: item.mapel_nama,
                            mapel_kode: item.mapel_kode,
                            guru_id: item.guru_id,
                            guru_nama: item.guru_nama,
                            guru_nip: item.guru_nip,
                            jumlah_jam_per_minggu: item.jumlah_jam_per_minggu || 1,
                            kategori_mapel: item.kategori_mapel || 'umum',
                            _temp_id: `sync_${Date.now()}_${Math.random()}`
                        }));

                    // Check for truly new items not yet in penugasan_list
                    const existingKeys = new Set(penugasan_list.map(p =>
                        `${p.rombel_id}_${(p.mapel_nama || '').toLowerCase().trim()}_${p.guru_id}`
                    ));

                    // Build a lookup for existing jam per (rombel_id, mapel_nama) to inherit
                    const existingJamLookup = {};
                    penugasan_list.forEach(p => {
                        const jKey = `${p.rombel_id}_${(p.mapel_nama || '').toLowerCase().trim()}`;
                        if (!existingJamLookup[jKey]) existingJamLookup[jKey] = p.jumlah_jam_per_minggu;
                    });

                    const trulyNew = this.deduplicateImport(
                        importedAll.filter(item => {
                            const key = `${item.rombel_id}_${(item.mapel_nama || '').toLowerCase().trim()}_${item.guru_id}`;
                            return !existingKeys.has(key);
                        }).map(item => {
                            // Inherit jam from existing entry for same mapel+rombel if available
                            const jKey = `${item.rombel_id}_${(item.mapel_nama || '').toLowerCase().trim()}`;
                            if (existingJamLookup[jKey] !== undefined) {
                                item.jumlah_jam_per_minggu = existingJamLookup[jKey];
                            }
                            return item;
                        })
                    );
                    if (trulyNew.length > 0) {
                        this.setState(prev => ({
                            penugasan_list: [...prev.penugasan_list, ...trulyNew],
                            penugasan_saved: false,
                        }));
                        this.showMessage(`${trulyNew.length} penugasan baru terdeteksi dari Penugasan Guru dan ditambahkan otomatis. Klik "Simpan Penugasan" untuk menyimpan.`, 'info');
                    }
                }
            })
            .catch(() => { /* silent fail */ });
    }

    loadHelpers() {
        const h = this.getApiHeaders();
        const base = this.getBaseParams();
        // rombel_options already loaded by loadKelasOptions() in Step 0
        return axios.get(`${conf.uri_backend}api/v1/jadwal-v2/data/guru?${base}`, h)
            .then((res) => { if (res.data.status) this.setState({ guru_options: res.data.data || [] }); })
            .catch(() => this.setState({ guru_options: [] }));
    }

    // Normalisasi nama kelas agar "TK B" == "TKB" == "tk b" (backend mengirim TKA/TKB)
    normKelas(k) {
        return String(k == null ? '' : k).replace(/\s+/g, '').toUpperCase();
    }

    loadPengampuOptions() {
        if (!this.state.jenjang_id) return Promise.resolve();
        // Map jenjang_id (m_struktur_jenjang.id) -> kode_jenjang (sama seperti PenugasanGuru)
        const jMap = { '1': '0', '2': '1', '3': '2', '4': '3', '5': '4' }; // 1 = PAUD/TK
        const kodeInstitusi = jMap[String(this.state.jenjang_id)];

        // Tentukan jenjang dari KELAS yang dipilih, bukan hanya dari jenjang institusi.
        // Kelas "TK A"/"TK B" (TKA/TKB) selalu memakai kode_jenjang '0' (PAUD).
        let selected = this.state.selected_kelas || [];
        if (selected.length === 0) selected = [...new Set((this.state.rombel_options || []).map((r) => r.kelas))];
        const isTkKelas = (k) => /^TK/i.test(this.normKelas(k));
        const hasTk = selected.some(isTkKelas);
        const hasNonTk = selected.length === 0 || selected.some((k) => !isTkKelas(k));

        const kodeSet = new Set();
        if (hasTk) kodeSet.add('0');
        if (hasNonTk && kodeInstitusi !== undefined) kodeSet.add(kodeInstitusi);

        if (kodeSet.size === 0) {
            this.setState({ pengampu_options: [] });
            return Promise.resolve();
        }

        const kurVal = this.state.kurikulum === 'k13' ? 'K13' : 'Merdeka';
        const tokenApi = "Bearer " + localStorage.getItem("token");
        const requests = [...kodeSet].map((kode) =>
            axios
                .get(`${conf.uri_backend}api/v1/penugasan/select_pengampu/GU/${kode}/${kurVal}`, { headers: { Authorization: tokenApi } })
                .then((res) => (Array.isArray(res.data) ? res.data : []))
                .catch(() => [])
        );
        return Promise.all(requests).then((results) => {
            this.setState({ pengampu_options: results.flat() });
        });
    }

    groupMapelOptions(options) {
        if (!options || !options.length) return [];
        if (!options[0]?.kmp) return options;
        const groups = {};
        options.forEach((opt) => {
            const key = `${opt.jenjang || '?'} - ${opt.kmp || '?'} - ${opt.jkmp || '?'} - Kelas ${opt.kelas || '?'}`;
            if (!groups[key]) groups[key] = { label: key, options: [] };
            groups[key].options.push(opt);
        });
        return Object.values(groups);
    }

    updatePenugasanJam(idxOrArray, newJam) {
        const list = [...this.state.penugasan_list];
        const parsedJam = parseInt(newJam) || 1;
        const indices = Array.isArray(idxOrArray) ? idxOrArray : [idxOrArray];

        // First, update the specified indices
        const affectedKeys = new Set();
        indices.forEach(idx => {
            if (idx >= 0 && idx < list.length) {
                list[idx] = { ...list[idx], jumlah_jam_per_minggu: parsedJam };
                // Track which (rombel+mapel) combos are affected
                const key = `${list[idx].rombel_id}_${(list[idx].mapel_nama || '').toLowerCase().trim()}`;
                affectedKeys.add(key);
            }
        });

        // Then, normalize: update ALL entries with the same (rombel_id, mapel_nama)
        // so they all have the same jam (prevents drift after save+reload)
        list.forEach((p, i) => {
            const key = `${p.rombel_id}_${(p.mapel_nama || '').toLowerCase().trim()}`;
            if (affectedKeys.has(key) && p.jumlah_jam_per_minggu !== parsedJam) {
                list[i] = { ...p, jumlah_jam_per_minggu: parsedJam };
            }
        });

        this.setState({ penugasan_list: list, penugasan_saved: false });
    }

    loadMapelForKelas(kelas, kurikulum) {
        if (!kelas || !this.state.jenjang_id) return;
        const h = this.getApiHeaders();
        const kurVal = kurikulum || this.state.kurikulum || 'merdeka';
        const params = `institusi=${this.state.institusi_id}&jenjang_id=${this.state.jenjang_id}&kelas=${kelas}&kurikulum=${kurVal}&semester=${this.state.semester}`;
        axios.get(`${conf.uri_backend}api/v1/jadwal-v2/data/mapel?${params}`, h)
            .then((res) => { if (res.data.status) this.setState({ mapel_options: res.data.data || [] }); })
            .catch(() => this.setState({ mapel_options: [] }));
    }

    addPenugasan() {
        const { new_penugasan, penugasan_list, rombel_options, selected_kelas } = this.state;
        if (!new_penugasan.mapel_nama || !new_penugasan.guru_nama) {
            this.showMessage("Mapel dan Guru harus diisi!", "error");
            return;
        }
        // Auto-assign to all rombels matching the mapel's kelas AND jurusan (jkmp)
        const mapelKelas = new_penugasan.kelas || "";
        const mapelJkmp = new_penugasan.jkmp || "";
        const mapelKmp = (new_penugasan.kmp || "").toLowerCase();
        const isSmkSpecific = mapelKmp.includes('bidang') || mapelKmp.includes('program') || mapelKmp.includes('kompetensi');
        const matchingRombels = rombel_options.filter(r => {
            if (selected_kelas.length > 0 && !selected_kelas.includes(r.kelas)) return false;
            if (mapelKelas && this.normKelas(r.kelas) !== this.normKelas(mapelKelas)) return false;
            // For SMK-specific mapel, only assign to rombels with matching jkmp
            if (isSmkSpecific && mapelJkmp && r.jkmp_list && Array.isArray(r.jkmp_list)) {
                if (!r.jkmp_list.includes(mapelJkmp)) return false;
            }
            return true;
        });

        if (matchingRombels.length === 0) {
            this.unmarkExcluded([new_penugasan]);
            this.setState({
                penugasan_list: [...penugasan_list, { ...new_penugasan, _temp_id: Date.now() }],
                penugasan_saved: false,
            });
        } else {
            const newEntries = matchingRombels.map((r, idx) => ({
                ...new_penugasan,
                rombel_id: r.id,
                rombel_nama: r.nama_rombel,
                kelas: r.kelas,
                _temp_id: Date.now() + idx,
            }));
            this.unmarkExcluded(newEntries);
            this.setState({
                penugasan_list: [...penugasan_list, ...newEntries],
                penugasan_saved: false,
            });
            this.showMessage(`Ditambahkan ke ${newEntries.length} rombel (Kelas ${mapelKelas})`);
        }

        // R5: Also sync to PenugasanGuru (fire-and-forget)
        this.syncToPenugasanGuru(new_penugasan);

        this.setState({
            new_penugasan: {
                rombel_id: "", rombel_nama: "", kelas: "",
                mapel_id: "", mapel_nama: "", mapel_kode: "",
                guru_id: "", guru_nama: "", guru_nip: "",
                jumlah_jam_per_minggu: 2, kategori_mapel: "umum"
            }
        });
    }

    removePenugasan(indexOrArray) {
        const indices = Array.isArray(indexOrArray) ? indexOrArray : [indexOrArray];
        const removeSet = new Set(indices);
        // Collect items being removed for sync-delete
        const removedItems = this.state.penugasan_list.filter((_, i) => removeSet.has(i));
        const list = this.state.penugasan_list.filter((_, i) => !removeSet.has(i));
        // Ingat item yang dihapus agar tidak diimpor ulang otomatis setelah Simpan / reload
        this.markExcluded(removedItems);
        this.setState({ penugasan_list: list, delete_modal: null, penugasan_saved: false }, () => {
            // Sync deletions to PenugasanGuru backend
            removedItems.forEach(item => this.unsyncFromPenugasanGuru(item, list));
        });
    }

    /**
     * Unsync a deleted penugasan from PenugasanGuru table.
     * Only removes the specific mapel. If guru has no more mapels in remaining list, removes guru entirely.
     */
    unsyncFromPenugasanGuru(removedItem, remainingList) {
        if (!removedItem.guru_id || !removedItem.mapel_id) return;
        // Check if this guru still teaches other mapels in the remaining list
        const guruStillHasMapels = remainingList.some(
            p => String(p.guru_id) === String(removedItem.guru_id) && String(p.mapel_id) === String(removedItem.mapel_id)
        );
        if (guruStillHasMapels) return; // Same guru+mapel still exists elsewhere, don't unsync
        // Fire-and-forget
        axios.post(`${conf.uri_backend}api/v1/jadwal-v2/data/unsync-penugasan-guru`, {
            guru_id: removedItem.guru_id,
            mapel_id: removedItem.mapel_id,
            tahun_ajaran: this.state.tahun_ajaran,
            kurikulum: this.state.kurikulum,
        }, this.getApiHeaders()).catch(() => {/* silent */ });
    }

    /**
     * Show delete modal for multi-guru mapels, or delete directly for single-guru
     */
    showDeleteModal(mapelRow) {
        const guruList = mapelRow.guru_list || [];
        const allIdxs = mapelRow.all_agama_idxs || guruList.map(g => g._idx);

        if (guruList.length <= 1) {
            // Single guru or agama with 1 guru - delete directly
            this.removePenugasan(allIdxs);
        } else {
            // Multi-guru - show selection modal
            this.setState({
                delete_modal: {
                    mapel_nama: mapelRow.mapel_nama,
                    kategori: mapelRow.kategori_mapel,
                    rombel_nama: mapelRow.rombel_nama || '',
                    guru_list: guruList.map(g => ({ ...g, selected: false })),
                    all_idxs: allIdxs,
                }
            });
        }
    }

    syncToPenugasanGuru(penugasan) {
        // Fire-and-forget: sync manual wizard entries to PenugasanGuru table
        if (!penugasan.guru_id || !penugasan.mapel_id) return;
        axios.post(`${conf.uri_backend}api/v1/jadwal-v2/data/sync-penugasan-guru`, {
            guru_id: penugasan.guru_id,
            mapel_id: penugasan.mapel_id,
            tahun_ajaran: this.state.tahun_ajaran,
            kurikulum: this.state.kurikulum,
        }, this.getApiHeaders()).catch(() => {/* silent */ });
    }

    savePenugasan() {
        this.setState({ saving: true });

        // Normalize: ensure all entries with same (rombel_id, mapel_nama) have identical jumlah_jam
        // This prevents jam drift after save+reload when DB returns entries in different order
        const list = [...this.state.penugasan_list];
        const canonicalJam = {}; // rombel_id + mapel_nama => first entry's jam
        list.forEach(p => {
            const key = `${p.rombel_id}_${(p.mapel_nama || '').toLowerCase().trim()}`;
            if (canonicalJam[key] === undefined) {
                canonicalJam[key] = p.jumlah_jam_per_minggu;
            }
        });
        const normalizedList = list.map(p => {
            const key = `${p.rombel_id}_${(p.mapel_nama || '').toLowerCase().trim()}`;
            if (canonicalJam[key] !== undefined && p.jumlah_jam_per_minggu !== canonicalJam[key]) {
                return { ...p, jumlah_jam_per_minggu: canonicalJam[key] };
            }
            return p;
        });

        axios.post(`${conf.uri_backend}api/v1/jadwal-v2/penugasan/save`, {
            institusi: this.state.institusi_id,
            tahun_ajaran: this.state.tahun_ajaran,
            semester: this.state.semester,
            kurikulum: this.state.kurikulum,
            penugasan: normalizedList,
        }, this.getApiHeaders())
            .then((res) => {
                this.setState({ saving: false });
                if (res.data.status) {
                    this.setState({ penugasan_saved: true, penugasan_list: normalizedList });
                    this.showMessage("Penugasan berhasil disimpan!");
                    this.loadPenugasan();
                }
            })
            .catch(() => {
                this.setState({ saving: false });
                this.showMessage("Gagal menyimpan penugasan", "error");
            });
    }

    // ========== STEP 3: CONSTRAINT (Guru Availability) ==========
    loadConstraint() {
        return axios.get(`${conf.uri_backend}api/v1/jadwal-v2/constraint?${this.getBaseParams()}`, this.getApiHeaders())
            .then((res) => {
                if (res.data.status) {
                    // Normalize guru_id from referensi_id
                    const data = (res.data.data || []).map(c => ({
                        ...c,
                        guru_id: c.guru_id || c.referensi_id || null,
                    }));
                    // Parse max_jam_per_minggu constraints
                    let maxJamEnabled = false;
                    let maxJamDefault = 24;
                    const maxJamCustom = {};
                    data.forEach(c => {
                        if (c.tipe === 'max_jam_per_minggu' && c.nilai_json) {
                            const val = typeof c.nilai_json === 'string' ? JSON.parse(c.nilai_json) : c.nilai_json;
                            maxJamEnabled = true;
                            if (c.referensi_nama) {
                                maxJamCustom[c.referensi_nama] = val.max_jam || 24;
                            } else {
                                maxJamDefault = val.max_jam || 24;
                            }
                        }
                    });
                    this.setState({
                        constraint_list: data,
                        constraint_saved: true,
                        max_jam_enabled: maxJamEnabled,
                        max_jam_default: maxJamDefault,
                        max_jam_custom: maxJamCustom,
                    });
                }
            });
    }

    toggleConstraint(guruNama, guruId, hari, jamKe) {
        const list = [...this.state.constraint_list];
        // Check if this guru+hari+jam already has a constraint
        const idx = list.findIndex(c =>
            c.tipe === 'guru_tidak_hadir' &&
            c.referensi_nama === guruNama &&
            c.hari === hari &&
            c.jam_ke_mulai === jamKe &&
            c.jam_ke_selesai === jamKe
        );
        if (idx >= 0) {
            list.splice(idx, 1); // Remove = mark as available
        } else {
            list.push({
                tipe: 'guru_tidak_hadir',
                referensi_nama: guruNama,
                referensi_id: guruId,
                guru_id: guruId,
                hari: hari,
                jam_ke_mulai: jamKe,
                jam_ke_selesai: jamKe,
                _temp_id: `c_${Date.now()}_${Math.random()}`
            });
        }
        this.setState({ constraint_list: list, constraint_saved: false });
    }

    isGuruUnavailable(guruNama, hari, jamKe) {
        return this.state.constraint_list.some(c =>
            c.tipe === 'guru_tidak_hadir' &&
            c.referensi_nama === guruNama &&
            c.hari === hari &&
            c.jam_ke_mulai <= jamKe &&
            c.jam_ke_selesai >= jamKe
        );
    }

    clearConstraintsForGuru(guruNama) {
        const list = this.state.constraint_list.filter(c =>
            !(c.tipe === 'guru_tidak_hadir' && c.referensi_nama === guruNama)
        );
        this.setState({ constraint_list: list, constraint_saved: false });
    }

    saveConstraint() {
        this.setState({ saving: true });
        // Build constraint list: guru_tidak_hadir + max_jam_per_minggu
        const { constraint_list, max_jam_enabled, max_jam_default, max_jam_custom } = this.state;
        // Filter out old max_jam constraints (will re-add from state)
        let constraints = constraint_list.filter(c => c.tipe !== 'max_jam_per_minggu');
        // Add max_jam constraints if enabled
        if (max_jam_enabled) {
            // Global default
            constraints.push({
                tipe: 'max_jam_per_minggu',
                referensi_id: null,
                referensi_nama: null,
                nilai_json: { max_jam: max_jam_default },
            });
            // Per-guru overrides
            Object.entries(max_jam_custom).forEach(([guruNama, maxJam]) => {
                if (maxJam !== null && maxJam !== undefined && maxJam !== max_jam_default) {
                    constraints.push({
                        tipe: 'max_jam_per_minggu',
                        referensi_nama: guruNama,
                        referensi_id: null,
                        nilai_json: { max_jam: maxJam },
                    });
                }
            });
        }
        axios.post(`${conf.uri_backend}api/v1/jadwal-v2/constraint/save`, {
            institusi: this.state.institusi_id,
            tahun_ajaran: this.state.tahun_ajaran,
            semester: this.state.semester,
            kurikulum: this.state.kurikulum,
            constraints: constraints,
        }, this.getApiHeaders())
            .then((res) => {
                this.setState({ saving: false });
                if (res.data.status) {
                    this.setState({ constraint_saved: true });
                    this.showMessage("Ketersediaan guru berhasil disimpan!");
                    this.loadConstraint();
                }
            })
            .catch(() => {
                this.setState({ saving: false });
                this.showMessage("Gagal menyimpan ketersediaan guru", "error");
            });
    }

    /**
     * Get jam validation results for all rombels
     */
    getJamValidation() {
        const { penugasan_list, waktu_list, hari_aktif, waktu_per_tingkat } = this.state;

        // Helper: count total jam pelajaran from a waktu list
        const countJamFromWaktu = (wList) => {
            const hariSet = [...new Set(wList.map(w => Number(w.hari)))];
            return hariSet.reduce((sum, h) => sum + wList.filter(w => Number(w.hari) === h && (w.tipe === 'pelajaran' || (!w.is_istirahat && !w.tipe))).length, 0);
        };

        // Default total (used as fallback and for display)
        const defaultTotal = hari_aktif.reduce((sum, h) => sum + waktu_list.filter(w => w.hari === h && (w.tipe === 'pelajaran' || (!w.is_istirahat && !w.tipe))).length, 0);

        const grouped = {};
        penugasan_list.forEach(p => {
            const key = p.rombel_id || p.rombel_nama;
            if (!grouped[key]) grouped[key] = { rombel_nama: p.rombel_nama, kelas: p.kelas, jam: 0, mapelFirstJam: {}, agamaFirstJam: {} };
            const mapelKey = (p.mapel_nama || '').toLowerCase().trim();
            if (p.kategori_mapel === 'agama') {
                // Use first entry's jam (= what UI spinner shows)
                if (grouped[key].agamaFirstJam[mapelKey] === undefined) {
                    grouped[key].agamaFirstJam[mapelKey] = p.jumlah_jam_per_minggu || 1;
                }
            } else {
                // Use first entry's jam (= what UI spinner shows)
                if (grouped[key].mapelFirstJam[mapelKey] === undefined) {
                    grouped[key].mapelFirstJam[mapelKey] = p.jumlah_jam_per_minggu || 0;
                }
            }
        });
        Object.values(grouped).forEach(g => {
            // Agama: MAX across variants (shared slot count)
            const agamaVals = Object.values(g.agamaFirstJam);
            g.jam = agamaVals.length > 0 ? Math.max(...agamaVals) : 0;
            // Regular: sum of first-entry jams
            Object.values(g.mapelFirstJam).forEach(j => { g.jam += j; });
        });

        const rombels = Object.values(grouped).map(r => {
            // Find per-tingkat waktu for this rombel's kelas
            let targetJam = defaultTotal;
            if (waktu_per_tingkat && r.kelas && waktu_per_tingkat[r.kelas]) {
                targetJam = countJamFromWaktu(waktu_per_tingkat[r.kelas]);
            }
            return {
                rombel_nama: r.rombel_nama,
                jam: r.jam,
                target: targetJam,
                exceeded: targetJam > 0 && r.jam > targetJam,
                diff: r.jam - targetJam,
            };
        });

        // Use the max target across all rombels for display purposes
        const maxTarget = rombels.length > 0 ? Math.max(...rombels.map(r => r.target)) : defaultTotal;

        return {
            totalTarget: maxTarget || defaultTotal,
            rombels,
            hasExceed: rombels.some(r => r.exceeded),
            exceededList: rombels.filter(r => r.exceeded),
        };
    }

    // ========== STEP 4: INPUT MANUAL ==========
    setInputCell(rombel, hari, jamKe, mapelNama, mapelKode, guruNama, guruNip) {
        const input_jadwal = { ...this.state.input_jadwal };
        if (!input_jadwal[rombel]) input_jadwal[rombel] = {};
        const key = `${hari}_${jamKe}`;
        input_jadwal[rombel][key] = { mapel_nama: mapelNama, mapel_kode: mapelKode, guru_nama: guruNama, guru_nip: guruNip };
        this.setState({ input_jadwal, input_editing_cell: null });
    }

    setInputCellAgama(rombel, hari, jamKe, agamaItems) {
        // agamaItems: [ { mapel_nama, mapel_kode, guru_nama, guru_nip }, ... ]
        const input_jadwal = { ...this.state.input_jadwal };
        if (!input_jadwal[rombel]) input_jadwal[rombel] = {};
        const key = `${hari}_${jamKe}`;
        input_jadwal[rombel][key] = { is_agama: true, mapel_nama: 'Pendidikan Agama', items: agamaItems };
        this.setState({ input_jadwal, input_editing_cell: null });
    }

    clearInputCell(rombel, hari, jamKe) {
        const input_jadwal = { ...this.state.input_jadwal };
        if (input_jadwal[rombel]) {
            const key = `${hari}_${jamKe}`;
            delete input_jadwal[rombel][key];
        }
        this.setState({ input_jadwal, input_editing_cell: null });
    }

    getInputConflicts() {
        const { input_jadwal } = this.state;
        const conflicts = [];
        const guruSlots = {};
        Object.entries(input_jadwal).forEach(([rombel, cells]) => {
            Object.entries(cells).forEach(([key, val]) => {
                if (val.is_agama) return; // agama gurus teach in parallel, skip conflict check
                const guruKey = `${val.guru_nama}|${key}`;
                if (guruSlots[guruKey] && guruSlots[guruKey] !== rombel) {
                    conflicts.push({ guru: val.guru_nama, key, rombel_1: guruSlots[guruKey], rombel_2: rombel });
                } else {
                    guruSlots[guruKey] = rombel;
                }
            });
        });
        return conflicts;
    }

    getInputMapelSummary(rombel) {
        const { input_jadwal, penugasan_list } = this.state;
        const cells = input_jadwal[rombel] || {};
        // Count filled jam per mapel
        const filledJam = {};
        Object.values(cells).forEach(c => {
            if (!filledJam[c.mapel_nama]) filledJam[c.mapel_nama] = 0;
            filledJam[c.mapel_nama]++;
        });
        // Get target jam from penugasan
        const penugasanForRombel = penugasan_list.filter(p => p.rombel_nama === rombel);
        // Merge unique mapels
        const mapelMap = {};
        penugasanForRombel.forEach(p => {
            if (!mapelMap[p.mapel_nama]) mapelMap[p.mapel_nama] = { target: 0, filled: 0 };
            mapelMap[p.mapel_nama].target += (p.jumlah_jam_per_minggu || 0);
        });
        Object.keys(filledJam).forEach(nama => {
            if (!mapelMap[nama]) mapelMap[nama] = { target: 0, filled: 0 };
            mapelMap[nama].filled = filledJam[nama];
        });
        return mapelMap;
    }

    saveInputJadwal() {
        const { input_jadwal } = this.state;
        const jadwalData = [];
        const rombelKelasMap = {};
        this.state.penugasan_list.forEach(p => {
            if (p.rombel_nama && p.kelas) rombelKelasMap[p.rombel_nama] = p.kelas;
        });
        Object.entries(input_jadwal).forEach(([rombel, cells]) => {
            Object.entries(cells).forEach(([key, val]) => {
                const [hari, jam_ke] = key.split('_').map(Number);
                if (val.is_agama && val.items) {
                    // Expand agama package into individual entries
                    val.items.forEach(item => {
                        jadwalData.push({
                            rombel_nama: rombel,
                            kelas: rombelKelasMap[rombel] || '',
                            hari, jam_ke,
                            mapel_nama: item.mapel_nama,
                            mapel_kode: item.mapel_kode || '',
                            guru_nama: item.guru_nama,
                            guru_nip: item.guru_nip || '',
                            keterangan: 'Pemisahan agama',
                        });
                    });
                } else {
                    jadwalData.push({
                        rombel_nama: rombel,
                        kelas: rombelKelasMap[rombel] || '',
                        hari, jam_ke,
                        mapel_nama: val.mapel_nama,
                        mapel_kode: val.mapel_kode || '',
                        guru_nama: val.guru_nama,
                        guru_nip: val.guru_nip || '',
                    });
                }
            });
        });

        if (jadwalData.length === 0) {
            this.showMessage('Belum ada jadwal yang diisi. Silakan isi minimal 1 slot.', 'error');
            return;
        }

        this.setState({ input_saving: true });
        axios.post(`${conf.uri_backend}api/v1/jadwal-v2/import-manual`, {
            institusi: this.state.institusi_id,
            tahun_ajaran: this.state.tahun_ajaran,
            semester: this.state.semester,
            kurikulum: this.state.kurikulum,
            jadwal: jadwalData,
        }, this.getApiHeaders())
            .then((res) => {
                this.setState({ input_saving: false });
                if (res.data.status) {
                    this.setState({
                        generate_result: res.data.data,
                        versi_draft_id: res.data.data.versi?.id,
                    });
                    try { sessionStorage.setItem('pc_generate_result', JSON.stringify(res.data.data)); } catch (e) { }
                    this.showMessage(`Jadwal manual berhasil disimpan! ${res.data.data.total_jadwal} entri.`);
                    this.loadPreview(res.data.data.versi.id);
                    this.loadVersiList();
                } else {
                    this.showMessage(res.data.message || 'Gagal menyimpan', 'error');
                }
            })
            .catch((err) => {
                this.setState({ input_saving: false });
                this.showMessage('Error: ' + (err.response?.data?.message || err.message), 'error');
            });
    }

    doGenerate() {
        // Pre-validate: check if any rombel exceeds target jam
        const validation = this.getJamValidation();
        if (validation.hasExceed) {
            const names = validation.exceededList.map(r => `• ${r.rombel_nama}: terpakai ${r.jam} jam (tersedia ${r.target}, kelebihan ${r.diff} jam)`).join('\n');
            this.showMessage(`Tidak bisa generate! Rombel berikut memiliki jam penugasan yang melebihi kuota waktu yang tersedia:\n${names}\n\nKurangi jumlah jam di Step 2 terlebih dahulu.`, 'error');
            return;
        }

        this.setState({ generating: true, generate_result: null });
        axios.post(`${conf.uri_backend}api/v1/jadwal-v2/generate`, {
            institusi: this.state.institusi_id,
            tahun_ajaran: this.state.tahun_ajaran,
            semester: this.state.semester,
            kurikulum: this.state.kurikulum,
            mode_penyebaran: this.state.mode_penyebaran,
        }, this.getApiHeaders())
            .then((res) => {
                this.setState({ generating: false });
                if (res.data.status) {
                    this.setState({
                        generate_result: res.data.data,
                        versi_draft_id: res.data.data.versi?.id,
                    });
                    try { sessionStorage.setItem('pc_generate_result', JSON.stringify(res.data.data)); } catch (e) { }
                    this.showMessage(`Jadwal berhasil digenerate! ${res.data.data.total_jadwal} entri dibuat.`);
                    this.loadPreview(res.data.data.versi.id);
                    this.loadVersiList();
                } else {
                    this.showMessage(res.data.message || "Gagal generate", "error");
                }
            })
            .catch((err) => {
                this.setState({ generating: false });
                this.showMessage("Error: " + (err.response?.data?.message || err.message), "error");
            });
    }

    loadPreview(versiId) {
        this.setState({ loading: true });
        return axios.get(`${conf.uri_backend}api/v1/jadwal-v2/jadwal/rombel?${this.getBaseParams()}&versi_id=${versiId}`, this.getApiHeaders())
            .then((res) => {
                if (res.data.status) {
                    // Backend bisa balikin array flat ATAU object grouped per nama hari
                    // (mis. { "Senin": [...], "Selasa": [...] }). Normalisasi jadi array
                    // supaya jadwal_preview.forEach() di renderStep4() tidak crash.
                    const raw = res.data.data;
                    let flatJadwal = [];
                    if (Array.isArray(raw)) {
                        flatJadwal = raw;
                    } else if (raw && typeof raw === 'object') {
                        flatJadwal = Object.values(raw).flat();
                    }
                    this.setState({
                        jadwal_preview: flatJadwal,
                        loading: false,
                    });
                }
            })
            .catch(() => this.setState({ loading: false }));
    }

    loadVersiList() {
        return axios.get(`${conf.uri_backend}api/v1/jadwal-v2/versi?${this.getBaseParams()}`, this.getApiHeaders())
            .then((res) => {
                if (res.data.status) {
                    const list = res.data.data || [];
                    this.setState({ versi_list: list });
                    // Auto-detect draft and load preview if jadwal_preview is empty
                    if (this.state.jadwal_preview.length === 0) {
                        const draft = list.find(v => v.is_draft);
                        if (draft) {
                            this.setState({ versi_draft_id: draft.id });
                            // Kembalikan promise-nya juga supaya bisa ikut ditunggu
                            return this.loadPreview(draft.id);
                        }
                    }
                }
            });
    }

    formatTime(timeStr) {
        if (!timeStr) return '';
        // Convert HH:MM:SS to HH:MM
        return timeStr.substring(0, 5);
    }

    saveVersi() {
        const { versi_draft_id, new_versi_name } = this.state;
        if (!versi_draft_id || !new_versi_name.trim()) return;
        this.setState({ saving: true });
        axios.post(`${conf.uri_backend}api/v1/jadwal-v2/versi/save-draft`, {
            institusi: this.state.institusi_id,
            tahun_ajaran: this.state.tahun_ajaran,
            semester: this.state.semester,
            kurikulum: this.state.kurikulum,
            draft_versi_id: versi_draft_id,
            nama_versi: new_versi_name.trim(),
        }, this.getApiHeaders())
            .then((res) => {
                this.setState({ saving: false, show_save_versi_modal: false, new_versi_name: "" });
                if (res.data.status) {
                    this.showMessage("Versi berhasil disimpan!");
                    this.loadVersiList();
                    if (res.data.data?.id) {
                        this.setState({ versi_draft_id: null });
                    }
                }
            })
            .catch(() => {
                this.setState({ saving: false });
                this.showMessage("Gagal menyimpan versi", "error");
            });
    }

    renameVersi(versiId, newName) {
        axios.post(`${conf.uri_backend}api/v1/jadwal-v2/versi/rename/${versiId}`, {
            nama_versi: newName,
        }, this.getApiHeaders())
            .then((res) => {
                if (res.data.status) {
                    this.showMessage("Nama versi diubah!");
                    this.loadVersiList();
                    this.setState({ rename_versi_id: null, rename_versi_name: "" });
                }
            });
    }

    deleteVersi(versiId) {
        if (!window.confirm("Hapus versi ini beserta semua jadwalnya?")) return;
        axios.delete(`${conf.uri_backend}api/v1/jadwal-v2/versi/${versiId}`, this.getApiHeaders())
            .then((res) => {
                if (res.data.status) {
                    this.showMessage("Versi dihapus!");
                    this.loadVersiList();
                    // If deleted the currently previewed versi, clear preview
                    if (this.state.versi_draft_id === versiId) {
                        this.setState({ jadwal_preview: [], versi_draft_id: null, generate_result: null });
                    }
                }
            });
    }

    // ========== STEP 4 MANUAL ADJUSTMENT / MOVE / SWAP METHODS ==========
    getWaktuListForRombel(rombelNama) {
        const { waktu_per_tingkat, penugasan_list, waktu_list } = this.state;
        const p = (penugasan_list || []).find(item => item.rombel_nama === rombelNama);
        const kelas = p?.kelas || '';
        if (waktu_per_tingkat && kelas && Array.isArray(waktu_per_tingkat[kelas]) && waktu_per_tingkat[kelas].length > 0) {
            return waktu_per_tingkat[kelas];
        }
        if (waktu_per_tingkat && typeof waktu_per_tingkat === 'object') {
            const keys = Object.keys(waktu_per_tingkat);
            if (keys.length > 0 && Array.isArray(waktu_per_tingkat[keys[0]]) && waktu_per_tingkat[keys[0]].length > 0) {
                return waktu_per_tingkat[keys[0]];
            }
        }
        return Array.isArray(waktu_list) ? waktu_list : [];
    }

    getLessonPeriodsForRombelAndHari(rombelNama, hari) {
        const wList = this.getWaktuListForRombel(rombelNama);
        return wList
            .filter(w => Number(w.hari) === Number(hari) && (w.tipe === 'pelajaran' || (!w.is_istirahat && !w.tipe)))
            .sort((a, b) => {
                if (a.urutan !== undefined && b.urutan !== undefined) return a.urutan - b.urutan;
                const jamA = Number(a.jam_ke) || 0;
                const jamB = Number(b.jam_ke) || 0;
                if (jamA !== jamB) return jamA - jamB;
                if (a.waktu_mulai && b.waktu_mulai) return a.waktu_mulai.localeCompare(b.waktu_mulai);
                return 0;
            });
    }

    getActiveDaysForRombel(rombelNama) {
        const wList = this.getWaktuListForRombel(rombelNama);
        const hariSet = new Set();
        wList.forEach(w => {
            if (w.tipe === 'pelajaran' || (!w.is_istirahat && !w.tipe)) {
                hariSet.add(Number(w.hari));
            }
        });
        const days = [...hariSet].sort((a, b) => a - b);
        return days.length > 0 ? days : (this.state.hari_aktif || [1, 2, 3, 4, 5]);
    }

    copyGenerateToInput() {
        const { jadwal_preview } = this.state;
        if (!jadwal_preview || jadwal_preview.length === 0) {
            this.showMessage("Tidak ada data jadwal untuk disalin ke mode input.", "error");
            return;
        }

        const newInputJadwal = {};
        jadwal_preview.forEach(j => {
            const rn = j.rombel_nama;
            if (!rn) return;
            if (!newInputJadwal[rn]) newInputJadwal[rn] = {};
            const key = `${j.hari}_${j.jam_ke}`;

            const isAgama = j.keterangan === 'Pemisahan agama';
            if (isAgama) {
                if (!newInputJadwal[rn][key]) {
                    newInputJadwal[rn][key] = {
                        is_agama: true,
                        mapel_nama: 'Pendidikan Agama',
                        items: []
                    };
                }
                newInputJadwal[rn][key].items.push({
                    mapel_nama: j.mapel_nama,
                    mapel_kode: j.mapel_kode || '',
                    guru_nama: j.guru_nama,
                    guru_nip: j.guru_nip || ''
                });
            } else {
                newInputJadwal[rn][key] = {
                    mapel_nama: j.mapel_nama,
                    mapel_kode: j.mapel_kode || '',
                    guru_nama: j.guru_nama,
                    guru_nip: j.guru_nip || ''
                };
            }
        });

        this.setState({
            input_jadwal: newInputJadwal,
            step4_mode: 'input'
        });
        this.showMessage("Jadwal hasil generate berhasil disalin ke mode Input Jadwal Manual! Anda dapat mengeditnya secara manual.", "success");
    }

    toggleStep4VisualSwap() {
        if (this.state.step4_is_visual_swap) {
            this.cancelStep4VisualSwap();
        } else {
            this.setState({
                step4_is_visual_swap: true,
                step4_visual_swap_source: null,
            });
            this.showMessage("Mode Tukar / Pindah Cepat Aktif: Klik slot pertama di tabel sebagai sumber.", "info");
        }
    }

    cancelStep4VisualSwap() {
        this.setState({
            step4_is_visual_swap: false,
            step4_visual_swap_source: null,
            step4_swap_confirm_modal: null,
        });
    }

    handleStep4CellClick(cellData, rombelName, hari, jamKe, timeStr) {
        const { step4_is_visual_swap, step4_visual_swap_source, versi_draft_id } = this.state;
        const versiId = cellData?.versi_id || versi_draft_id;

        // Visual Swap Mode
        if (step4_is_visual_swap) {
            const clickedSlot = {
                id: cellData?.id || null,
                versi_id: versiId,
                rombel_nama: cellData?.rombel_nama || rombelName,
                hari: Number(hari),
                jam_ke: Number(jamKe),
                mapel_nama: cellData?.mapel_nama || "",
                guru_nama: cellData?.guru_nama || "",
                waktuLabel: timeStr
            };

            if (!step4_visual_swap_source) {
                // First click: select source
                this.setState({ step4_visual_swap_source: clickedSlot });
                this.showMessage(`Slot 1 dipilih: ${rombelName} ${this.renderHariLabel(hari)} Jam ke-${jamKe} (${clickedSlot.mapel_nama || 'Kosong'}). Sekarang klik slot kedua di tabel untuk menukar/memindahkan.`, "info");
            } else {
                // Second click: check if same slot
                if (step4_visual_swap_source.rombel_nama === clickedSlot.rombel_nama &&
                    step4_visual_swap_source.hari === clickedSlot.hari &&
                    step4_visual_swap_source.jam_ke === clickedSlot.jam_ke) {
                    this.showMessage("Pilih slot lain yang berbeda untuk ditukar.", "warning");
                    return;
                }

                // Open swap confirmation modal
                this.setState({
                    step4_swap_confirm_modal: {
                        slot_a: step4_visual_swap_source,
                        slot_b: clickedSlot,
                        bentrokan_error: null
                    }
                });
            }
            return;
        }

        // Normal mode: open cell action modal
        const isFilled = !!(cellData && (cellData.id || cellData.mapel_nama));
        const defaultTab = isFilled ? 'pindah' : 'isi';

        const rombelPenugasan = (this.state.penugasan_list || []).filter(p => p.rombel_nama === rombelName);

        let initialPenugasan = "";
        let initialMapel = cellData?.mapel_nama || "";
        let initialGuru = cellData?.guru_nama || "";
        if (isFilled && cellData.mapel_nama && cellData.guru_nama) {
            initialPenugasan = `${cellData.mapel_nama}|||${cellData.guru_nama}`;
        } else if (rombelPenugasan.length > 0) {
            initialPenugasan = `${rombelPenugasan[0].mapel_nama}|||${rombelPenugasan[0].guru_nama}`;
            initialMapel = rombelPenugasan[0].mapel_nama;
            initialGuru = rombelPenugasan[0].guru_nama;
        }

        const activeDays = this.getActiveDaysForRombel(rombelName);
        const targetHari = activeDays.includes(Number(hari)) ? Number(hari) : (activeDays[0] || 1);
        const validPeriods = this.getLessonPeriodsForRombelAndHari(rombelName, targetHari);
        const targetJamKe = validPeriods.some(p => Number(p.jam_ke) === Number(jamKe))
            ? Number(jamKe)
            : (validPeriods[0]?.jam_ke ? Number(validPeriods[0].jam_ke) : 1);

        this.setState({
            step4_active_cell_modal: {
                slot: cellData || null,
                id: cellData?.id || null,
                versi_id: versiId,
                rombel_nama: rombelName,
                hari: Number(hari),
                jam_ke: Number(jamKe),
                waktuLabel: timeStr,
                target_hari: targetHari,
                target_jam_ke: targetJamKe,
                is_empty: !isFilled,
                action_tab: defaultTab,
                selected_penugasan: initialPenugasan,
                mapel_nama: initialMapel,
                guru_nama: initialGuru,
                bentrokan_error: null
            }
        });
    }

    executeStep4Move(force = false) {
        const { step4_active_cell_modal, institusi_id, tahun_ajaran, semester, kurikulum, versi_draft_id, penugasan_list } = this.state;
        if (!step4_active_cell_modal) return;

        const { id, rombel_nama, mapel_nama, guru_nama, target_hari, target_jam_ke } = step4_active_cell_modal;
        const versiId = step4_active_cell_modal.versi_id || versi_draft_id;

        const p = (penugasan_list || []).find(item => item.rombel_nama === rombel_nama && item.mapel_nama === mapel_nama);
        const kelas = p?.kelas || undefined;

        const payload = {
            id: id || undefined,
            versi_id: versiId,
            institusi: institusi_id,
            institusi_id: institusi_id,
            tahun_ajaran: tahun_ajaran,
            semester: semester,
            kurikulum: kurikulum,
            rombel_nama: rombel_nama,
            kelas: kelas,
            hari: Number(target_hari),
            jam_ke: Number(target_jam_ke),
            mapel_nama: mapel_nama,
            guru_nama: guru_nama,
            guru_nip: p?.guru_nip || undefined,
            mapel_kode: p?.mapel_kode || undefined,
            force: !!force
        };

        this.setState({ step4_cell_saving: true });

        axios.post(`${conf.uri_backend}api/v1/jadwal-v2/jadwal/update-slot`, payload, this.getApiHeaders())
            .then((res) => {
                this.setState({ step4_cell_saving: false });
                if (res.data.status) {
                    this.showMessage(res.data.message || `Slot ${rombel_nama} berhasil dipindahkan ke ${this.renderHariLabel(target_hari)} Jam ke-${target_jam_ke}!`, "success");
                    this.setState({ step4_active_cell_modal: null });
                    this.loadPreview(versiId);
                    this.refreshDraftBentrokan();
                } else if (res.data.is_bentrok) {
                    this.setState(prevState => ({
                        step4_active_cell_modal: {
                            ...prevState.step4_active_cell_modal,
                            bentrokan_error: {
                                message: res.data.message || "Terdeteksi bentrokan jika jadwal dipindahkan ke slot ini.",
                                bentrokan: res.data.bentrokan || []
                            }
                        }
                    }));
                } else {
                    this.showMessage(res.data.message || "Gagal memindahkan slot jadwal.", "error");
                }
            })
            .catch((err) => {
                this.setState({ step4_cell_saving: false });
                const data = err.response?.data;
                if (data?.is_bentrok) {
                    this.setState(prevState => ({
                        step4_active_cell_modal: {
                            ...prevState.step4_active_cell_modal,
                            bentrokan_error: {
                                message: data.message || "Terdeteksi bentrokan jika jadwal dipindahkan ke slot ini.",
                                bentrokan: data.bentrokan || []
                            }
                        }
                    }));
                } else {
                    this.showMessage(data?.message || err.message || "Terjadi kesalahan saat memindahkan slot.", "error");
                }
            });
    }

    executeStep4Swap(force = false) {
        const { step4_swap_confirm_modal, institusi_id, versi_draft_id } = this.state;
        if (!step4_swap_confirm_modal) return;
        const { slot_a, slot_b } = step4_swap_confirm_modal;
        const versiId = slot_a.versi_id || slot_b.versi_id || versi_draft_id;

        let payload = {
            versi_id: versiId,
            institusi: institusi_id,
            force: !!force
        };

        if (slot_a.id && slot_b.id) {
            payload.slot_a_id = slot_a.id;
            payload.slot_b_id = slot_b.id;
        } else {
            payload.slot_a = {
                id: slot_a.id || null,
                hari: Number(slot_a.hari),
                jam_ke: Number(slot_a.jam_ke),
                rombel_nama: slot_a.rombel_nama
            };
            payload.slot_b = {
                id: slot_b.id || null,
                hari: Number(slot_b.hari),
                jam_ke: Number(slot_b.jam_ke),
                rombel_nama: slot_b.rombel_nama
            };
        }

        this.setState({ step4_cell_saving: true });

        axios.post(`${conf.uri_backend}api/v1/jadwal-v2/jadwal/swap-slot`, payload, this.getApiHeaders())
            .then((res) => {
                this.setState({ step4_cell_saving: false });
                if (res.data.status) {
                    this.showMessage(res.data.message || "Slot jadwal berhasil ditukar!", "success");
                    this.setState({
                        step4_swap_confirm_modal: null,
                        step4_is_visual_swap: false,
                        step4_visual_swap_source: null
                    });
                    this.loadPreview(versiId);
                    this.refreshDraftBentrokan();
                } else if (res.data.is_bentrok) {
                    this.setState(prevState => ({
                        step4_swap_confirm_modal: {
                            ...prevState.step4_swap_confirm_modal,
                            bentrokan_error: {
                                message: res.data.message || "Terdeteksi bentrokan jika kedua slot ditukar.",
                                bentrokan: res.data.bentrokan || []
                            }
                        }
                    }));
                } else {
                    this.showMessage(res.data.message || "Gagal menukar slot jadwal.", "error");
                }
            })
            .catch((err) => {
                this.setState({ step4_cell_saving: false });
                const data = err.response?.data;
                if (data?.is_bentrok) {
                    this.setState(prevState => ({
                        step4_swap_confirm_modal: {
                            ...prevState.step4_swap_confirm_modal,
                            bentrokan_error: {
                                message: data.message || "Terdeteksi bentrokan jika kedua slot ditukar.",
                                bentrokan: data.bentrokan || []
                            }
                        }
                    }));
                } else {
                    this.showMessage(data?.message || err.message || "Terjadi kesalahan saat menukar slot.", "error");
                }
            });
    }

    executeStep4DeleteSlot() {
        const { step4_active_cell_modal, versi_draft_id } = this.state;
        if (!step4_active_cell_modal) return;
        const { id, rombel_nama, hari, jam_ke } = step4_active_cell_modal;
        const versiId = step4_active_cell_modal.versi_id || versi_draft_id;

        const payload = {
            id: id || undefined,
            versi_id: versiId,
            rombel_nama: rombel_nama,
            hari: Number(hari),
            jam_ke: Number(jam_ke)
        };

        this.setState({ step4_cell_saving: true });

        axios.post(`${conf.uri_backend}api/v1/jadwal-v2/jadwal/delete-slot`, payload, this.getApiHeaders())
            .then((res) => {
                this.setState({ step4_cell_saving: false, step4_active_cell_modal: null });
                if (res.data.status) {
                    this.showMessage(res.data.message || "Slot jadwal berhasil dikosongkan.", "success");
                    this.loadPreview(versiId);
                    this.refreshDraftBentrokan();
                } else {
                    this.showMessage(res.data.message || "Gagal mengosongkan slot jadwal.", "error");
                }
            })
            .catch((err) => {
                this.setState({ step4_cell_saving: false });
                this.showMessage(err.response?.data?.message || err.message || "Gagal mengosongkan slot.", "error");
            });
    }

    executeStep4FillSlot(force = false) {
        const { step4_active_cell_modal, institusi_id, tahun_ajaran, semester, kurikulum, versi_draft_id, penugasan_list } = this.state;
        if (!step4_active_cell_modal) return;
        const { id, rombel_nama, hari, jam_ke, mapel_nama, guru_nama } = step4_active_cell_modal;
        const versiId = step4_active_cell_modal.versi_id || versi_draft_id;

        if (!mapel_nama || !guru_nama) {
            this.showMessage("Mata Pelajaran dan Guru Pengajar wajib dipilih.", "warning");
            return;
        }

        const p = (penugasan_list || []).find(item => item.rombel_nama === rombel_nama && item.mapel_nama === mapel_nama && item.guru_nama === guru_nama);

        const payload = {
            id: id || undefined,
            versi_id: versiId,
            institusi: institusi_id,
            institusi_id: institusi_id,
            tahun_ajaran: tahun_ajaran,
            semester: semester,
            kurikulum: kurikulum,
            rombel_nama: rombel_nama,
            kelas: p?.kelas || undefined,
            hari: Number(hari),
            jam_ke: Number(jam_ke),
            mapel_nama: mapel_nama,
            mapel_kode: p?.mapel_kode || undefined,
            guru_nama: guru_nama,
            guru_nip: p?.guru_nip || undefined,
            force: !!force
        };

        this.setState({ step4_cell_saving: true });

        axios.post(`${conf.uri_backend}api/v1/jadwal-v2/jadwal/update-slot`, payload, this.getApiHeaders())
            .then((res) => {
                this.setState({ step4_cell_saving: false });
                if (res.data.status) {
                    this.showMessage(res.data.message || `Slot ${rombel_nama} berhasil disimpan dengan ${mapel_nama}!`, "success");
                    this.setState({ step4_active_cell_modal: null });
                    this.loadPreview(versiId);
                    this.refreshDraftBentrokan();
                } else if (res.data.is_bentrok) {
                    this.setState(prevState => ({
                        step4_active_cell_modal: {
                            ...prevState.step4_active_cell_modal,
                            bentrokan_error: {
                                message: res.data.message || "Terdeteksi bentrokan untuk guru atau rombel ini.",
                                bentrokan: res.data.bentrokan || []
                            }
                        }
                    }));
                } else {
                    this.showMessage(res.data.message || "Gagal mengisi slot jadwal.", "error");
                }
            })
            .catch((err) => {
                this.setState({ step4_cell_saving: false });
                const data = err.response?.data;
                if (data?.is_bentrok) {
                    this.setState(prevState => ({
                        step4_active_cell_modal: {
                            ...prevState.step4_active_cell_modal,
                            bentrokan_error: {
                                message: data.message || "Terdeteksi bentrokan untuk guru atau rombel ini.",
                                bentrokan: data.bentrokan || []
                            }
                        }
                    }));
                } else {
                    this.showMessage(data?.message || err.message || "Terjadi kesalahan saat mengisi slot.", "error");
                }
            });
    }

    refreshDraftBentrokan() {
        const versiId = this.state.versi_draft_id;
        if (!versiId) return;

        axios.get(`${conf.uri_backend}api/v1/jadwal-v2/jadwal/bentrokan?${this.getBaseParams()}&versi_id=${versiId}`, this.getApiHeaders())
            .then((res) => {
                if (res.data.status && this.state.generate_result) {
                    this.setState(prevState => ({
                        generate_result: {
                            ...prevState.generate_result,
                            bentrokan: res.data.data || []
                        }
                    }));
                }
            })
            .catch(() => {});
    }

    jumpToConflictDay(c) {
        let targetHari = c.hari;
        if (!targetHari && c.pesan) {
            const pLower = c.pesan.toLowerCase();
            if (pLower.includes("senin")) targetHari = 1;
            else if (pLower.includes("selasa")) targetHari = 2;
            else if (pLower.includes("rabu")) targetHari = 3;
            else if (pLower.includes("kamis")) targetHari = 4;
            else if (pLower.includes("jumat") || pLower.includes("jum'at")) targetHari = 5;
            else if (pLower.includes("sabtu")) targetHari = 6;
            else if (pLower.includes("minggu")) targetHari = 7;
        }
        if (targetHari) {
            this.setState({ preview_hari_tab: Number(targetHari) });
        }
        setTimeout(() => {
            const el = document.querySelector('.pc-timetable-section');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 50);
    }

    // ========== RENDER ==========
    renderHariLabel(hari) {
        const labels = { 1: "Senin", 2: "Selasa", 3: "Rabu", 4: "Kamis", 5: "Jumat", 6: "Sabtu", 7: "Minggu" };
        return labels[hari] || hari;
    }

    renderTipeBadge(entry) {
        const tipe = entry.tipe || (entry.is_istirahat ? "istirahat" : "pelajaran");
        const map = {
            pelajaran: { cls: "pc-badge-info", label: "Pelajaran", icon: "bx-book" },
            istirahat: { cls: "pc-badge-warning", label: "Istirahat", icon: "bx-coffee" },
            upacara: { cls: "pc-badge-success", label: "Upacara", icon: "bx-flag" },
            kegiatan: { cls: "pc-badge-default", label: entry.keterangan || "Kegiatan", icon: "bx-calendar-event" },
        };
        const info = map[tipe] || map.pelajaran;
        return <span className={`pc-badge ${info.cls}`}><i className={`bx ${info.icon}`}></i> {info.label}</span>;
    }

    renderStep0() {
        const { tahun_ajaran, tahun_ajaran_options, semester, kelas_options, selected_kelas, kurikulum, settings_locked } = this.state;

        return (
            <div className="pc-wizard-content pc-fade-in">
                <h3 className="pc-wizard-step-title"><i className="bx bx-cog"></i> Pengaturan Jadwal</h3>
                <p className="pc-wizard-step-desc">Tentukan parameter dasar untuk penjadwalan. Pengaturan ini berlaku untuk semua langkah berikutnya.</p>

                <div className="pc-settings-grid">
                    {/* Tahun Ajaran */}
                    <div className="pc-settings-card">
                        <div className="pc-settings-card-icon"><i className="bx bx-calendar"></i></div>
                        <div className="pc-settings-card-body">
                            <label className="pc-settings-label">Tahun Ajaran</label>
                            {!settings_locked ? (
                                <select
                                    className="pc-settings-select"
                                    value={tahun_ajaran}
                                    onChange={(e) => { const val = e.target.value; this.setState({ tahun_ajaran: val }, () => { sessionStorage.setItem('pc_tahun_ajaran', val); this.loadKelasOptions(); }); }}
                                >
                                    {tahun_ajaran_options.map(ta => (
                                        <option key={ta} value={ta}>{ta}</option>
                                    ))}
                                </select>
                            ) : (
                                <div className="pc-settings-value">{tahun_ajaran}</div>
                            )}
                            <small className="pc-settings-hint">Default otomatis, bisa diganti sesuai kebutuhan</small>
                        </div>
                    </div>

                    {/* Semester */}
                    <div className="pc-settings-card">
                        <div className="pc-settings-card-icon"><i className="bx bx-bookmarks"></i></div>
                        <div className="pc-settings-card-body">
                            <label className="pc-settings-label">Semester</label>
                            <div className="pc-semester-toggle">
                                <button
                                    className={`pc-sem-btn ${semester === "1" ? "pc-sem-active" : ""}`}
                                    onClick={() => this.setState({ semester: "1" }, () => sessionStorage.setItem('pc_semester', '1'))}
                                    disabled={settings_locked}
                                >
                                    <span>1</span> <small>Ganjil</small>
                                </button>
                                <button
                                    className={`pc-sem-btn ${semester === "2" ? "pc-sem-active" : ""}`}
                                    onClick={() => this.setState({ semester: "2" }, () => sessionStorage.setItem('pc_semester', '2'))}
                                    disabled={settings_locked}
                                >
                                    <span>2</span> <small>Genap</small>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Kurikulum */}
                    <div className="pc-settings-card">
                        <div className="pc-settings-card-icon"><i className="bx bx-book-alt"></i></div>
                        <div className="pc-settings-card-body">
                            <label className="pc-settings-label">Kurikulum</label>
                            <div className="pc-kurikulum-toggle">
                                {["merdeka", "k13"].map(k => (
                                    <button
                                        key={k}
                                        className={`pc-kur-btn ${kurikulum === k ? "pc-kur-active" : ""}`}
                                        onClick={() => this.setState({ kurikulum: k }, () => this.loadKelasOptions())}
                                        disabled={settings_locked}
                                    >
                                        {k === "merdeka" ? "Merdeka" : "K13"}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Kelas */}
                    <div className="pc-settings-card pc-settings-card-wide">
                        <div className="pc-settings-card-icon"><i className="bx bx-group"></i></div>
                        <div className="pc-settings-card-body">
                            <label className="pc-settings-label">Kelas yang Dijadwalkan</label>
                            {kelas_options.length > 0 ? (
                                <div className="pc-kelas-chips">
                                    {kelas_options.map(kelas => {
                                        const isSelected = selected_kelas.includes(kelas);
                                        return (
                                            <button
                                                key={kelas}
                                                className={`pc-kelas-chip ${isSelected ? "pc-kelas-chip-active" : ""}`}
                                                onClick={() => {
                                                    if (settings_locked) return;
                                                    const newSel = isSelected
                                                        ? selected_kelas.filter(k => k !== kelas)
                                                        : [...selected_kelas, kelas];
                                                    this.setState({ selected_kelas: newSel });
                                                }}
                                                disabled={settings_locked}
                                            >
                                                {isSelected && <i className="bx bx-check"></i>}
                                                Kelas {kelas}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="pc-settings-empty">
                                    <i className="bx bx-info-circle"></i> Belum ada data rombel. Pastikan rombel sudah dibuat di menu Rombel.
                                </div>
                            )}
                            <small className="pc-settings-hint">
                                {selected_kelas.length} dari {kelas_options.length} kelas dipilih
                            </small>
                        </div>
                    </div>


                </div>

                {/* Lock/Proceed Button */}
                <div className="pc-wizard-actions" style={{ marginTop: 24 }}>
                    {!settings_locked ? (
                        <button
                            onClick={() => this.lockSettings()}
                            disabled={selected_kelas.length === 0}
                            className="pc-btn pc-btn-primary pc-btn-lg pc-btn-glow"
                        >
                            <i className="bx bx-lock-alt"></i> Kunci Pengaturan & Lanjutkan
                        </button>
                    ) : (
                        <button onClick={() => this.unlockSettings()} className="pc-btn pc-btn-outline">
                            <i className="bx bx-lock-open-alt"></i> Ubah Pengaturan
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Switch to a different tingkat in per-tingkat mode
    switchTingkat(newTingkat) {
        const { waktu_active_tingkat, waktu_list, waktu_per_tingkat, waktu_saved, waktu_saved_per_tingkat } = this.state;
        // Save current tingkat's waktu_list and save status into the maps
        const perTingkat = { ...(waktu_per_tingkat || {}) };
        const savedMap = { ...(waktu_saved_per_tingkat || {}) };
        if (waktu_active_tingkat) {
            perTingkat[waktu_active_tingkat] = [...waktu_list];
            savedMap[waktu_active_tingkat] = waktu_saved;
        }
        // Load new tingkat's data
        const newList = perTingkat[newTingkat] || [];
        // Restore new tingkat's save status (default: true if data exists from server)
        const newSavedStatus = savedMap[newTingkat] !== undefined ? savedMap[newTingkat] : (newList.length > 0);

        // Derive hari_aktif from new data
        const hariSet = new Set();
        newList.forEach(w => hariSet.add(Number(w.hari)));
        const derivedHari = [...hariSet].sort((a, b) => a - b);

        this.setState({
            waktu_active_tingkat: newTingkat,
            waktu_list: newList,
            waktu_per_tingkat: perTingkat,
            waktu_saved_per_tingkat: savedMap,
            hari_aktif: derivedHari.length > 0 ? derivedHari : [1, 2, 3, 4, 5],
            waktu_tab_hari: derivedHari.length > 0 ? derivedHari[0] : 1,
            waktu_saved: newSavedStatus,
        });
    }

    // Copy current tingkat's waktu to another tingkat
    copyToTingkat(toTingkat) {
        const { waktu_list, waktu_active_tingkat } = this.state;
        if (waktu_list.length === 0) {
            this.showMessage(`Kelas ${waktu_active_tingkat} belum memiliki data waktu untuk disalin`, "error");
            return;
        }
        // Save current to source first
        const perTingkat = { ...(this.state.waktu_per_tingkat || {}) };
        if (waktu_active_tingkat) {
            perTingkat[waktu_active_tingkat] = [...waktu_list];
        }
        // Copy to target
        perTingkat[toTingkat] = waktu_list.map(w => ({ ...w }));
        const savedMap2 = { ...(this.state.waktu_saved_per_tingkat || {}) };
        savedMap2[toTingkat] = true; // target is saved (auto-saved to server)
        this.setState({ waktu_per_tingkat: perTingkat, waktu_saved: false, waktu_saved_per_tingkat: savedMap2 });

        // Also save to server
        const hariCounters = {};
        const waktuFinal = waktu_list.map((w) => {
            if (!hariCounters[w.hari]) hariCounters[w.hari] = 0;
            return { ...w, tingkat: String(toTingkat), urutan: hariCounters[w.hari]++ };
        });
        axios.post(`${conf.uri_backend}api/v1/jadwal-v2/waktu/save`, {
            institusi: this.state.institusi_id,
            tahun_ajaran: this.state.tahun_ajaran,
            semester: this.state.semester,
            kurikulum: this.state.kurikulum,
            tingkat: String(toTingkat),
            waktu: waktuFinal,
        }, this.getApiHeaders())
            .then(() => {
                this.showMessage(`Waktu berhasil disalin ke Kelas ${toTingkat}!`);
                this.loadWaktu();
            })
            .catch(() => this.showMessage("Gagal menyalin waktu", "error"));
    }

    renderStep1() {
        const { waktu_list, hari_aktif, default_jam_mulai, default_durasi, default_jumlah_jam, saving, waktu_tab_hari, adding_entry, new_entry, default_istirahat_list, show_apply_modal, apply_target_days, selected_kelas, waktu_mode, waktu_active_tingkat, waktu_per_tingkat } = this.state;
        const hariOptions = [
            { value: 1, label: "Senin", short: "Sen" }, { value: 2, label: "Selasa", short: "Sel" }, { value: 3, label: "Rabu", short: "Rab" },
            { value: 4, label: "Kamis", short: "Kam" }, { value: 5, label: "Jumat", short: "Jum" }, { value: 6, label: "Sabtu", short: "Sab" },
            { value: 7, label: "Minggu", short: "Min" },
        ];

        const dayEntries = waktu_list.filter(w => w.hari === waktu_tab_hari);
        const activeHariOptions = hariOptions.filter(h => hari_aktif.includes(h.value));
        const otherDays = activeHariOptions.filter(h => h.value !== waktu_tab_hari);

        // Tingkat tabs - always show when multiple kelas selected
        const showTingkatTabs = selected_kelas.length > 1;
        const tingkatOptions = [...new Set(selected_kelas)].sort(sortKelas);
        const otherTingkat = tingkatOptions.filter(t => t !== waktu_active_tingkat);
        // Auto-set active tingkat if not set
        if (showTingkatTabs && !waktu_active_tingkat && tingkatOptions.length > 0) {
            setTimeout(() => this.setState({ waktu_active_tingkat: tingkatOptions[0], waktu_mode: 'per_tingkat' }), 0);
        }

        return (
            <div className="pc-wizard-content">
                <h3 className="pc-wizard-step-title"><i className="bx bx-time-five"></i> Atur Waktu & Hari</h3>
                <p className="pc-wizard-step-desc">Tentukan hari aktif, generate waktu otomatis (termasuk istirahat), lalu sesuaikan setiap entri. Tipe setiap slot bisa diubah langsung, dan konfigurasi hari bisa diterapkan ke hari lain.</p>

                {/* Tingkat Tabs */}
                {showTingkatTabs && (
                    <div style={{ marginBottom: 16, background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                            <span style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                                <i className="bx bx-layer" style={{ marginRight: 4 }}></i> Atur Waktu Per Tingkat:
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                            {tingkatOptions.map(t => {
                                const savedData = (waktu_per_tingkat || {})[t] || [];
                                const hasData = savedData.length > 0;
                                const isActive = waktu_active_tingkat === t;
                                const jamCount = hasData ? savedData.filter(w => w.tipe === 'pelajaran' || (!w.is_istirahat && !w.tipe)).length : 0;
                                const isSavedPerTingkat = (this.state.waktu_saved_per_tingkat || {})[t];
                                const isSaved = isActive ? this.state.waktu_saved : (isSavedPerTingkat !== undefined ? isSavedPerTingkat : hasData);
                                const hasUnsaved = (hasData || (isActive && waktu_list.length > 0)) && !isSaved;
                                return (
                                    <button
                                        key={t}
                                        className={`pc-kur-btn ${isActive ? 'pc-kur-active' : ''}`}
                                        onClick={() => this.switchTingkat(t)}
                                        style={{ padding: '8px 16px', fontSize: 13, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
                                    >
                                        <span>Kelas {t}</span>
                                        {hasData || (isActive && waktu_list.length > 0) ? (
                                            <span style={{
                                                fontSize: 10, padding: '1px 6px', borderRadius: 10,
                                                background: hasUnsaved ? (isActive ? '#78350f' : '#fef3c7') : (isActive ? '#166534' : '#dcfce7'),
                                                color: hasUnsaved ? (isActive ? '#fde68a' : '#92400e') : (isActive ? '#bbf7d0' : '#16a34a'),
                                                fontWeight: 600
                                            }}>
                                                {isActive ? waktu_list.filter(w => w.tipe === 'pelajaran' || (!w.is_istirahat && !w.tipe)).length : jamCount} jam
                                                {hasUnsaved ? ' — belum disimpan' : ' ✓ tersimpan'}
                                            </span>
                                        ) : (
                                            <span style={{
                                                fontSize: 10, padding: '1px 6px', borderRadius: 10,
                                                background: isActive ? '#78350f' : '#fef3c7',
                                                color: isActive ? '#fde68a' : '#92400e',
                                                fontWeight: 600
                                            }}>
                                                Belum diatur
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        {/* Salin ke tingkat lain */}
                        {waktu_list.length > 0 && otherTingkat.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '8px 12px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
                                <i className="bx bx-copy" style={{ color: '#3b82f6', fontSize: 14 }}></i>
                                <span style={{ fontSize: 12, color: '#1e40af', fontWeight: 500 }}>Salin waktu Kelas {waktu_active_tingkat} ke:</span>
                                {otherTingkat.map(t => (
                                    <button
                                        key={t}
                                        className="pc-btn pc-btn-xs pc-btn-secondary"
                                        onClick={() => this.copyToTingkat(t)}
                                        style={{ fontSize: 11 }}
                                        title={`Salin konfigurasi waktu ke Kelas ${t}`}
                                    >
                                        Kelas {t}
                                    </button>
                                ))}
                            </div>
                        )}
                        {waktu_list.length === 0 && (
                            <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                                <i className="bx bx-info-circle" style={{ marginRight: 4 }}></i>
                                Kelas {waktu_active_tingkat} belum memiliki data waktu. Silakan generate waktu atau salin dari tingkat lain.
                            </div>
                        )}
                    </div>
                )}

                {/* Quick Config */}
                <div className="pc-config-grid">
                    {/* Hari Aktif - Styled Toggle Buttons */}
                    <div className="pc-config-item">
                        <label>Hari Aktif</label>
                        <div className="pc-day-toggles">
                            {hariOptions.map((h) => (
                                <button
                                    key={h.value}
                                    type="button"
                                    className={`pc-day-toggle ${hari_aktif.includes(h.value) ? "pc-day-toggle-active" : ""}`}
                                    onClick={() => {
                                        const newHari = hari_aktif.includes(h.value)
                                            ? hari_aktif.filter((x) => x !== h.value)
                                            : [...hari_aktif, h.value].sort();
                                        this.setState({ hari_aktif: newHari });
                                    }}
                                >
                                    <span className="pc-day-toggle-short">{h.short}</span>
                                    <span className="pc-day-toggle-full">{h.label}</span>
                                    {hari_aktif.includes(h.value) && <i className="bx bx-check pc-day-toggle-check"></i>}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Config */}
                    <div className="pc-config-row pc-config-row-5">
                        <div className="pc-config-item">
                            <label>Jam Mulai</label>
                            <input type="time" value={default_jam_mulai} className="pc-input" onChange={(e) => this.setState({ default_jam_mulai: e.target.value })} />
                        </div>
                        <div className="pc-config-item">
                            <label>Durasi/Jam (mnt)</label>
                            <input type="number" value={default_durasi} className="pc-input" min={20} max={90} onChange={(e) => this.setState({ default_durasi: parseInt(e.target.value) })} />
                        </div>
                        <div className="pc-config-item">
                            <label>Jumlah Jam/Hari</label>
                            <input type="number" value={default_jumlah_jam} className="pc-input" min={1} max={15} onChange={(e) => this.setState({ default_jumlah_jam: parseInt(e.target.value) })} />
                        </div>
                        <div className="pc-config-item" style={{ gridColumn: '1 / -1' }}>
                            <label>Jam Istirahat <button className="pc-btn pc-btn-xs pc-btn-secondary" style={{ marginLeft: 8 }} onClick={() => {
                                const list = [...default_istirahat_list, { setelah_jam: default_istirahat_list.length > 0 ? default_istirahat_list[default_istirahat_list.length - 1].setelah_jam + 2 : 4, durasi: 15 }];
                                this.setState({ default_istirahat_list: list });
                            }}><i className="bx bx-plus"></i> Tambah Istirahat</button></label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                                {default_istirahat_list.map((b, idx) => (
                                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f1f5f9', borderRadius: 8, padding: '4px 8px' }}>
                                        <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600, minWidth: 85 }}>Istirahat {idx + 1}:</span>
                                        <span style={{ fontSize: 12, color: '#64748b' }}>Setelah jam ke -</span>
                                        <input type="number" value={b.setelah_jam} className="pc-input" min={1} max={14} style={{ width: 60 }}
                                            onChange={(e) => {
                                                const list = [...default_istirahat_list];
                                                list[idx] = { ...list[idx], setelah_jam: parseInt(e.target.value) };
                                                this.setState({ default_istirahat_list: list });
                                            }} />
                                        <span style={{ fontSize: 12, color: '#64748b' }}>durasi</span>
                                        <input type="number" value={b.durasi} className="pc-input" min={5} max={60} style={{ width: 75 }}
                                            onChange={(e) => {
                                                const list = [...default_istirahat_list];
                                                list[idx] = { ...list[idx], durasi: parseInt(e.target.value) };
                                                this.setState({ default_istirahat_list: list });
                                            }} />
                                        <span style={{ fontSize: 12, color: '#64748b' }}>mnt</span>
                                        {default_istirahat_list.length > 1 && (
                                            <button className="pc-btn pc-btn-xs pc-btn-danger" style={{ padding: '2px 6px' }}
                                                onClick={() => {
                                                    const list = default_istirahat_list.filter((_, i) => i !== idx);
                                                    this.setState({ default_istirahat_list: list });
                                                }}><i className="bx bx-x"></i></button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    {/* Generate CTA Card */}
                    <div className="pc-generate-cta">
                        <div className="pc-generate-cta-content">
                            <div className="pc-generate-cta-icon"><i className="bx bx-magic-wand"></i></div>
                            <div className="pc-generate-cta-text">
                                <h4>Generate Waktu Otomatis</h4>
                                <p>Atur konfigurasi di atas, lalu klik tombol ini untuk membuat slot waktu pelajaran beserta jam istirahat secara otomatis.</p>
                            </div>
                        </div>
                        <button onClick={() => this.generateDefaultWaktu()} className="pc-btn pc-btn-primary pc-btn-lg pc-btn-glow">
                            <i className="bx bx-bolt-circle"></i> Generate Sekarang
                        </button>
                    </div>
                </div>

                {/* Day Tabs */}
                {waktu_list.length > 0 && (
                    <>
                        <div className="pc-tabs" style={{ marginTop: 8 }}>
                            {activeHariOptions.map((h) => {
                                const count = waktu_list.filter(w => w.hari === h.value).length;
                                return (
                                    <button
                                        key={h.value}
                                        className={`pc-tab ${waktu_tab_hari === h.value ? "pc-tab-active" : ""}`}
                                        onClick={() => this.setState({ waktu_tab_hari: h.value, adding_entry: false, show_apply_modal: false })}
                                    >
                                        {h.label} <span className="pc-tab-count">{count}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Day Content */}
                        <div className="pc-day-card">
                            <div className="pc-day-card-header">
                                <h4><i className="bx bx-calendar"></i> {this.renderHariLabel(waktu_tab_hari)}</h4>
                                <div className="pc-day-card-actions">
                                    <span className="pc-day-summary">{dayEntries.filter(w => w.tipe === "pelajaran" || (!w.is_istirahat && !w.tipe)).length} jam pelajaran, {dayEntries.filter(w => w.tipe === 'istirahat').length} istirahat</span>
                                    {otherDays.length > 0 && dayEntries.length > 0 && (
                                        <button onClick={() => this.setState({ show_apply_modal: !show_apply_modal, apply_target_days: [] })} className="pc-btn pc-btn-sm pc-btn-outline" title="Terapkan konfigurasi hari ini ke hari lain">
                                            <i className="bx bx-copy-alt"></i> Terapkan ke Hari Lain
                                        </button>
                                    )}
                                    <button onClick={() => this.setState({ adding_entry: !adding_entry })} className="pc-btn pc-btn-sm pc-btn-secondary">
                                        <i className={`bx ${adding_entry ? "bx-x" : "bx-plus"}`}></i> {adding_entry ? "Batal" : "Tambah"}
                                    </button>
                                </div>
                            </div>

                            {/* Apply to Other Days Modal */}
                            {show_apply_modal && (
                                <div className="pc-apply-modal">
                                    <div className="pc-apply-modal-content">
                                        <p className="pc-apply-modal-title"><i className="bx bx-copy-alt"></i> Terapkan konfigurasi <strong>{this.renderHariLabel(waktu_tab_hari)}</strong> ke:</p>
                                        <div className="pc-day-toggles pc-day-toggles-sm">
                                            {otherDays.map((h) => (
                                                <button
                                                    key={h.value}
                                                    type="button"
                                                    className={`pc-day-toggle ${apply_target_days.includes(h.value) ? "pc-day-toggle-active" : ""}`}
                                                    onClick={() => {
                                                        const newTargets = apply_target_days.includes(h.value)
                                                            ? apply_target_days.filter(x => x !== h.value)
                                                            : [...apply_target_days, h.value];
                                                        this.setState({ apply_target_days: newTargets });
                                                    }}
                                                >
                                                    {h.label}
                                                    {apply_target_days.includes(h.value) && <i className="bx bx-check"></i>}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="pc-apply-modal-actions">
                                            <button
                                                onClick={() => {
                                                    const all = otherDays.map(h => h.value);
                                                    this.setState({ apply_target_days: apply_target_days.length === all.length ? [] : all });
                                                }}
                                                className="pc-btn pc-btn-xs pc-btn-ghost"
                                            >
                                                {apply_target_days.length === otherDays.length ? "Batal Semua" : "Pilih Semua"}
                                            </button>
                                            <button onClick={() => this.applyDayToOtherDays()} disabled={apply_target_days.length === 0} className="pc-btn pc-btn-sm pc-btn-primary">
                                                <i className="bx bx-check"></i> Terapkan ({apply_target_days.length} hari)
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Add Entry Form */}
                            {adding_entry && (
                                <div className="pc-add-entry-form">
                                    <div className="pc-form-grid pc-form-grid-inline">
                                        <div className="pc-form-group">
                                            <label>Tipe</label>
                                            <select className="pc-input" value={new_entry.tipe}
                                                onChange={(e) => {
                                                    const tipe = e.target.value;
                                                    const defaults = {
                                                        istirahat: { durasi: 15, keterangan: "Istirahat" },
                                                        upacara: { durasi: 45, keterangan: "Upacara" },
                                                        kegiatan: { durasi: 30, keterangan: "" },
                                                        pelajaran: { durasi: default_durasi, keterangan: "" },
                                                    };
                                                    this.setState({ new_entry: { tipe, ...defaults[tipe] } });
                                                }}>
                                                <option value="istirahat">☕ Istirahat</option>
                                                <option value="upacara">🚩 Upacara</option>
                                                <option value="kegiatan">📋 Kegiatan Lain</option>
                                                <option value="pelajaran">📖 Jam Pelajaran</option>
                                            </select>
                                        </div>
                                        <div className="pc-form-group">
                                            <label>Durasi (menit)</label>
                                            <input type="number" className="pc-input" min={5} max={120}
                                                value={new_entry.durasi}
                                                onChange={(e) => this.setState({ new_entry: { ...new_entry, durasi: parseInt(e.target.value) } })} />
                                        </div>
                                        {(new_entry.tipe === "kegiatan" || new_entry.tipe === "upacara") && (
                                            <div className="pc-form-group">
                                                <label>Keterangan</label>
                                                <input type="text" className="pc-input" placeholder="Mis: Senam Pagi, Doa Bersama..."
                                                    value={new_entry.keterangan}
                                                    onChange={(e) => this.setState({ new_entry: { ...new_entry, keterangan: e.target.value } })} />
                                            </div>
                                        )}
                                    </div>
                                    <button onClick={() => this.addEntryToDay(waktu_tab_hari)} className="pc-btn pc-btn-sm pc-btn-primary">
                                        <i className="bx bx-plus"></i> Tambahkan ke {this.renderHariLabel(waktu_tab_hari)}
                                    </button>
                                </div>
                            )}

                            {/* Entries List */}
                            {dayEntries.length > 0 ? (
                                <div className="pc-entries-list">
                                    {dayEntries.map((w, idx) => {
                                        const tipe = w.tipe || (w.is_istirahat ? "istirahat" : "pelajaran");
                                        const isPelajaran = tipe === "pelajaran";
                                        return (
                                            <div key={idx} className={`pc-entry-row ${tipe === "istirahat" ? "pc-entry-istirahat" : ""} ${tipe === "upacara" ? "pc-entry-upacara" : ""} ${tipe === "kegiatan" ? "pc-entry-kegiatan" : ""}`}>
                                                <div className="pc-entry-order">
                                                    <button onClick={() => this.moveEntry(waktu_tab_hari, idx, -1)} disabled={idx === 0} className="pc-btn-icon" title="Geser ke atas">
                                                        <i className="bx bx-chevron-up"></i>
                                                    </button>
                                                    <button onClick={() => this.moveEntry(waktu_tab_hari, idx, 1)} disabled={idx === dayEntries.length - 1} className="pc-btn-icon" title="Geser ke bawah">
                                                        <i className="bx bx-chevron-down"></i>
                                                    </button>
                                                </div>
                                                <div className="pc-entry-num">
                                                    {isPelajaran ? <span className="pc-jam-num">{w.jam_ke}</span> : <i className={`bx ${tipe === "istirahat" ? "bx-coffee" : tipe === "upacara" ? "bx-flag" : "bx-calendar-event"}`}></i>}
                                                </div>
                                                <div className="pc-entry-time">
                                                    <input type="time" className="pc-input pc-input-sm" value={w.waktu_mulai}
                                                        onChange={(e) => this.updateWaktuField(waktu_tab_hari, idx, "waktu_mulai", e.target.value)} />
                                                    <span className="pc-time-sep">-</span>
                                                    <input type="time" className="pc-input pc-input-sm" value={w.waktu_selesai}
                                                        onChange={(e) => this.updateWaktuField(waktu_tab_hari, idx, "waktu_selesai", e.target.value)} />
                                                </div>
                                                <div className="pc-entry-tipe-select">
                                                    <select className="pc-input pc-input-sm pc-tipe-dropdown" value={tipe}
                                                        onChange={(e) => this.changeEntryTipe(waktu_tab_hari, idx, e.target.value)}>
                                                        <option value="pelajaran">📖 Pelajaran</option>
                                                        <option value="istirahat">☕ Istirahat</option>
                                                        <option value="upacara">🚩 Upacara</option>
                                                        <option value="kegiatan">📋 Kegiatan</option>
                                                    </select>
                                                </div>
                                                {tipe === "istirahat" && w.keterangan && (
                                                    <div className="pc-entry-desc-input">
                                                        <span className="pc-istirahat-badge">{w.keterangan}</span>
                                                    </div>
                                                )}
                                                {(tipe === "kegiatan" || tipe === "upacara") && (
                                                    <div className="pc-entry-desc-input">
                                                        <input type="text" className="pc-input pc-input-sm" placeholder="Keterangan..."
                                                            value={w.keterangan || ""}
                                                            onChange={(e) => this.updateWaktuField(waktu_tab_hari, idx, "keterangan", e.target.value)} />
                                                    </div>
                                                )}
                                                <div className="pc-entry-actions">
                                                    <button onClick={() => this.removeEntryFromDay(waktu_tab_hari, idx)} className="pc-btn-icon pc-btn-icon-danger" title="Hapus">
                                                        <i className="bx bx-trash"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="pc-empty-state" style={{ padding: "24px" }}>
                                    <i className="bx bx-plus-circle"></i>
                                    <p>Belum ada entri untuk hari ini</p>
                                    <small>Gunakan "Generate Waktu" atau tambahkan manual</small>
                                </div>
                            )}
                        </div>
                    </>
                )
                }

                {/* Total Jam Per Minggu Summary */}
                {
                    waktu_list.length > 0 && (() => {
                        const jamPerHari = {};
                        activeHariOptions.forEach(h => {
                            jamPerHari[h.value] = waktu_list.filter(w => w.hari === h.value && (w.tipe === 'pelajaran' || (!w.is_istirahat && !w.tipe))).length;
                        });
                        const totalJamPerMinggu = Object.values(jamPerHari).reduce((a, b) => a + b, 0);
                        return (
                            <div className="pc-total-jam-card" style={{ marginTop: 16, background: 'linear-gradient(135deg, #1e293b, #334155)', borderRadius: 12, padding: '16px 20px', color: '#fff' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: 14, color: '#94a3b8', fontWeight: 500 }}>
                                            <i className="bx bx-bar-chart-alt-2" style={{ marginRight: 6 }}></i> Total Jam Per Minggu
                                        </h4>
                                        <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                                            {activeHariOptions.map(h => (
                                                <div key={h.value} style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{h.short}</div>
                                                    <div style={{ fontSize: 18, fontWeight: 700, color: '#38bdf8' }}>{jamPerHari[h.value] || 0}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>Total</div>
                                        <div style={{ fontSize: 32, fontWeight: 800, color: '#22d3ee' }}>{totalJamPerMinggu}</div>
                                        <div style={{ fontSize: 11, color: '#94a3b8' }}>jam/minggu</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()
                }
                <div className="pc-wizard-actions">
                    <button onClick={() => this.saveWaktu()} disabled={saving || waktu_list.length === 0} className="pc-btn pc-btn-primary">
                        {saving ? <><i className="bx bx-loader-alt bx-spin"></i> Menyimpan...</> : <><i className="bx bx-save"></i> {waktu_mode === 'per_tingkat' && waktu_active_tingkat ? `Simpan Waktu Kelas ${waktu_active_tingkat}` : 'Simpan Waktu'}</>}
                    </button>
                    {waktu_list.length === 0 ? (
                        <span className="pc-save-status pc-save-status-unsaved"><i className="bx bx-info-circle"></i> Belum ada data waktu</span>
                    ) : this.state.waktu_saved ? (
                        <span className="pc-save-status pc-save-status-saved"><i className="bx bx-check-circle"></i> Tersimpan</span>
                    ) : (
                        <span className="pc-save-status pc-save-status-unsaved"><i className="bx bx-error-circle"></i> Ada perubahan belum disimpan</span>
                    )}
                </div>
            </div >
        );
    }

    renderStep2() {
        const { penugasan_list, rombel_options, guru_options, mapel_options, new_penugasan, saving, importing_penugasan, show_manual_form, expanded_rombels, penugasan_filter_kelas, penugasan_search, selected_kelas, waktu_list, hari_aktif } = this.state;

        // Group penugasan by rombel_id
        const grouped = {};
        penugasan_list.forEach((p, idx) => {
            const key = p.rombel_id || `unknown_${idx}`;
            if (!grouped[key]) {
                grouped[key] = { rombel_id: p.rombel_id, rombel_nama: p.rombel_nama, kelas: p.kelas, items: [] };
            }
            grouped[key].items.push({ ...p, _idx: idx });
        });

        // Inject empty rombels from rombel_options that have no penugasan yet
        rombel_options.forEach(r => {
            if (!grouped[r.id] && (selected_kelas.length === 0 || selected_kelas.includes(r.kelas))) {
                grouped[r.id] = { rombel_id: r.id, rombel_nama: r.nama_rombel, kelas: r.kelas, items: [] };
            }
        });

        // Filter grouped rombels
        let rombelGroups = Object.values(grouped).sort((a, b) => {
            if (a.kelas !== b.kelas) return sortKelas(a.kelas || '', b.kelas || '');
            return sortRombel(a.rombel_nama || '', b.rombel_nama || '');
        });
        if (penugasan_filter_kelas) {
            rombelGroups = rombelGroups.filter(g => g.kelas === penugasan_filter_kelas);
        }
        if (penugasan_search) {
            const q = penugasan_search.toLowerCase();
            rombelGroups = rombelGroups.filter(g =>
                g.rombel_nama.toLowerCase().includes(q) ||
                g.items.some(i => i.mapel_nama?.toLowerCase().includes(q) || i.guru_nama?.toLowerCase().includes(q))
            );
        }

        // Calculate totals from merged data (deduplicate same-mapel, agama = 1 jam)
        let totalMapelUnique = 0;
        let totalJam = 0;
        Object.values(grouped).forEach(g => {
            const seen = {};
            let agamaJam = 0;
            let hasAgama = false;
            g.items.forEach(i => {
                const key = i.mapel_nama || i.mapel_id;
                if (i.kategori_mapel === 'agama') {
                    if (!hasAgama) {
                        hasAgama = true;
                        agamaJam = i.jumlah_jam_per_minggu || 1;
                        totalMapelUnique++;
                        totalJam += agamaJam;
                    }
                } else if (!seen[key]) {
                    seen[key] = true;
                    totalMapelUnique++;
                    totalJam += (i.jumlah_jam_per_minggu || 0);
                }
            });
        });
        const totalPenugasan = totalMapelUnique;
        const totalRombel = Object.keys(grouped).length;

        // Kelas color map for left-border accent
        const kelasColors = {
            "I": "#ef4444", "II": "#f97316", "III": "#eab308", "IV": "#22c55e", "V": "#06b6d4", "VI": "#8b5cf6",
            "VII": "#ef4444", "VIII": "#f97316", "IX": "#eab308",
            "X": "#3b82f6", "XI": "#8b5cf6", "XII": "#ec4899", "XIII": "#14b8a6"
        };

        return (
            <div className="pc-wizard-content pc-fade-in">
                <h3 className="pc-wizard-step-title"><i className="bx bx-book-open"></i> Penugasan Mapel & Guru</h3>
                <p className="pc-wizard-step-desc">Import penugasan dari data Penugasan Guru, atau tambah manual. Data dikelompokkan per rombel.</p>

                {/* Auto-loaded info */}
                {importing_penugasan && (
                    <div className="pc-import-card">
                        <div className="pc-import-card-body">
                            <div className="pc-import-info">
                                <div className="pc-import-icon"><i className="bx bx-loader-alt bx-spin"></i></div>
                                <div>
                                    <h4>Memuat Penugasan Guru...</h4>
                                    <small>Data diambil otomatis dari Penugasan Guru.</small>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Stats Summary */}
                {totalPenugasan > 0 && (
                    <div className="pc-stats-bar">
                        <div className="pc-stat-item">
                            <i className="bx bx-buildings"></i>
                            <span className="pc-stat-num">{totalRombel}</span>
                            <span className="pc-stat-label">Rombel</span>
                        </div>
                        <div className="pc-stat-item">
                            <i className="bx bx-list-check"></i>
                            <span className="pc-stat-num">{totalPenugasan}</span>
                            <span className="pc-stat-label">Penugasan</span>
                        </div>
                        <div className="pc-stat-item">
                            <i className="bx bx-time"></i>
                            <span className="pc-stat-num">{totalJam}</span>
                            <span className="pc-stat-label">Jam/Minggu</span>
                        </div>
                    </div>
                )}

                {/* Reset & Re-import Button */}
                {totalPenugasan > 0 && (
                    <div style={{ textAlign: 'right', marginBottom: '8px' }}>
                        <button className="pc-btn pc-btn-sm pc-btn-outline"
                            onClick={() => this.resetAndReimport()}
                            disabled={importing_penugasan}
                            style={{ fontSize: '0.75rem', padding: '4px 12px' }}>
                            <i className="bx bx-refresh"></i> Reset & Import Ulang
                        </button>
                    </div>
                )}

                {/* Filter & Search */}
                {rombelGroups.length > 0 && (
                    <div className="pc-filter-bar">
                        <select className="pc-input pc-input-sm" value={penugasan_filter_kelas}
                            onChange={(e) => this.setState({ penugasan_filter_kelas: e.target.value })}>
                            <option value="">Semua Kelas</option>
                            {selected_kelas.map(k => <option key={k} value={k}>Kelas {k}</option>)}
                        </select>
                        <div className="pc-search-box">
                            <i className="bx bx-search"></i>
                            <input type="text" className="pc-input pc-input-sm" placeholder="Cari mapel atau guru..."
                                value={penugasan_search} onChange={(e) => this.setState({ penugasan_search: e.target.value })} />
                        </div>
                        <button className="pc-btn pc-btn-xs pc-btn-ghost" onClick={() => {
                            const allExpanded = {};
                            rombelGroups.forEach(g => { allExpanded[g.rombel_id] = true; });
                            this.setState({ expanded_rombels: allExpanded });
                        }}><i className="bx bx-expand"></i> Buka Semua</button>
                        <button className="pc-btn pc-btn-xs pc-btn-ghost" onClick={() => this.setState({ expanded_rombels: {} })}>
                            <i className="bx bx-collapse"></i> Tutup Semua
                        </button>
                    </div>
                )}

                {/* Rombel Accordion Cards */}
                <div className="pc-rombel-list">
                    {rombelGroups.map((group) => {
                        const isExpanded = expanded_rombels[group.rombel_id];
                        // Calculate jam from MERGED mapels (agama shares slots, multi-teacher same mapel shares jam)
                        const mapelFirstJam = {}; // regular mapel_nama => first entry's jam
                        const agamaFirstJam = {}; // agama mapel_nama => first entry's jam
                        let groupJam = 0;
                        let mergedMapelCount = 0;
                        group.items.forEach(i => {
                            const key = i.mapel_nama || i.mapel_id;
                            if (i.kategori_mapel === 'agama') {
                                // Use first entry's jam (= what UI spinner shows)
                                if (agamaFirstJam[key] === undefined) {
                                    agamaFirstJam[key] = i.jumlah_jam_per_minggu || 1;
                                }
                            } else {
                                // Use first entry's jam (= what UI spinner shows)
                                if (mapelFirstJam[key] === undefined) {
                                    mapelFirstJam[key] = i.jumlah_jam_per_minggu || 0;
                                }
                            }
                        });
                        // Agama: MAX across variants (shared slot count)
                        const agamaVariants = Object.values(agamaFirstJam);
                        if (agamaVariants.length > 0) {
                            groupJam += Math.max(...agamaVariants);
                            mergedMapelCount++;
                        }
                        // Regular: sum of first-entry jams
                        Object.entries(mapelFirstJam).forEach(([k, jam]) => {
                            groupJam += jam;
                            mergedMapelCount++;
                        });
                        const borderColor = kelasColors[group.kelas] || "#64748b";

                        // Cross-validate: compare penugasan jam with total jam per minggu from Step 1
                        // Use per-tingkat waktu if available
                        let waktuForKelas = waktu_list;
                        if (this.state.waktu_per_tingkat && this.state.waktu_per_tingkat[group.kelas]) {
                            waktuForKelas = this.state.waktu_per_tingkat[group.kelas];
                        }
                        const activeHari = [...new Set(waktuForKelas.map(w => Number(w.hari)))];
                        const totalJamPerMinggu = activeHari.reduce((sum, h) => sum + waktuForKelas.filter(w => w.hari === h && (w.tipe === 'pelajaran' || (!w.is_istirahat && !w.tipe))).length, 0);
                        const sisaJam = totalJamPerMinggu - groupJam;
                        const jamMismatch = group.items.length > 0 && totalJamPerMinggu > 0 && groupJam !== totalJamPerMinggu;

                        return (
                            <div key={group.rombel_id} className={`pc-rombel-card ${isExpanded ? "pc-rombel-card-expanded" : ""}`}
                                style={{ borderLeftColor: borderColor }}>
                                <div className="pc-rombel-header" onClick={() => {
                                    this.setState({ expanded_rombels: { ...expanded_rombels, [group.rombel_id]: !isExpanded } });
                                }}>
                                    <div className="pc-rombel-header-left">
                                        <i className={`bx ${isExpanded ? "bx-chevron-down" : "bx-chevron-right"} pc-rombel-chevron`}></i>
                                        <span className="pc-rombel-name">{group.rombel_nama}</span>
                                        <span className="pc-rombel-kelas-badge" style={{ background: borderColor + "20", color: borderColor }}>
                                            Kelas {group.kelas}
                                        </span>
                                    </div>
                                    <div className="pc-rombel-header-right">
                                        {group.items.length > 0 ? (
                                            <>
                                                <span className="pc-rombel-stat">{mergedMapelCount} mapel</span>
                                                <span className={`pc-rombel-stat ${jamMismatch ? 'pc-rombel-stat-warning' : ''}`}
                                                    title={totalJamPerMinggu > 0
                                                        ? `Tersedia ${totalJamPerMinggu} jam/minggu dari pengaturan waktu, terpakai ${groupJam} jam`
                                                        : 'Waktu belum diatur di Step 1'}>
                                                    {jamMismatch && <i className="bx bx-error-circle" style={{ color: groupJam > totalJamPerMinggu ? '#ef4444' : '#f59e0b', marginRight: 2 }}></i>}
                                                    {groupJam} jam/minggu
                                                    {totalJamPerMinggu > 0 ? (
                                                        <small style={{ color: groupJam > totalJamPerMinggu ? '#ef4444' : groupJam === totalJamPerMinggu ? '#10b981' : '#f59e0b', marginLeft: 4 }}>
                                                            {groupJam > totalJamPerMinggu
                                                                ? `(kelebihan ${groupJam - totalJamPerMinggu} jam dari ${totalJamPerMinggu} tersedia)`
                                                                : groupJam === totalJamPerMinggu
                                                                    ? `✓ sesuai kuota ${totalJamPerMinggu} jam`
                                                                    : `(sisa ${sisaJam} jam dari ${totalJamPerMinggu} tersedia)`}
                                                        </small>
                                                    ) : (
                                                        <small style={{ color: '#94a3b8', marginLeft: 4 }}>(waktu belum diatur)</small>
                                                    )}
                                                </span>
                                            </>
                                        ) : (
                                            <span className="pc-rombel-stat" style={{ color: '#94a3b8' }}>
                                                <i className="bx bx-info-circle"></i> {totalJamPerMinggu > 0 ? `Kuota ${totalJamPerMinggu} jam/minggu — belum ada penugasan` : 'Belum ada penugasan'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {isExpanded && (
                                    <div className="pc-rombel-body">
                                        {group.items.length === 0 ? (
                                            <div style={{ padding: '24px 16px', textAlign: 'center', color: '#94a3b8' }}>
                                                <i className="bx bx-info-circle" style={{ fontSize: 24, display: 'block', marginBottom: 8 }}></i>
                                                <p style={{ margin: 0, fontSize: 13 }}>Belum ada penugasan mapel & guru untuk rombel ini.</p>
                                                <p style={{ margin: '4px 0 0', fontSize: 12 }}>Gunakan form di bawah untuk menambahkan penugasan manual, atau pastikan data sudah ada di halaman Penugasan Guru.</p>
                                            </div>
                                        ) : (
                                            <table className="pc-rombel-table">
                                                <thead>
                                                    <tr>
                                                        <th>Mata Pelajaran</th>
                                                        <th>Guru</th>
                                                        <th style={{ width: 80 }}>Jam</th>
                                                        <th style={{ width: 100 }}>Kategori</th>
                                                        <th style={{ width: 50 }}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        // Merge same mapel rows + group ALL agama into single row
                                                        const merged = [];
                                                        const mapelMap = {};
                                                        let agamaRow = null;

                                                        group.items.forEach((p) => {
                                                            if (p.kategori_mapel === 'agama') {
                                                                // Group ALL agama into a single row
                                                                if (!agamaRow) {
                                                                    agamaRow = {
                                                                        ...p,
                                                                        rombel_nama: group.rombel_nama,
                                                                        mapel_nama: 'Pendidikan Agama',
                                                                        mapel_kode: '',
                                                                        kategori_mapel: 'agama',
                                                                        guru_list: [{ guru_id: p.guru_id, guru_nama: p.guru_nama, _idx: p._idx, mapel_detail: p.mapel_nama }],
                                                                        all_agama_idxs: [p._idx],
                                                                    };
                                                                    merged.push(agamaRow);
                                                                } else {
                                                                    // Dedup: same guru + same mapel_detail = skip
                                                                    const isDupe = agamaRow.guru_list.some(g => g.guru_id === p.guru_id && g.mapel_detail === p.mapel_nama);
                                                                    if (!isDupe) {
                                                                        agamaRow.guru_list.push({ guru_id: p.guru_id, guru_nama: p.guru_nama, _idx: p._idx, mapel_detail: p.mapel_nama });
                                                                    }
                                                                    if (!agamaRow.all_agama_idxs.includes(p._idx)) {
                                                                        agamaRow.all_agama_idxs.push(p._idx);
                                                                    }
                                                                }
                                                            } else {
                                                                const mapelKey = p.mapel_nama || p.mapel_id;
                                                                if (mapelMap[mapelKey]) {
                                                                    const existing = mapelMap[mapelKey];
                                                                    if (!existing.guru_list.some(g => g.guru_id === p.guru_id)) {
                                                                        existing.guru_list.push({ guru_id: p.guru_id, guru_nama: p.guru_nama, _idx: p._idx });
                                                                    }
                                                                } else {
                                                                    mapelMap[mapelKey] = {
                                                                        ...p,
                                                                        rombel_nama: group.rombel_nama,
                                                                        guru_list: [{ guru_id: p.guru_id, guru_nama: p.guru_nama, _idx: p._idx }],
                                                                    };
                                                                    merged.push(mapelMap[mapelKey]);
                                                                }
                                                            }
                                                        });

                                                        return merged.map((p) => (
                                                            <tr key={p._idx} className={p.kategori_mapel === 'agama' ? 'pc-agama-row' : ''}>
                                                                <td>
                                                                    <span className="pc-mapel-name">{p.mapel_nama}</span>
                                                                    {p.mapel_kode && <small className="pc-mapel-code">{p.mapel_kode}</small>}
                                                                    {p.kategori_mapel === 'agama' && p.guru_list && (() => {
                                                                        const uniqueMapels = [...new Set(p.guru_list.map(g => g.mapel_detail))];
                                                                        return (
                                                                            <div className="pc-agama-detail">
                                                                                {uniqueMapels.map((name, i) => (
                                                                                    <small key={i} className="pc-agama-sub">{name}</small>
                                                                                ))}
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </td>
                                                                <td className="pc-guru-cell">
                                                                    {p.guru_list.map((g, gi) => (
                                                                        <div key={gi} className="pc-guru-item">
                                                                            <i className="bx bx-user"></i> {g.guru_nama}
                                                                        </div>
                                                                    ))}
                                                                </td>
                                                                <td className="text-center">
                                                                    {p.kategori_mapel === 'agama' ? (
                                                                        <input
                                                                            type="number"
                                                                            min="1"
                                                                            max="20"
                                                                            className="pc-jam-input"
                                                                            value={p.jumlah_jam_per_minggu}
                                                                            onChange={(e) => {
                                                                                const newVal = e.target.value;
                                                                                // Update ALL agama entries in this rombel in one batch
                                                                                this.updatePenugasanJam(p.all_agama_idxs || [], newVal);
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <input
                                                                            type="number"
                                                                            min="1"
                                                                            max="20"
                                                                            className="pc-jam-input"
                                                                            value={p.jumlah_jam_per_minggu}
                                                                            onChange={(e) => {
                                                                                const newVal = e.target.value;
                                                                                // Update all guru entries for this mapel in one batch
                                                                                this.updatePenugasanJam(p.guru_list.map(g => g._idx), newVal);
                                                                            }}
                                                                        />
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <span className={`pc-badge-sm ${p.kategori_mapel === "agama" ? "pc-badge-sm-warning" : p.kategori_mapel === "muatan_lokal" ? "pc-badge-sm-info" : "pc-badge-sm-default"}`}>
                                                                        {p.kategori_mapel}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <button onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        this.showDeleteModal(p);
                                                                    }} className="pc-btn-icon pc-btn-icon-danger" title="Hapus">
                                                                        <i className="bx bx-trash"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ));
                                                    })()}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {penugasan_list.length === 0 && (
                    <div className="pc-empty-state pc-empty-state-lg">
                        <div className="pc-empty-icon"><i className="bx bx-book-open"></i></div>
                        <h4>Belum Ada Penugasan</h4>
                        <p>Data dimuat otomatis dari Penugasan Guru, atau tambah manual di bawah.</p>
                    </div>
                )}

                {/* Manual Add Section (Enhanced) */}
                <div className="pc-manual-add-section">
                    <div className="pc-manual-add-header" onClick={() => this.setState({ show_manual_form: !show_manual_form })}>
                        <div className="pc-manual-add-header-left">
                            <i className={`bx ${show_manual_form ? 'bx-chevron-down' : 'bx-chevron-right'} pc-manual-chevron`}></i>
                            <div className="pc-manual-add-header-icon"><i className="bx bx-edit"></i></div>
                            <div>
                                <span className="pc-manual-add-title">Tambah Penugasan Manual</span>
                                <small className="pc-manual-add-subtitle">Tambah mapel & guru yang belum ada di data Penugasan Guru</small>
                            </div>
                        </div>
                        <span className={`pc-manual-add-badge ${show_manual_form ? 'pc-badge-active' : ''}`}>{show_manual_form ? 'Buka' : 'Klik untuk buka'}</span>
                    </div>
                    {show_manual_form && (
                        <div className="pc-manual-add-body pc-fade-in">
                            <div className="pc-manual-auto-info">
                                <div className="pc-auto-badge"><i className="bx bx-book-alt"></i> {this.state.kurikulum === 'merdeka' ? 'Merdeka' : 'K13'}</div>
                                <div className="pc-auto-badge"><i className="bx bx-calendar"></i> {this.state.tahun_ajaran}</div>
                                <div className="pc-auto-badge"><i className="bx bx-bookmarks"></i> Semester {this.state.semester}</div>
                                <small className="pc-auto-hint">Otomatis dari pengaturan Step 0</small>
                            </div>
                            <div className="pc-manual-flow-hint">
                                <div className="pc-flow-step"><span className="pc-flow-num">1</span> Pilih Guru</div>
                                <i className="bx bx-right-arrow-alt pc-flow-arrow"></i>
                                <div className="pc-flow-step"><span className="pc-flow-num">2</span> Pilih Mapel</div>
                                <i className="bx bx-right-arrow-alt pc-flow-arrow"></i>
                                <div className="pc-flow-step"><span className="pc-flow-num">3</span> Atur Jam</div>
                                <i className="bx bx-right-arrow-alt pc-flow-arrow"></i>
                                <div className="pc-flow-step"><span className="pc-flow-num">4</span> Otomatis ke Rombel</div>
                            </div>
                            <div className="pc-form-grid pc-form-grid-enhanced">
                                <div className="pc-form-group">
                                    <label><i className="bx bx-user" style={{ marginRight: 4 }}></i> Guru <span className="pc-required">*</span></label>
                                    <select className="pc-input pc-input-enhanced" value={new_penugasan.guru_id} onChange={(e) => {
                                        const sel = guru_options.find((g) => g.id == e.target.value);
                                        this.setState({
                                            new_penugasan: { ...new_penugasan, guru_id: e.target.value, guru_nama: sel ? sel.nama : "", guru_nip: sel ? sel.nip : "" }
                                        });
                                    }}>
                                        <option value="">-- Pilih Guru ({guru_options.length} tersedia) --</option>
                                        {guru_options.map((g) => (
                                            <option key={g.id} value={g.id}>{g.nama} {g.nip ? `(${g.nip})` : ""}</option>
                                        ))}
                                    </select>
                                    {new_penugasan.guru_id && <span className="pc-form-check"><i className="bx bx-check-circle"></i></span>}
                                </div>
                                <div className="pc-form-group">
                                    <label><i className="bx bx-book" style={{ marginRight: 4 }}></i> Mata Pelajaran <span className="pc-required">*</span></label>
                                    {(() => {
                                        // Filter by kelas + jkmp from rombel data
                                        const selectedKelas = this.state.selected_kelas || [];
                                        const rombels = this.state.rombel_options || [];
                                        // Collect unique jkmp from selected rombels
                                        const selectedRombels = rombels.filter(r => selectedKelas.includes(r.kelas));
                                        const jkmpSet = new Set();
                                        selectedRombels.forEach(r => {
                                            if (r.jkmp_list && Array.isArray(r.jkmp_list)) {
                                                r.jkmp_list.forEach(j => jkmpSet.add(j));
                                            }
                                        });

                                        const kelasFiltered = this.state.pengampu_options.filter(m => {
                                            // 1. Kelas filter
                                            if (selectedKelas.length > 0 && m.kelas) {
                                                if (!selectedKelas.some(k => this.normKelas(m.kelas) === this.normKelas(k))) {
                                                    return false;
                                                }
                                            }
                                            // 2. JKMP filter - only for SMK-specific categories (Bidang/Program/Kompetensi Keahlian)
                                            // Wajib Nasional, Wajib Lokal, Peminatan etc. always pass through
                                            if (jkmpSet.size > 0 && m.jkmp && m.kmp) {
                                                const kmpLower = (m.kmp || '').toLowerCase();
                                                const isSmkSpecific = kmpLower.includes('bidang') || kmpLower.includes('program') || kmpLower.includes('kompetensi');
                                                if (isSmkSpecific && !jkmpSet.has(m.jkmp)) {
                                                    return false;
                                                }
                                            }
                                            return true;
                                        });
                                        const grouped = this.groupMapelOptions(kelasFiltered);
                                        const isGrouped = grouped.length > 0 && grouped[0]?.options;
                                        const allOpts = isGrouped ? grouped.flatMap(g => g.options) : kelasFiltered;
                                        const searchTerm = (this.state.mapel_search || "").toLowerCase();
                                        const filteredGroups = isGrouped ? grouped.map(g => ({
                                            ...g,
                                            options: g.options.filter(m => {
                                                const label = `${m.mp || m.label || ''} ${m.indeks_mpk || ''}`.toLowerCase();
                                                return label.includes(searchTerm);
                                            })
                                        })).filter(g => g.options.length > 0) : null;
                                        const filteredFlat = !isGrouped ? allOpts.filter(m => {
                                            const label = `${m.mp || m.label || ''} ${m.indeks_mpk || ''}`.toLowerCase();
                                            return label.includes(searchTerm);
                                        }) : null;
                                        return (
                                            <div className="pc-searchable-select" tabIndex="-1" onBlur={(e) => {
                                                // Close dropdown if focus moves outside this container
                                                if (!e.currentTarget.contains(e.relatedTarget)) {
                                                    setTimeout(() => this.setState({ mapel_dropdown_open: false }), 150);
                                                }
                                            }}>
                                                <div className="pc-searchable-input-wrap">
                                                    <i className="bx bx-search pc-searchable-icon"></i>
                                                    <input
                                                        type="text"
                                                        className="pc-input pc-input-enhanced pc-searchable-input"
                                                        placeholder={new_penugasan.mapel_id ? `Terpilih:  ${new_penugasan.mapel_nama}` : `Cari mapel... (${allOpts.length} tersedia)`}
                                                        value={this.state.mapel_search}
                                                        onChange={(e) => this.setState({ mapel_search: e.target.value, mapel_dropdown_open: true })}
                                                        onFocus={() => this.setState({ mapel_dropdown_open: true })}
                                                    />
                                                    {new_penugasan.mapel_id && (
                                                        <button className="pc-searchable-clear" onClick={() => this.setState({
                                                            mapel_search: "", mapel_dropdown_open: false,
                                                            new_penugasan: { ...new_penugasan, mapel_id: "", mapel_nama: "", mapel_kode: "", kelas: "", kategori_mapel: "umum" }
                                                        })}><i className="bx bx-x"></i></button>
                                                    )}
                                                </div>
                                                {this.state.mapel_dropdown_open && (
                                                    <div className="pc-searchable-dropdown">
                                                        {isGrouped ? (filteredGroups || []).map((group, gi) => (
                                                            <div key={gi} className="pc-searchable-group">
                                                                <div className="pc-searchable-group-label">{group.label} ({group.options.length})</div>
                                                                {group.options.map((m) => (
                                                                    <div key={`${m.value}_${this.normKelas(m.kelas)}`} className={`pc-searchable-option ${(new_penugasan.mapel_id == m.value && this.normKelas(new_penugasan.kelas) === this.normKelas(m.kelas)) ? 'pc-searchable-option-active' : ''}`}
                                                                        onClick={() => {
                                                                            this.setState({
                                                                                mapel_search: "", mapel_dropdown_open: false,
                                                                                new_penugasan: {
                                                                                    ...new_penugasan,
                                                                                    mapel_id: m.value,
                                                                                    mapel_nama: m.mp || m.label || "",
                                                                                    mapel_kode: m.indeks_mpk || "",
                                                                                    kelas: m.kelas || "",
                                                                                    jkmp: m.jkmp || "",
                                                                                    kmp: m.kmp || "",
                                                                                    jumlah_jam_per_minggu: m.semester_1 ? parseInt(m.semester_1) : new_penugasan.jumlah_jam_per_minggu,
                                                                                    kategori_mapel: m.kode_pilihan || new_penugasan.kategori_mapel,
                                                                                }
                                                                            });
                                                                        }}>
                                                                        <span className="pc-searchable-option-text">{m.mp || m.label}</span>
                                                                        {m.indeks_mpk && <span className="pc-searchable-option-badge">{m.indeks_mpk}</span>}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )) : (filteredFlat || []).map((m) => (
                                                            <div key={`${m.value}_${this.normKelas(m.kelas)}`} className={`pc-searchable-option ${(new_penugasan.mapel_id == m.value && this.normKelas(new_penugasan.kelas) === this.normKelas(m.kelas)) ? 'pc-searchable-option-active' : ''}`}
                                                                onClick={() => {
                                                                    this.setState({
                                                                        mapel_search: "", mapel_dropdown_open: false,
                                                                        new_penugasan: {
                                                                            ...new_penugasan,
                                                                            mapel_id: m.value,
                                                                            mapel_nama: m.mp || m.label || "",
                                                                            mapel_kode: m.indeks_mpk || "",
                                                                            kelas: m.kelas || "",
                                                                            jkmp: m.jkmp || "",
                                                                            kmp: m.kmp || "",
                                                                            jumlah_jam_per_minggu: m.semester_1 ? parseInt(m.semester_1) : new_penugasan.jumlah_jam_per_minggu,
                                                                            kategori_mapel: m.kode_pilihan || new_penugasan.kategori_mapel,
                                                                        }
                                                                    });
                                                                }}>
                                                                <span className="pc-searchable-option-text">{m.mp || m.label}</span>
                                                                {m.indeks_mpk && <span className="pc-searchable-option-badge">{m.indeks_mpk}</span>}
                                                            </div>
                                                        ))}
                                                        {((isGrouped && filteredGroups.length === 0) || (!isGrouped && filteredFlat.length === 0)) && (
                                                            <div className="pc-searchable-empty">Tidak ditemukan</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                    {new_penugasan.mapel_id && <span className="pc-form-check"><i className="bx bx-check-circle"></i></span>}
                                    {new_penugasan.kelas && <small className="pc-mapel-kelas-hint"><i className="bx bx-info-circle"></i> Kelas {new_penugasan.kelas} - akan otomatis ditambahkan ke semua rombel kelas ini</small>}
                                </div>
                                <div className="pc-form-row-inline">
                                    <div className="pc-form-group pc-form-group-sm">
                                        <label><i className="bx bx-time" style={{ marginRight: 4 }}></i> Jam/Minggu</label>
                                        <input type="number" className="pc-input pc-input-enhanced" min={1} max={12} value={new_penugasan.jumlah_jam_per_minggu}
                                            onChange={(e) => this.setState({ new_penugasan: { ...new_penugasan, jumlah_jam_per_minggu: parseInt(e.target.value) || 1 } })} />
                                    </div>
                                    <div className="pc-form-group pc-form-group-sm">
                                        <label><i className="bx bx-category" style={{ marginRight: 4 }}></i> Kategori</label>
                                        <select className="pc-input pc-input-enhanced pc-input-disabled" value={new_penugasan.kategori_mapel}
                                            disabled>
                                            <option value="umum">Umum</option>
                                            <option value="agama">Agama</option>
                                            <option value="muatan_lokal">Muatan Lokal</option>
                                            <option value="peminatan">Peminatan</option>
                                            <option value="kejuruan">Kejuruan</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="pc-manual-add-actions">
                                <button
                                    onClick={() => this.addPenugasan()}
                                    disabled={!new_penugasan.mapel_id || !new_penugasan.guru_id}
                                    className="pc-btn pc-btn-primary pc-btn-glow"
                                >
                                    <i className="bx bx-plus-circle"></i> Tambah Penugasan
                                </button>
                                <button onClick={() => this.setState({ show_manual_form: false })} className="pc-btn pc-btn-ghost">
                                    <i className="bx bx-x"></i> Tutup
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="pc-wizard-actions">
                    <button onClick={() => this.savePenugasan()} disabled={saving} className="pc-btn pc-btn-primary">
                        {saving ? <><i className="bx bx-loader-alt bx-spin"></i> Menyimpan...</> : <><i className="bx bx-save"></i> Simpan Penugasan</>}
                    </button>
                    {this.state.penugasan_saved ? (
                        <span className="pc-save-status pc-save-status-saved"><i className="bx bx-check-circle"></i> Tersimpan</span>
                    ) : (
                        <span className="pc-save-status pc-save-status-unsaved"><i className="bx bx-error-circle"></i> Ada perubahan belum disimpan</span>
                    )}
                </div>
            </div>
        );
    }

    renderStep3() {
        const { constraint_list, constraint_guru, constraint_tingkat, penugasan_list, waktu_list, hari_aktif, saving, selected_kelas, waktu_per_tingkat } = this.state;

        // Per-tingkat tabs setup
        const tingkatOptions3 = [...new Set(selected_kelas)].sort(sortKelas);

        // Check if all tingkat have the same waktu configuration
        const hasTingkatData = waktu_per_tingkat && Object.keys(waktu_per_tingkat).length > 1;
        let waktuIsSameForAll = true;
        if (hasTingkatData && tingkatOptions3.length > 1) {
            // Serialize each tingkat's waktu for comparison (ignore tingkat field)
            const serialized = tingkatOptions3.map(t => {
                const entries = (waktu_per_tingkat[t] || []).map(w => `${w.hari}_${w.jam_ke || ''}_${w.waktu_mulai || ''}_${w.waktu_selesai || ''}_${w.tipe || ''}`).sort().join('|');
                return entries;
            });
            waktuIsSameForAll = serialized.every(s => s === serialized[0]);
        }

        const showTingkatTabs3 = hasTingkatData && !waktuIsSameForAll;
        const activeTingkat3 = showTingkatTabs3 ? (constraint_tingkat || tingkatOptions3[0] || '') : '';

        // Use the selected tingkat's waktu or merged data
        let gridWaktu = waktu_list;
        let gridHari = hari_aktif;
        if (hasTingkatData) {
            if (activeTingkat3 && waktu_per_tingkat[activeTingkat3]) {
                // Specific tingkat selected — show only that tingkat's waktu
                gridWaktu = waktu_per_tingkat[activeTingkat3];
                const hariSet = new Set();
                gridWaktu.forEach(w => hariSet.add(Number(w.hari)));
                gridHari = [...hariSet].sort((a, b) => a - b);
            } else {
                // Same waktu for all — use first tingkat's data (or merge)
                const firstTingkat = tingkatOptions3[0];
                if (firstTingkat && waktu_per_tingkat[firstTingkat]) {
                    gridWaktu = waktu_per_tingkat[firstTingkat];
                    const hariSet = new Set();
                    gridWaktu.forEach(w => hariSet.add(Number(w.hari)));
                    gridHari = [...hariSet].sort((a, b) => a - b);
                }
            }
        }

        // Extract unique gurus from penugasan, filtered by tingkat if tabs are shown
        const guruMap = {};
        penugasan_list.forEach(p => {
            if (p.guru_id && p.guru_nama && !guruMap[p.guru_id]) {
                if (showTingkatTabs3 && activeTingkat3) {
                    if (String(p.kelas) === String(activeTingkat3)) {
                        guruMap[p.guru_id] = { id: p.guru_id, nama: p.guru_nama };
                    }
                } else {
                    guruMap[p.guru_id] = { id: p.guru_id, nama: p.guru_nama };
                }
            }
        });
        if (showTingkatTabs3 && activeTingkat3) {
            penugasan_list.forEach(p => {
                if (p.guru_id && p.guru_nama && String(p.kelas) === String(activeTingkat3) && !guruMap[p.guru_id]) {
                    guruMap[p.guru_id] = { id: p.guru_id, nama: p.guru_nama };
                }
            });
        }
        const guruList = Object.values(guruMap).sort((a, b) => a.nama.localeCompare(b.nama));

        // Build per-hari sorted entries (each day has its own sequence)
        const hariEntries = {};
        gridHari.forEach(h => {
            const hNum = Number(h);
            const seen = {};
            hariEntries[hNum] = gridWaktu
                .filter(w => {
                    if (Number(w.hari) !== hNum) return false;
                    const key = `${w.jam_ke || ''}_${w.waktu_mulai || ''}_${w.tipe || ''}_${w.urutan || ''}`;
                    if (seen[key]) return false;
                    seen[key] = true;
                    return true;
                })
                .sort((a, b) => {
                    if (a.urutan !== undefined && b.urutan !== undefined) return a.urutan - b.urutan;
                    const jamA = Number(a.jam_ke) || 0;
                    const jamB = Number(b.jam_ke) || 0;
                    if (jamA !== jamB) return jamA - jamB;
                    if (a.waktu_mulai && b.waktu_mulai) return a.waktu_mulai.localeCompare(b.waktu_mulai);
                    return 0;
                });
        });

        // Find max number of entries across all days (for row count)
        const maxEntries = Math.max(...gridHari.map(h => (hariEntries[Number(h)] || []).length), 0);

        const selectedGuru = guruList.find(g => String(g.id) === String(constraint_guru));
        // Helper: match a constraint to a guru (check guru_id, referensi_id, and referensi_nama)
        const matchConstraintGuru = (c, g) => {
            const gid = String(g.id);
            return (c.guru_id && String(c.guru_id) === gid) || (c.referensi_id && String(c.referensi_id) === gid) || (c.referensi_nama && c.referensi_nama === g.nama);
        };
        const constraintCountForGuru = selectedGuru ? constraint_list.filter(c => c.tipe === 'guru_tidak_hadir' && matchConstraintGuru(c, selectedGuru)).length : 0;
        const gurusWithConstraints = new Set();
        const allGthConstraints = constraint_list.filter(c => c.tipe === 'guru_tidak_hadir');
        let matchedSlotCount = 0;
        let orphanedSlotCount = 0;
        allGthConstraints.forEach(c => {
            const matchedGuru = guruList.find(g => matchConstraintGuru(c, g));
            if (matchedGuru) {
                gurusWithConstraints.add(String(matchedGuru.id));
                matchedSlotCount++;
            } else {
                orphanedSlotCount++;
            }
        });
        // Compute guru total loads from penugasan (unique rombel-mapel per guru)
        const guruTotalJam = {};
        const seenPenugasan = new Set();
        penugasan_list.forEach(p => {
            if (p.guru_nama) {
                // Deduplicate by guru+rombel+mapel to avoid over-counting
                const key = `${p.guru_nama}|||${p.rombel_nama}|||${p.mapel_nama}`;
                if (!seenPenugasan.has(key)) {
                    seenPenugasan.add(key);
                    guruTotalJam[p.guru_nama] = (guruTotalJam[p.guru_nama] || 0) + (p.jumlah_jam_per_minggu || 0);
                }
            }
        });

        const { max_jam_enabled, max_jam_default, max_jam_custom, max_jam_panel_open } = this.state;

        return (
            <div className="pc-wizard-content pc-fade-in">
                <h3 className="pc-wizard-step-title"><i className="bx bx-user-check"></i> Ketersediaan Guru</h3>
                <p className="pc-wizard-step-desc">
                    Atur hari dan jam dimana guru <strong>tidak bisa mengajar</strong>. Default: semua guru tersedia di semua waktu.
                    Pilih guru, lalu klik cell jam pelajaran untuk menandai tidak hadir.
                </p>

                {/* Max Jam Per Minggu Card */}
                <div className="pc-maxjam-card" style={{ marginBottom: 16 }}>
                    <div className="pc-maxjam-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                            <i className="bx bx-time-five" style={{ fontSize: 20, color: '#6366f1' }}></i>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Maks Jam Mengajar Per Minggu</div>
                                <div style={{ fontSize: 12, color: '#64748b' }}>Batasi jumlah jam mengajar setiap guru per minggu</div>
                            </div>
                        </div>
                        <label className="pc-toggle-switch">
                            <input
                                type="checkbox"
                                checked={max_jam_enabled}
                                onChange={(e) => this.setState({ max_jam_enabled: e.target.checked, constraint_saved: false })}
                            />
                            <span className="pc-toggle-slider"></span>
                        </label>
                    </div>
                    {max_jam_enabled && (
                        <div className="pc-maxjam-body">
                            <div className="pc-maxjam-default-row">
                                <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                                    <i className="bx bx-group" style={{ marginRight: 4 }}></i> Default semua guru:
                                </label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input
                                        type="number"
                                        min={1}
                                        max={50}
                                        value={max_jam_default}
                                        onChange={(e) => this.setState({ max_jam_default: parseInt(e.target.value) || 1, constraint_saved: false })}
                                        className="pc-maxjam-input"
                                    />
                                    <span style={{ fontSize: 12, color: '#64748b' }}>jam/minggu</span>
                                </div>
                            </div>

                            <div className="pc-maxjam-toggle-detail" onClick={() => this.setState({ max_jam_panel_open: !max_jam_panel_open })}>
                                <i className={`bx ${max_jam_panel_open ? 'bx-chevron-up' : 'bx-chevron-down'}`}></i>
                                {max_jam_panel_open ? 'Sembunyikan' : 'Atur Per Guru'} ({Object.keys(max_jam_custom).length} guru di-custom)
                            </div>

                            {max_jam_panel_open && (
                                <div className="pc-maxjam-guru-table">
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                                        <button
                                            className="pc-btn pc-btn-outline"
                                            style={{ fontSize: 11, padding: '4px 10px' }}
                                            onClick={() => this.setState({ max_jam_custom: {}, constraint_saved: false })}
                                        >
                                            <i className="bx bx-reset"></i> Samakan Semua ke Default
                                        </button>
                                    </div>
                                    <table className="pc-table-mini">
                                        <thead>
                                            <tr>
                                                <th style={{ textAlign: 'left' }}>Guru</th>
                                                <th style={{ width: 90, textAlign: 'center' }}>Penugasan</th>
                                                <th style={{ width: 100, textAlign: 'center' }}>Maks Jam</th>
                                                <th style={{ width: 70, textAlign: 'center' }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {guruList.map(g => {
                                                const totalJam = guruTotalJam[g.nama] || 0;
                                                const customVal = max_jam_custom[g.nama];
                                                const effectiveMax = customVal !== undefined ? customVal : max_jam_default;
                                                const isOver = totalJam > effectiveMax;
                                                const isCustom = customVal !== undefined;
                                                return (
                                                    <tr key={g.id} style={{ background: isCustom ? '#f0f4ff' : undefined }}>
                                                        <td style={{ fontSize: 12, fontWeight: isCustom ? 600 : 400 }}>{g.nama}</td>
                                                        <td style={{ textAlign: 'center', fontSize: 12 }}>{totalJam} jam</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="number"
                                                                min={1}
                                                                max={50}
                                                                value={effectiveMax}
                                                                onChange={(e) => {
                                                                    const val = parseInt(e.target.value) || 1;
                                                                    const custom = { ...this.state.max_jam_custom };
                                                                    if (val === max_jam_default) {
                                                                        delete custom[g.nama];
                                                                    } else {
                                                                        custom[g.nama] = val;
                                                                    }
                                                                    this.setState({ max_jam_custom: custom, constraint_saved: false });
                                                                }}
                                                                className="pc-maxjam-input-sm"
                                                                style={{ fontWeight: isCustom ? 700 : 400, color: isCustom ? '#4f46e5' : '#334155' }}
                                                            />
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            {isOver ? (
                                                                <span className="pc-badge pc-badge-red">Over</span>
                                                            ) : (
                                                                <span className="pc-badge pc-badge-green">OK</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Per-Tingkat Tabs — only show when waktu differs between tingkat */}
                {showTingkatTabs3 && (
                    <div style={{ marginBottom: 16, background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                            <span style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                                <i className="bx bx-layer" style={{ marginRight: 4 }}></i> Ketersediaan Per Tingkat:
                            </span>
                            <small style={{ color: '#94a3b8' }}>Waktu berbeda per tingkat — pilih tingkat untuk melihat guru & waktu yang sesuai</small>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {tingkatOptions3.map(t => {
                                const guruCountForTingkat = new Set(penugasan_list.filter(p => String(p.kelas) === String(t) && p.guru_id).map(p => p.guru_id)).size;
                                return (
                                    <button
                                        key={t}
                                        className={`pc-kur-btn ${activeTingkat3 === t ? 'pc-kur-active' : ''}`}
                                        onClick={() => this.setState({ constraint_tingkat: t, constraint_guru: '' })}
                                        style={{ padding: '6px 14px', fontSize: 12 }}
                                    >
                                        Kelas {t}
                                        <span style={{ marginLeft: 4, fontSize: 10, color: activeTingkat3 === t ? '#bbf7d0' : '#64748b' }}>
                                            ({guruCountForTingkat} guru)
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="pc-stats-bar" style={{ marginBottom: 16, cursor: 'pointer' }}
                    onClick={() => this.setState({ guru_detail_modal: true })}
                    title="Klik untuk lihat detail ketersediaan semua guru">
                    <div className="pc-stat-item"><i className="bx bx-user"></i> {guruList.length} Guru</div>
                    <div className="pc-stat-item" style={{ color: gurusWithConstraints.size > 0 ? '#ef4444' : undefined, fontWeight: gurusWithConstraints.size > 0 ? 600 : undefined }}>
                        <i className="bx bx-error-circle"></i> {gurusWithConstraints.size} guru ada jadwal tidak hadir
                        <i className="bx bx-chevron-right" style={{ marginLeft: 4, fontSize: 14 }}></i>
                    </div>
                    <div className="pc-stat-item"><i className="bx bx-block"></i> {matchedSlotCount} slot tidak hadir</div>
                </div>

                {orphanedSlotCount > 0 && (
                    <div className="pc-alert pc-alert-warning" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>
                            <i className="bx bx-info-circle"></i> Ada {orphanedSlotCount} data ketersediaan lama yang merujuk guru tidak aktif.
                        </span>
                        <button className="pc-btn pc-btn-ghost pc-btn-sm" style={{ marginLeft: 12, whiteSpace: 'nowrap' }}
                            onClick={() => {
                                const cleaned = constraint_list.filter(c => {
                                    if (c.tipe !== 'guru_tidak_hadir') return true;
                                    return guruList.some(g => matchConstraintGuru(c, g));
                                });
                                this.setState({ constraint_list: cleaned, constraint_saved: false });
                                this.showMessage(`${orphanedSlotCount} data lama dihapus. Klik Simpan untuk menyimpan.`);
                            }}>
                            <i className="bx bx-trash"></i> Bersihkan
                        </button>
                    </div>
                )}

                {/* Guru Detail Modal */}
                {this.state.guru_detail_modal && (() => {
                    // Build per-guru constraint summary
                    const guruSummary = guruList.map(g => {
                        const constraints = constraint_list.filter(c => c.tipe === 'guru_tidak_hadir' && matchConstraintGuru(c, g));
                        // Group by hari
                        const byHari = {};
                        constraints.forEach(c => {
                            const hariLabel = this.renderHariLabel(c.hari);
                            if (!byHari[hariLabel]) byHari[hariLabel] = [];
                            byHari[hariLabel].push(c.jam_ke_mulai);
                        });
                        return { ...g, count: constraints.length, byHari };
                    });
                    const withConstraints = guruSummary.filter(g => g.count > 0);
                    const withoutConstraints = guruSummary.filter(g => g.count === 0);

                    return (
                        <div className="pc-modal-overlay" style={{ zIndex: 9999, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)' }}
                            onClick={() => this.setState({ guru_detail_modal: false })}>
                            <div onClick={e => e.stopPropagation()} style={{
                                background: '#fff', borderRadius: 16, maxWidth: 600, width: '92%', margin: 'auto',
                                boxShadow: '0 25px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
                                animation: 'modalSlideIn 0.25s ease', maxHeight: '80vh', display: 'flex', flexDirection: 'column',
                            }}>
                                {/* Header */}
                                <div style={{
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                    padding: '20px 24px', color: '#fff',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <i className="bx bx-group" style={{ fontSize: 22 }}></i>
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: 16 }}>Detail Ketersediaan Guru</div>
                                            <div style={{ fontSize: 12, opacity: 0.85 }}>{guruList.length} guru — {gurusWithConstraints.size} ada batasan tidak hadir</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Body */}
                                <div style={{ overflowY: 'auto', padding: '16px 20px', flex: 1 }}>
                                    {withConstraints.length > 0 && (
                                        <>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                                                <i className="bx bx-error-circle" style={{ marginRight: 4 }}></i>
                                                Guru Dengan Jadwal Tidak Hadir ({withConstraints.length})
                                            </div>
                                            {withConstraints.map((g, idx) => (
                                                <div key={g.id} style={{
                                                    display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 12px',
                                                    borderRadius: 10, marginBottom: 6, cursor: 'pointer',
                                                    background: '#fef2f2', border: '1px solid #fecaca',
                                                    transition: 'all 0.15s ease',
                                                }} onClick={() => this.setState({ constraint_guru: String(g.id), guru_detail_modal: false })}
                                                    title="Klik untuk edit constraint guru ini">
                                                    <div style={{
                                                        width: 32, height: 32, borderRadius: 8, background: '#fca5a5',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        color: '#991b1b', fontWeight: 700, fontSize: 13, flexShrink: 0,
                                                    }}>{idx + 1}</div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{g.nama}</div>
                                                        <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, marginTop: 2 }}>
                                                            {g.count} slot tidak hadir
                                                        </div>
                                                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                            {Object.entries(g.byHari).map(([hari, jams]) => (
                                                                <span key={hari} style={{
                                                                    background: '#fee2e2', padding: '1px 6px', borderRadius: 4, fontSize: 10,
                                                                }}>
                                                                    {hari}: jam {jams.sort((a, b) => a - b).join(', ')}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <i className="bx bx-edit" style={{ color: '#94a3b8', fontSize: 16 }}></i>
                                                </div>
                                            ))}
                                        </>
                                    )}

                                    {withoutConstraints.length > 0 && (
                                        <>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, marginTop: withConstraints.length > 0 ? 16 : 0 }}>
                                                <i className="bx bx-check-circle" style={{ marginRight: 4 }}></i>
                                                Guru Tersedia Penuh ({withoutConstraints.length})
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                {withoutConstraints.map(g => (
                                                    <div key={g.id} style={{
                                                        padding: '6px 12px', borderRadius: 8, fontSize: 12,
                                                        background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534',
                                                        cursor: 'pointer', transition: 'all 0.15s ease',
                                                    }} onClick={() => this.setState({ constraint_guru: String(g.id), guru_detail_modal: false })}
                                                        title="Klik untuk edit constraint guru ini">
                                                        {g.nama}
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Footer */}
                                <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
                                    <button className="pc-btn pc-btn-ghost pc-btn-sm"
                                        onClick={() => this.setState({ guru_detail_modal: false })}>
                                        <i className="bx bx-x"></i> Tutup
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {guruList.length === 0 ? (
                    <div className="pc-alert pc-alert-warning">
                        <i className="bx bx-info-circle"></i> Belum ada penugasan guru. Silakan atur penugasan di Step 2 terlebih dahulu.
                    </div>
                ) : (
                    <div className="pc-form-card">
                        <div className="pc-constraint-header">
                            <div className="pc-form-group" style={{ flex: 1, marginBottom: 0 }}>
                                <label><i className="bx bx-user" style={{ marginRight: 4 }}></i> Pilih Guru</label>
                                <select className="pc-input pc-input-enhanced" value={constraint_guru}
                                    onChange={(e) => this.setState({ constraint_guru: e.target.value })}>
                                    <option value="">-- Pilih Guru --</option>
                                    {guruList.map(g => (
                                        <option key={g.id} value={g.id}>
                                            {g.nama} {gurusWithConstraints.has(String(g.id)) ? '(ada batasan)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {selectedGuru && constraintCountForGuru > 0 && (
                                <button className="pc-btn pc-btn-ghost pc-btn-sm" style={{ marginLeft: 12, alignSelf: 'flex-end' }}
                                    onClick={() => this.clearConstraintsForGuru(selectedGuru.nama)}>
                                    <i className="bx bx-reset"></i> Reset Guru Ini
                                </button>
                            )}
                        </div>

                        {selectedGuru && maxEntries > 0 && (
                            <div className="pc-constraint-grid-wrap">
                                <div className="pc-constraint-guru-name">
                                    <i className="bx bx-user-circle"></i> {selectedGuru.nama}
                                    {constraintCountForGuru > 0 && (
                                        <span className="pc-badge pc-badge-warning" style={{ marginLeft: 8 }}>{constraintCountForGuru} slot tidak hadir</span>
                                    )}
                                </div>
                                <div className="pc-table-container">
                                    <table className="pc-constraint-table">
                                        <thead>
                                            <tr>
                                                <th className="pc-constraint-th-jam" style={{ width: 30 }}>#</th>
                                                {gridHari.map(h => (
                                                    <th key={h} className="pc-constraint-th-hari">{this.renderHariLabel(h)}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.from({ length: maxEntries }, (_, rowIdx) => {
                                                // Trim seconds from time: "07:00:00" -> "07:00"
                                                const fmtTime = (t) => t ? t.substring(0, 5) : '';
                                                return (
                                                    <tr key={rowIdx}>
                                                        <td className="pc-constraint-td-jam" style={{ textAlign: 'center', color: '#94a3b8', fontSize: 10 }}>{rowIdx + 1}</td>
                                                        {gridHari.map(hari => {
                                                            const hNum = Number(hari);
                                                            const entry = hariEntries[hNum]?.[rowIdx];
                                                            if (!entry) {
                                                                return <td key={hari} className="pc-constraint-cell pc-constraint-no-slot"></td>;
                                                            }
                                                            const isPelajaran = !entry.is_istirahat && entry.tipe !== 'istirahat' && entry.tipe !== 'upacara' && entry.tipe !== 'kegiatan' && Number(entry.jam_ke) > 0;
                                                            if (!isPelajaran) {
                                                                const bLabel = entry.keterangan || entry.tipe || 'Istirahat';
                                                                return (
                                                                    <td key={hari} className="pc-constraint-cell pc-constraint-break"
                                                                        title={`${bLabel} (${fmtTime(entry.waktu_mulai)} - ${fmtTime(entry.waktu_selesai)})`}>
                                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '3px 2px' }}>
                                                                            <i className={`bx ${entry.tipe === 'upacara' ? 'bx-flag' : entry.tipe === 'kegiatan' ? 'bx-run' : 'bx-coffee'}`} style={{ fontSize: 14, color: '#64748b' }}></i>
                                                                            <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600, lineHeight: 1.2 }}>{bLabel}</span>
                                                                            <span style={{ fontSize: 9, color: '#94a3b8', lineHeight: 1 }}>{fmtTime(entry.waktu_mulai)} - {fmtTime(entry.waktu_selesai)}</span>
                                                                        </div>
                                                                    </td>
                                                                );
                                                            }
                                                            const unavailable = this.isGuruUnavailable(selectedGuru.nama, hari, Number(entry.jam_ke));
                                                            return (
                                                                <td key={hari}
                                                                    className={`pc-constraint-cell ${unavailable ? 'pc-constraint-unavail' : 'pc-constraint-avail'}`}
                                                                    onClick={() => this.toggleConstraint(selectedGuru.nama, selectedGuru.id, hari, Number(entry.jam_ke))}
                                                                    title={`Jam ${entry.jam_ke} (${fmtTime(entry.waktu_mulai)} - ${fmtTime(entry.waktu_selesai)}) — ${unavailable ? 'Tidak hadir' : 'Tersedia'}`}>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                                        <span style={{ fontSize: 12, fontWeight: 700 }}>Jam {entry.jam_ke}</span>
                                                                        <span style={{ fontSize: 10, color: unavailable ? '#ef4444' : '#16a34a', fontWeight: 500 }}>{fmtTime(entry.waktu_mulai)} - {fmtTime(entry.waktu_selesai)}</span>
                                                                        <i className={`bx ${unavailable ? 'bx-x' : 'bx-check'}`} style={{ fontSize: 16 }}></i>
                                                                    </div>
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="pc-constraint-legend">
                                    <span><span className="pc-legend-dot pc-legend-avail"></span> Tersedia (klik untuk tandai tidak hadir)</span>
                                    <span><span className="pc-legend-dot pc-legend-unavail"></span> Tidak Hadir (klik untuk jadikan tersedia)</span>
                                    <span><span className="pc-legend-dot pc-legend-break"></span> Istirahat / Non-pelajaran</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="pc-wizard-actions">
                    <button onClick={() => this.saveConstraint()} disabled={saving} className="pc-btn pc-btn-primary">
                        {saving ? <><i className="bx bx-loader-alt bx-spin"></i> Menyimpan...</> : <><i className="bx bx-save"></i> Simpan Ketersediaan</>}
                    </button>
                    {this.state.constraint_list.length === 0 && this.state.constraint_saved ? (
                        <span className="pc-save-status pc-save-status-saved"><i className="bx bx-check-circle"></i> Data tersimpan (belum ada perubahan)</span>
                    ) : this.state.constraint_saved ? (
                        <span className="pc-save-status pc-save-status-saved"><i className="bx bx-check-circle"></i> Tersimpan</span>
                    ) : (
                        <span className="pc-save-status pc-save-status-unsaved"><i className="bx bx-error-circle"></i> Ada perubahan belum disimpan</span>
                    )}
                </div>
            </div>
        );
    }


    renderStep4() {
        const { generating, generate_result, jadwal_preview, waktu_list, hari_aktif, preview_hari_tab,
            versi_list, versi_draft_id, show_save_versi_modal, new_versi_name, saving,
            rename_versi_id, rename_versi_name, penugasan_list, step4_mode,
            waktu_per_tingkat, selected_kelas, data_loading,
            step4_active_cell_modal, step4_is_visual_swap, step4_visual_swap_source,
            step4_swap_confirm_modal, step4_cell_saving } = this.state;

        // Guard reload: jangan proses data Step 4 sebelum semua fetch (loadWaktu,
        // loadPenugasan, loadVersiList, dst) dari lockSettings() selesai.
        if (data_loading) {
            return (
                <div className="pc-wizard-content pc-fade-in">
                    <h3 className="pc-wizard-step-title"><i className="bx bx-calendar-check"></i> Buat Jadwal</h3>
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <i className="bx bx-loader-alt bx-spin" style={{ fontSize: 32 }}></i>
                        <p style={{ marginTop: 12 }}>Memuat data jadwal...</p>
                    </div>
                </div>
            );
        }

        // Lapis kedua: kalau suatu saat state ini ternyata bukan array (misal ada
        // response API yang bentuknya berubah), forEach di bawah tidak akan crash.
        const safePenugasanList = Array.isArray(penugasan_list) ? penugasan_list : [];
        const safeJadwalPreview = Array.isArray(jadwal_preview) ? jadwal_preview : [];

        // Build rombel → kelas mapping from penugasan
        const rombelKelasMap = {};
        safePenugasanList.forEach(p => {
            if (p.rombel_nama && p.kelas) rombelKelasMap[p.rombel_nama] = p.kelas;
        });

        // Detect per-tingkat waktu differences
        const tingkatOptions4 = [...new Set(selected_kelas || [])].sort(sortKelas);
        const hasTingkatData = waktu_per_tingkat && Object.keys(waktu_per_tingkat).length > 1;
        let waktuIsSameForAll = true;
        if (hasTingkatData && tingkatOptions4.length > 1) {
            const serialized = tingkatOptions4.map(t => {
                const entries = (waktu_per_tingkat[t] || []).map(w => `${w.hari}_${w.jam_ke || ''}_${w.waktu_mulai || ''}_${w.waktu_selesai || ''}_${w.tipe || ''}`).sort().join('|');
                return entries;
            });
            waktuIsSameForAll = serialized.every(s => s === serialized[0]);
        }
        const showTingkatSelector4 = hasTingkatData && !waktuIsSameForAll;
        const activeTingkat4 = showTingkatSelector4 ? (this.state.preview_tingkat || tingkatOptions4[0] || '') : '';

        // Determine waktu data based on per-tingkat mode
        let effectiveWaktu = Array.isArray(waktu_list) ? waktu_list : [];
        let effectiveHari = hari_aktif;
        if (hasTingkatData) {
            if (showTingkatSelector4 && activeTingkat4 && Array.isArray(waktu_per_tingkat[activeTingkat4])) {
                effectiveWaktu = waktu_per_tingkat[activeTingkat4];
                const hariSet = new Set();
                effectiveWaktu.forEach(w => hariSet.add(Number(w.hari)));
                effectiveHari = [...hariSet].sort((a, b) => a - b);
            } else if (!showTingkatSelector4) {
                // Same waktu for all — use first tingkat's data
                const firstT = tingkatOptions4[0];
                if (firstT && Array.isArray(waktu_per_tingkat[firstT]) && waktu_per_tingkat[firstT].length > 0) {
                    effectiveWaktu = waktu_per_tingkat[firstT];
                    const hariSet = new Set();
                    effectiveWaktu.forEach(w => hariSet.add(Number(w.hari)));
                    effectiveHari = [...hariSet].sort((a, b) => a - b);
                }
            }
        }

        // Get unique rombel names, filtered by tingkat if per-tingkat mode
        let rombelNames = [...new Set(safePenugasanList.map(p => p.rombel_nama))].sort(sortRombel);
        if (showTingkatSelector4 && activeTingkat4) {
            rombelNames = rombelNames.filter(rn => String(rombelKelasMap[rn]) === String(activeTingkat4));
        }

        // Current day's waktu entries (all types)
        const currentHari = preview_hari_tab || effectiveHari[0] || 1;
        const dayEntries = effectiveWaktu
            .filter(w => Number(w.hari) === Number(currentHari))
            .sort((a, b) => {
                if (a.urutan !== undefined && b.urutan !== undefined) return a.urutan - b.urutan;
                const jamA = Number(a.jam_ke) || 0;
                const jamB = Number(b.jam_ke) || 0;
                if (jamA !== jamB) return jamA - jamB;
                if (a.waktu_mulai && b.waktu_mulai) return a.waktu_mulai.localeCompare(b.waktu_mulai);
                return 0;
            });

        // Build jadwal lookup: { rombel_nama -> { jam_ke -> [entries] } }
        const jadwalMap = {};
        safeJadwalPreview.forEach(j => {
            if (Number(j.hari) === Number(currentHari)) {
                // Filter by tingkat if applicable
                if (showTingkatSelector4 && activeTingkat4 && rombelKelasMap[j.rombel_nama] && String(rombelKelasMap[j.rombel_nama]) !== String(activeTingkat4)) return;
                if (!jadwalMap[j.rombel_nama]) jadwalMap[j.rombel_nama] = {};
                if (!jadwalMap[j.rombel_nama][j.jam_ke]) jadwalMap[j.rombel_nama][j.jam_ke] = [];
                jadwalMap[j.rombel_nama][j.jam_ke].push(j);
            }
        });

        return (
            <div className="pc-wizard-content pc-fade-in">
                <h3 className="pc-wizard-step-title"><i className="bx bx-calendar-check"></i> Buat Jadwal</h3>
                <p className="pc-wizard-step-desc">Pilih cara membuat jadwal: generate otomatis atau input jadwal yang sudah ada.</p>

                {/* Mode Selector */}
                <div className="pc-mode-selector">
                    <div className={`pc-mode-card ${step4_mode === 'generate' ? 'pc-mode-card-active' : ''}`}
                        onClick={() => this.setState({ step4_mode: 'generate' })}>
                        <div className="pc-mode-card-icon" style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}>
                            <i className="bx bx-magic-wand"></i>
                        </div>
                        <div className="pc-mode-card-body">
                            <h4>Generate Otomatis</h4>
                            <p>Biarkan sistem membuat jadwal optimal secara otomatis berdasarkan data penugasan & preferensi guru</p>
                        </div>
                        {step4_mode === 'generate' && <div className="pc-mode-card-check"><i className="bx bx-check-circle"></i></div>}
                    </div>
                    <div className={`pc-mode-card ${step4_mode === 'input' ? 'pc-mode-card-active' : ''}`}
                        onClick={() => this.setState({ step4_mode: 'input' })}>
                        <div className="pc-mode-card-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                            <i className="bx bx-edit-alt"></i>
                        </div>
                        <div className="pc-mode-card-body">
                            <h4>Input Jadwal Manual</h4>
                            <p>Masukkan jadwal yang sudah ada langsung ke timetable per rombel dengan klik & pilih</p>
                        </div>
                        {step4_mode === 'input' && <div className="pc-mode-card-check"><i className="bx bx-check-circle"></i></div>}
                    </div>
                </div>

                {/* Per-Tingkat Selector for timetable */}
                {showTingkatSelector4 && (
                    <div style={{ marginTop: 12, marginBottom: 12, background: '#f8fafc', borderRadius: 12, padding: '12px 16px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                            <span style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                                <i className="bx bx-layer" style={{ marginRight: 4 }}></i> Lihat Jadwal Per Tingkat:
                            </span>
                            <small style={{ color: '#94a3b8' }}>Waktu berbeda per tingkat — pilih tingkat untuk melihat rombel & waktu yang sesuai</small>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {tingkatOptions4.map(t => {
                                const count = [...new Set(safePenugasanList.filter(p => String(p.kelas) === String(t)).map(p => p.rombel_nama))].length;
                                return (
                                    <button
                                        key={t}
                                        className={`pc-kur-btn ${activeTingkat4 === t ? 'pc-kur-active' : ''}`}
                                        onClick={() => this.setState({ preview_tingkat: t, preview_hari_tab: null })}
                                        style={{ padding: '6px 14px', fontSize: 12 }}
                                    >
                                        Kelas {t}
                                        <span style={{ marginLeft: 4, fontSize: 10, color: activeTingkat4 === t ? '#bbf7d0' : '#64748b' }}>
                                            ({count} rombel)
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Input Manual Mode */}
                {step4_mode === 'input' && this.renderStep4Input(rombelNames, dayEntries, currentHari, effectiveHari)}

                {/* Generate Mode */}
                {step4_mode === 'generate' && <>
                    <p className="pc-wizard-step-desc" style={{ marginTop: 8 }}>Generate jadwal otomatis untuk <strong>semua rombel</strong>. Jadwal draft di-update setiap generate, simpan versi untuk menyimpan permanen.</p>

                    {/* Generate Settings */}
                    <div className="pc-generate-settings">
                        <div className="pc-form-row-inline" style={{ alignItems: 'center', gap: 12, marginBottom: 16 }}>
                            <label style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
                                <i className="bx bx-shuffle"></i> Mode Penyebaran:
                            </label>
                            <select className="pc-input pc-input-sm" style={{ maxWidth: 260 }}
                                value={this.state.mode_penyebaran}
                                onChange={(e) => this.setState({ mode_penyebaran: e.target.value })}>
                                <option value="acak">Acak (merata per hari)</option>
                                <option value="berurutan_2">Berurutan 2 jam per mapel</option>
                                <option value="berurutan_3">Berurutan 3 jam per mapel</option>
                                <option value="berurutan_4">Berurutan 4 jam per mapel</option>
                                <option value="berurutan_5">Berurutan 5 jam per mapel</option>
                                <option value="sejumlah_jam">Sejumlah jam mapel (blok penuh)</option>
                            </select>
                        </div>
                    </div>

                    {/* Jam validation warning */}
                    {(() => {
                        const validation = this.getJamValidation();
                        if (validation.hasExceed) {
                            return (
                                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '14px 18px', marginBottom: 16 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <i className="bx bx-error-circle" style={{ color: '#ef4444', fontSize: 20 }}></i>
                                        <strong style={{ color: '#dc2626', fontSize: 14 }}>Tidak bisa generate!</strong>
                                    </div>
                                    <p style={{ margin: '0 0 8px', fontSize: 13, color: '#7f1d1d' }}>
                                        Rombel berikut memiliki jam penugasan yang <strong>melebihi kuota {validation.totalTarget} jam/minggu</strong> dari pengaturan waktu. Silakan kurangi jam di Step 2.
                                    </p>
                                    <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#991b1b' }}>
                                        {validation.exceededList.map((r, i) => (
                                            <li key={i}><strong>{r.rombel_nama}</strong>: terpakai {r.jam} jam (kelebihan {r.diff} jam dari {r.target} tersedia)</li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        }
                        return (
                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 18px', marginBottom: 16 }}>
                                <p style={{ margin: 0, fontSize: 12, color: '#166534' }}>
                                    <i className="bx bx-check-circle" style={{ marginRight: 4 }}></i>
                                    <strong>Siap generate.</strong> Semua rombel sesuai target jam/minggu. Sistem akan otomatis memastikan <strong>guru tidak mengajar di waktu yang sama</strong> di rombel berbeda.
                                </p>
                            </div>
                        );
                    })()}

                    {/* Generate button */}
                    {!generating && (
                        <div className="pc-generate-section">
                            <button onClick={() => this.doGenerate()} disabled={this.getJamValidation().hasExceed} className="pc-btn pc-btn-primary pc-btn-lg pc-btn-glow">
                                <i className="bx bx-magic-wand"></i> {versi_draft_id ? 'ReGenerate Jadwal' : 'Buat Jadwal'}
                            </button>
                            {versi_draft_id && safeJadwalPreview.length > 0 && (
                                <button onClick={() => this.setState({ show_save_versi_modal: true, new_versi_name: '' })}
                                    className="pc-btn pc-btn-secondary" style={{ marginLeft: 12 }}>
                                    <i className="bx bx-save"></i> Simpan Versi
                                </button>
                            )}
                        </div>
                    )}

                    {/* Progress Animation */}
                    {generating && (
                        <div style={{ textAlign: 'center', padding: '50px 20px', background: 'linear-gradient(to right bottom, #f8fafc, #f1f5f9)', borderRadius: 20, border: '2px dashed #cbd5e1', marginBottom: 24, boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.02)' }}>
                            <div className="pc-animation-container">
                                <div className="pc-spinner-ring"></div>
                                <div className="pc-spinner-icon"><i className="bx bx-planet bx-tada"></i></div>
                            </div>
                            <h3 style={{ marginTop: 24, marginBottom: 8, color: '#1e293b', fontSize: 22, fontWeight: 700 }}>Algoritma AI Sedang Berjalan...</h3>
                            <p style={{ color: '#64748b', fontSize: 15, margin: 0, maxWidth: 500, marginInline: 'auto', lineHeight: 1.5 }}>
                                Mesin cerdas kami sedang menyusun jadwal terbaik dengan mempertimbangkan ketersediaan guru, sebaran mapel, dan mencegah terjadinya bentrok. Mohon tunggu beberapa detik...
                            </p>

                            <style dangerouslySetInnerHTML={{
                                __html: `
                                .pc-animation-container {
                                    position: relative;
                                    width: 100px;
                                    height: 100px;
                                    margin: 0 auto;
                                }
                                .pc-spinner-ring {
                                    position: absolute;
                                    top: 0; left: 0; right: 0; bottom: 0;
                                    border: 5px solid #e2e8f0;
                                    border-top-color: #3b82f6;
                                    border-right-color: #8b5cf6;
                                    border-bottom-color: #ec4899;
                                    border-left-color: #10b981;
                                    border-radius: 50%;
                                    animation: spinRing 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
                                }
                                .pc-spinner-icon {
                                    position: absolute;
                                    top: 50%; left: 50%;
                                    transform: translate(-50%, -50%);
                                    font-size: 44px;
                                    color: #4f46e5;
                                }
                                @keyframes spinRing {
                                    0% { transform: rotate(0deg); }
                                    100% { transform: rotate(360deg); }
                                }
                            `}} />
                        </div>
                    )}

                    {/* Result alert */}
                    {generate_result && !generating && (() => {
                        const bentrokan = generate_result.bentrokan || [];

                        // Separate guru_bentrok from kurang_slot issues
                        const guruBentrokList = bentrokan.filter(b => b.tipe === 'guru_bentrok');
                        const kurangSlotList = bentrokan.filter(b => b.tipe !== 'guru_bentrok');

                        // --- Group guru_bentrok by guru ---
                        const bentrokByGuru = {};
                        guruBentrokList.forEach(b => {
                            const key = b.guru || 'unknown';
                            if (!bentrokByGuru[key]) {
                                bentrokByGuru[key] = { guru: b.guru, conflicts: [] };
                            }
                            bentrokByGuru[key].conflicts.push(b);
                        });
                        const bentrokGurus = Object.values(bentrokByGuru);
                        const totalBentrokCount = guruBentrokList.length;

                        // --- Group kurang_slot by mapel ---
                        const kurangGrouped = {};
                        kurangSlotList.forEach(b => {
                            const mapelKey = b.mapel || 'unknown';
                            if (!kurangGrouped[mapelKey]) {
                                kurangGrouped[mapelKey] = {
                                    mapel: b.mapel, guruSet: new Set(), kelas: [],
                                    totalKurang: 0, totalButuh: 0, totalDapat: 0,
                                    isAgama: b.tipe === 'kurang_slot_agama',
                                    isOverload: (b.pesan || '').includes('KELEBIHAN BEBAN'),
                                    guruLoads: [], underutilized: [],
                                    _guruLoadMap: {},
                                };
                            }
                            (b.guru || '').split(', ').forEach(g => kurangGrouped[mapelKey].guruSet.add(g));
                            if (b.rombel && !kurangGrouped[mapelKey].kelas.includes(b.rombel)) {
                                kurangGrouped[mapelKey].kelas.push(b.rombel);
                            }
                            if (b.tipe === 'kurang_slot_agama') kurangGrouped[mapelKey].isAgama = true;
                            if ((b.pesan || '').includes('KELEBIHAN BEBAN')) kurangGrouped[mapelKey].isOverload = true;
                            kurangGrouped[mapelKey].totalKurang += (b.dibutuhkan || 0) - (b.teralokasi || 0);
                            kurangGrouped[mapelKey].totalButuh += (b.dibutuhkan || 0);
                            kurangGrouped[mapelKey].totalDapat += (b.teralokasi || 0);
                            // Merge guru_loads
                            if (b.guru_loads) {
                                b.guru_loads.forEach(gl => {
                                    if (!kurangGrouped[mapelKey]._guruLoadMap[gl.guru]) {
                                        kurangGrouped[mapelKey]._guruLoadMap[gl.guru] = gl;
                                    }
                                });
                            }
                            // Merge underutilized
                            if (b.underutilized) {
                                b.underutilized.forEach(u => {
                                    if (!kurangGrouped[mapelKey].underutilized.includes(u)) {
                                        kurangGrouped[mapelKey].underutilized.push(u);
                                    }
                                });
                            }
                        });
                        // Finalize guruLoads from map
                        Object.values(kurangGrouped).forEach(g => {
                            g.guruLoads = Object.values(g._guruLoadMap).sort((a, b) => a.load - b.load);
                            delete g._guruLoadMap;
                        });
                        const kurangIssues = Object.values(kurangGrouped).sort((a, b) => b.totalKurang - a.totalKurang);
                        const totalJamGagal = kurangIssues.reduce((s, g) => s + g.totalKurang, 0);

                        // --- Calculate overall stats ---
                        const hasIssues = bentrokan.length > 0;
                        const totalIssueCount = kurangIssues.length + bentrokGurus.length;
                        const totalJamTarget = generate_result.total_jadwal + totalJamGagal;
                        const successRate = totalJamTarget > 0
                            ? Math.round(((generate_result.total_jadwal - totalBentrokCount) / totalJamTarget) * 100)
                            : (hasIssues ? 0 : 100);

                        return (
                            <div className={`pc-alert ${hasIssues ? "pc-alert-warning" : "pc-alert-success"}`}>
                                {/* Header */}
                                <div className="pc-alert-header" style={{ marginBottom: 8 }}>
                                    <i className={`bx ${hasIssues ? "bx-error" : "bx-check-circle"}`}></i>
                                    <strong style={{ fontSize: '1.05em' }}>
                                        {hasIssues
                                            ? `⚠️ Jadwal Digenerate — ${totalIssueCount} Masalah Ditemukan`
                                            : "✅ Jadwal Berhasil Digenerate Tanpa Masalah!"}
                                    </strong>
                                </div>

                                {/* Summary bar — always show */}
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12, padding: '8px 12px', background: 'rgba(0,0,0,0.04)', borderRadius: 8 }}>
                                    <div style={{ textAlign: 'center', minWidth: 80 }}>
                                        <div style={{ fontSize: '1.3em', fontWeight: 700, color: '#059669' }}>{generate_result.total_jadwal}</div>
                                        <div style={{ fontSize: '0.72em', color: '#64748b' }}>Jam Terisi</div>
                                    </div>
                                    {totalBentrokCount > 0 && (
                                        <div style={{ textAlign: 'center', minWidth: 80 }}>
                                            <div style={{ fontSize: '1.3em', fontWeight: 700, color: '#ea580c' }}>{totalBentrokCount}</div>
                                            <div style={{ fontSize: '0.72em', color: '#64748b' }}>Guru Bentrok</div>
                                        </div>
                                    )}
                                    {totalJamGagal > 0 && (
                                        <div style={{ textAlign: 'center', minWidth: 80 }}>
                                            <div style={{ fontSize: '1.3em', fontWeight: 700, color: '#dc2626' }}>{totalJamGagal}</div>
                                            <div style={{ fontSize: '0.72em', color: '#64748b' }}>Jam Gagal</div>
                                        </div>
                                    )}
                                    <div style={{ textAlign: 'center', minWidth: 80 }}>
                                        <div style={{ fontSize: '1.3em', fontWeight: 700, color: '#2563eb' }}>{totalJamTarget}</div>
                                        <div style={{ fontSize: '0.72em', color: '#64748b' }}>Target Total</div>
                                    </div>
                                    <div style={{ textAlign: 'center', minWidth: 80 }}>
                                        <div style={{ fontSize: '1.3em', fontWeight: 700, color: successRate >= 95 ? '#059669' : successRate >= 80 ? '#f59e0b' : '#dc2626' }}>
                                            {Math.max(0, successRate)}%
                                        </div>
                                        <div style={{ fontSize: '0.72em', color: '#64748b' }}>Keberhasilan</div>
                                    </div>
                                </div>

                                {!hasIssues && (
                                    <p style={{ margin: '4px 0' }}>Total <strong>{generate_result.total_jadwal}</strong> entri jadwal berhasil dialokasikan tanpa konflik.{versi_draft_id ? ' Draft (belum disimpan).' : ''}</p>
                                )}

                                {/* === GURU BENTROK section === */}
                                {bentrokGurus.length > 0 && (
                                    <div style={{ marginBottom: 12 }}>
                                        <div style={{ fontSize: '0.9em', fontWeight: 700, color: '#ea580c', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <i className="bx bx-transfer-alt"></i>
                                            Guru Bentrok — {totalBentrokCount} konflik dari {bentrokGurus.length} guru
                                        </div>
                                        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                                            {bentrokGurus.map((bg, i) => (
                                                <div key={i} style={{
                                                    background: '#fff7ed', border: '1px solid #fed7aa',
                                                    borderLeft: '4px solid #ea580c', borderRadius: 8,
                                                    padding: '10px 14px', marginBottom: 8,
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                        <div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                                                <span style={{
                                                                    display: 'inline-block', padding: '1px 8px', borderRadius: 4,
                                                                    fontSize: '0.7em', fontWeight: 700, textTransform: 'uppercase',
                                                                    background: '#ea580c', color: '#fff',
                                                                }}>Bentrok</span>
                                                            </div>
                                                            <strong style={{ fontSize: '0.95em' }}><i className="bx bx-user" style={{ marginRight: 3 }}></i> {bg.guru}</strong>
                                                        </div>
                                                        <div style={{ textAlign: 'right', minWidth: 60 }}>
                                                            <div style={{ fontSize: '1.2em', fontWeight: 700, color: '#ea580c' }}>{bg.conflicts.length}x</div>
                                                            <div style={{ fontSize: '0.68em', color: '#94a3b8' }}>bentrok</div>
                                                        </div>
                                                    </div>

                                                    {/* Detail each conflict */}
                                                    {(() => {
                                                        const expandedKey = `bentrok_${i}`;
                                                        const isExpanded = this.state[`_expand_${expandedKey}`];
                                                        const showCount = isExpanded ? bg.conflicts.length : 3;
                                                        const hasMore = bg.conflicts.length > 3;
                                                        return (
                                                            <div style={{ marginTop: 6, fontSize: '0.8em' }}>
                                                                {bg.conflicts.slice(0, showCount).map((c, ci) => (
                                                                    <div key={ci} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#9a3412', padding: '3px 0', borderBottom: '1px dotted #fed7aa' }}>
                                                                        <div>
                                                                            <i className="bx bx-right-arrow-alt" style={{ marginRight: 3 }}></i>
                                                                            {c.pesan || `${c.guru} bentrok di ${c.rombel}`}
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            className="pc-btn pc-btn-xs"
                                                                            style={{ fontSize: 10, padding: '2px 8px', background: '#fed7aa', color: '#9a3412', border: 'none', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: 8, flexShrink: 0 }}
                                                                            onClick={() => this.jumpToConflictDay(c)}
                                                                            title="Buka hari jadwal ini di tabel di bawah"
                                                                        >
                                                                            <i className="bx bx-show" style={{ marginRight: 2 }}></i> Lihat di Tabel
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                                {hasMore && (
                                                                    <div
                                                                        onClick={() => this.setState({ [`_expand_${expandedKey}`]: !isExpanded })}
                                                                        style={{ color: '#2563eb', cursor: 'pointer', marginTop: 4, fontSize: '0.9em', fontWeight: 500, userSelect: 'none' }}
                                                                    >
                                                                        <i className={`bx ${isExpanded ? 'bx-chevron-up' : 'bx-chevron-down'}`} style={{ marginRight: 3 }}></i>
                                                                        {isExpanded ? 'Sembunyikan' : `Lihat ${bg.conflicts.length - 3} bentrok lainnya...`}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}

                                                    {/* Solution */}
                                                    <div style={{
                                                        marginTop: 8, padding: '6px 10px', borderRadius: 6,
                                                        background: 'rgba(255,255,255,0.7)', border: '1px dashed #fed7aa',
                                                        fontSize: '0.78em', color: '#475569',
                                                    }}>
                                                        <i className="bx bx-bulb" style={{ color: '#f59e0b', marginRight: 4 }}></i>
                                                        <strong>Saran Tindakan:</strong> Guru <strong>{bg.guru}</strong> terdeteksi mengajar di 2 kelas secara bersamaan. Silakan: <br />
                                                        (1) Tambahkan minimal 1 guru pendamping/pengganti untuk kelas-kelas yang diajar guru ini pada <strong>Step 2 Penugasan</strong> agar tidak terjadi tumpang tindih jadwal, atau <br />
                                                        (2) Kurangi jumlah kelas yang menjadi tanggung jawab guru ini, karena bentrok ini murni keterbatasan slot waktu guru tersebut.
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* === KURANG SLOT section === */}
                                {kurangIssues.length > 0 && (
                                    <div>
                                        <div style={{ fontSize: '0.9em', fontWeight: 700, color: '#dc2626', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <i className="bx bx-time-five"></i>
                                            Kurang Slot — {totalJamGagal} jam gagal dialokasikan
                                        </div>
                                        <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                                            {kurangIssues.map((g, i) => {
                                                const guruList = [...g.guruSet].filter(Boolean);
                                                const severity = g.totalKurang >= 10 ? 'high' : g.totalKurang >= 3 ? 'medium' : 'low';
                                                const severityColor = severity === 'high' ? '#dc2626' : severity === 'medium' ? '#f59e0b' : '#64748b';
                                                const severityBg = severity === 'high' ? '#fef2f2' : severity === 'medium' ? '#fffbeb' : '#f8fafc';
                                                const severityLabel = severity === 'high' ? 'Kritis' : severity === 'medium' ? 'Sedang' : 'Ringan';

                                                let solution;
                                                const friendlyGuru = guruList.length > 2 ? guruList.slice(0, 2).join(', ') + ' dkk' : guruList.join(' dan ');

                                                // Generate specific teacher split recommendations
                                                const kelasVII = g.kelas.filter(k => k.startsWith('VII-') || k.startsWith('VII '));
                                                const kelasVIII = g.kelas.filter(k => k.startsWith('VIII-') || k.startsWith('VIII '));
                                                const kelasIX = g.kelas.filter(k => k.startsWith('IX-') || k.startsWith('IX '));
                                                const kelasX = g.kelas.filter(k => k.startsWith('X-') || k.startsWith('X '));
                                                const kelasXI = g.kelas.filter(k => k.startsWith('XI-') || k.startsWith('XI '));
                                                const kelasXII = g.kelas.filter(k => k.startsWith('XII-') || k.startsWith('XII '));

                                                let recom = [];
                                                if (kelasVII.length > 0) recom.push(`tambah ${Math.max(1, Math.ceil(kelasVII.length / 2))} guru untuk kelas VII`);
                                                if (kelasVIII.length > 0) recom.push(`tambah ${Math.max(1, Math.ceil(kelasVIII.length / 2))} guru untuk kelas VIII`);
                                                if (kelasIX.length > 0) recom.push(`tambah ${Math.max(1, Math.ceil(kelasIX.length / 2))} guru untuk kelas IX`);
                                                if (kelasX.length > 0) recom.push(`tambah ${Math.max(1, Math.ceil(kelasX.length / 2))} guru untuk kelas X`);
                                                if (kelasXI.length > 0) recom.push(`tambah ${Math.max(1, Math.ceil(kelasXI.length / 2))} guru untuk kelas XI`);
                                                if (kelasXII.length > 0) recom.push(`tambah ${Math.max(1, Math.ceil(kelasXII.length / 2))} guru untuk kelas XII`);
                                                let recomText = recom.length > 0 ? recom.join(', ') : `tambah ${Math.max(1, Math.ceil(g.kelas.length / 2))} guru baru`;

                                                if (g.isAgama) {
                                                    solution = `Jadwal guru Agama (${friendlyGuru}) bentrok dengan jadwal mengajarnya di kelas lain pada jam kosong yang tersisa untuk kelas tersebut. Tindakan yang disarankan: (1) Pecah penugasannya! Pada Step 2 Penugasan, ${recomText}. (2) Diusahakan TIDAK SAMA gurunya antar jenjang kelas agar jam mengajar mereka tidak saling mengunci.`;
                                                } else if (g.isOverload) {
                                                    solution = `Total jam mengajar untuk kelas-kelas ini menyebabkan guru ${friendlyGuru} kehabisan slot waktu di jadwal pribadinya. Tindakan yang disarankan: (1) Pecah penugasannya! Pada Step 2 Penugasan, ${recomText} untuk mapel ${g.mapel}. (2) Pastikan Anda menggunakan guru yang berbeda-beda per jenjang kelas agar jadwal mudah dipecah oleh mesin.`;
                                                } else {
                                                    solution = `Semua guru mapel ${g.mapel} yang ditugaskan ke kelas ini (${friendlyGuru}) kebetulan sedang sibuk mengajar di kelas lain saat mesin mencoba mengisi jadwal kosong. Murni karena guru yang digunakan itu-itu saja untuk terlalu banyak kelas. Tindakan yang disarankan: Pada Step 2 Penugasan, ${recomText}. Sangat penting: usahakan guru yang ditugaskan TIDAK SAMA di semua kelas tersebut biar jadwalnya tidak saling bentrok/mengunci (deadlock).`;
                                                }

                                                return (
                                                    <div key={i} style={{
                                                        background: severityBg, border: `1px solid ${severityColor}22`,
                                                        borderLeft: `4px solid ${severityColor}`, borderRadius: 8,
                                                        padding: '10px 14px', marginBottom: 8,
                                                    }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                                                    <span style={{
                                                                        display: 'inline-block', padding: '1px 8px', borderRadius: 4,
                                                                        fontSize: '0.7em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                                                                        background: severityColor, color: '#fff',
                                                                    }}>{severityLabel}</span>
                                                                    <span style={{
                                                                        display: 'inline-block', padding: '1px 8px', borderRadius: 4,
                                                                        fontSize: '0.7em', fontWeight: 600,
                                                                        background: g.isAgama ? '#dbeafe' : '#fce7f3', color: g.isAgama ? '#1e40af' : '#9d174d',
                                                                    }}>{g.isAgama ? 'Guru Agama' : g.isOverload ? 'Kelebihan Beban' : 'Kurang Slot'}</span>
                                                                </div>
                                                                <strong style={{ fontSize: '0.95em' }}>{g.mapel}</strong>
                                                            </div>
                                                            <div style={{ textAlign: 'right', minWidth: 70 }}>
                                                                <div style={{ fontSize: '1.2em', fontWeight: 700, color: severityColor }}>-{g.totalKurang}</div>
                                                                <div style={{ fontSize: '0.68em', color: '#94a3b8' }}>jam gagal</div>
                                                            </div>
                                                        </div>

                                                        {(() => {
                                                            const guruExpandKey = `guru_kurang_${i}`;
                                                            const guruExpanded = this.state[`_expand_${guruExpandKey}`];
                                                            const showGurus = guruExpanded ? guruList : guruList.slice(0, 3);
                                                            const hasMoreGuru = guruList.length > 3;
                                                            return (
                                                                <div style={{ fontSize: '0.8em', color: '#64748b', marginTop: 4 }}>
                                                                    <i className="bx bx-user" style={{ marginRight: 3 }}></i>
                                                                    Guru: {showGurus.join(', ')}
                                                                    {hasMoreGuru && (
                                                                        <span
                                                                            onClick={() => this.setState({ [`_expand_${guruExpandKey}`]: !guruExpanded })}
                                                                            style={{ color: '#2563eb', cursor: 'pointer', marginLeft: 4, fontWeight: 500 }}
                                                                        >
                                                                            {guruExpanded ? ' [sembunyikan]' : ` (+${guruList.length - 3} lainnya ▸)`}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                        <div style={{ fontSize: '0.8em', color: '#64748b', marginTop: 2 }}>
                                                            <i className="bx bx-buildings" style={{ marginRight: 3 }}></i>
                                                            Gagal di {g.kelas.length} kelas: <span style={{ color: '#dc2626', fontWeight: 500 }}>{g.kelas.join(', ')}</span>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: '0.78em' }}>
                                                            <span style={{ color: '#64748b' }}>Dibutuhkan: <strong>{g.totalButuh}</strong></span>
                                                            <span style={{ color: '#059669' }}>Berhasil: <strong>{g.totalDapat}</strong></span>
                                                            <span style={{ color: '#dc2626' }}>Gagal: <strong>{g.totalKurang}</strong></span>
                                                        </div>

                                                        {/* Guru Load Breakdown */}
                                                        {g.guruLoads && g.guruLoads.length > 0 && (() => {
                                                            const loadExpandKey = `loads_kurang_${i}`;
                                                            const loadsExpanded = this.state[`_expand_${loadExpandKey}`];
                                                            return (
                                                                <div style={{ marginTop: 6 }}>
                                                                    <div
                                                                        onClick={() => this.setState({ [`_expand_${loadExpandKey}`]: !loadsExpanded })}
                                                                        style={{ fontSize: '0.78em', color: '#2563eb', cursor: 'pointer', fontWeight: 500, userSelect: 'none' }}
                                                                    >
                                                                        <i className={`bx ${loadsExpanded ? 'bx-chevron-up' : 'bx-chevron-down'}`} style={{ marginRight: 2 }}></i>
                                                                        {loadsExpanded ? 'Sembunyikan' : 'Lihat'} beban guru {g.mapel} ({g.guruLoads.length} guru)
                                                                    </div>
                                                                    {loadsExpanded && (
                                                                        <div style={{ marginTop: 4, padding: '6px 8px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                                                                            {g.guruLoads.map((gl, li) => (
                                                                                <div key={li} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: '0.78em', borderBottom: li < g.guruLoads.length - 1 ? '1px dotted #e2e8f0' : 'none' }}>
                                                                                    <span style={{ color: '#334155' }}>{gl.guru}</span>
                                                                                    <span style={{ color: gl.load < 10 ? '#059669' : gl.load < 20 ? '#f59e0b' : '#dc2626', fontWeight: 600 }}>
                                                                                        {gl.load} jam
                                                                                        {gl.load < 10 && <span style={{ color: '#059669', marginLeft: 4 }}>✦ ringan</span>}
                                                                                    </span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}

                                                        {/* Solution with underutilized guru suggestions */}
                                                        <div style={{
                                                            marginTop: 8, padding: '6px 10px', borderRadius: 6,
                                                            background: 'rgba(255,255,255,0.7)', border: '1px dashed #cbd5e1',
                                                            fontSize: '0.78em', color: '#475569',
                                                        }}>
                                                            <i className="bx bx-bulb" style={{ color: '#f59e0b', marginRight: 4 }}></i>
                                                            <strong>Solusi:</strong> {solution}
                                                            {g.underutilized && g.underutilized.length > 0 && (
                                                                <div style={{ marginTop: 4, padding: '4px 8px', background: '#ecfdf5', borderRadius: 4, border: '1px solid #a7f3d0', color: '#065f46' }}>
                                                                    <i className="bx bx-user-plus" style={{ marginRight: 4 }}></i>
                                                                    <strong>Rekomendasi:</strong> Tambahkan guru berikut ke kelas {g.kelas.join(', ')} di Step 2 Penugasan:
                                                                    <div style={{ marginTop: 2, fontWeight: 500 }}>
                                                                        {g.underutilized.slice(0, 5).map((u, ui) => (
                                                                            <div key={ui}>→ {u}</div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}



                    {/* Total Jam / Minggu Summary */}
                    {safeJadwalPreview.length > 0 && !generating && (() => {
                        // Deduplicate: agama entries at same (rombel, hari, jam_ke) count as 1 slot
                        const rombelJam = {};
                        const seen = new Set();
                        safeJadwalPreview.forEach(j => {
                            const key = `${j.rombel_nama}_${j.hari}_${j.jam_ke}`;
                            if (seen.has(key)) return;
                            seen.add(key);
                            if (!rombelJam[j.rombel_nama]) rombelJam[j.rombel_nama] = { jam: 0, kelas: rombelKelasMap[j.rombel_nama] || '' };
                            rombelJam[j.rombel_nama].jam++;
                        });

                        // Calculate target jam per rombel from waktu settings
                        const getTargetJam = (kelas) => {
                            let wList = waktu_list;
                            if (waktu_per_tingkat && kelas && waktu_per_tingkat[kelas]) {
                                wList = waktu_per_tingkat[kelas];
                            }
                            const hariSet = [...new Set(wList.map(w => Number(w.hari)))];
                            return hariSet.reduce((sum, h) => sum + wList.filter(w => Number(w.hari) === h && (w.tipe === 'pelajaran' || (!w.is_istirahat && !w.tipe))).length, 0);
                        };

                        const sortedRombels = Object.keys(rombelJam).sort(sortRombel);
                        const totalSlots = Object.values(rombelJam).reduce((a, b) => a + b.jam, 0);
                        let countTerpenuhi = 0;
                        let countBelum = 0;
                        const rombelData = sortedRombels.map(rn => {
                            const info = rombelJam[rn];
                            const target = getTargetJam(info.kelas);
                            const terpenuhi = target > 0 && info.jam >= target;
                            if (terpenuhi) countTerpenuhi++; else countBelum++;
                            return { rn, jam: info.jam, target, terpenuhi, kurang: target - info.jam };
                        });
                        const totalTarget = rombelData.reduce((s, r) => s + r.target, 0);

                        return (
                            <div className="pc-summary-section" style={{ marginTop: 16, marginBottom: 16 }}>
                                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <i className="bx bx-bar-chart-alt-2"></i> Ringkasan Jadwal
                                </h4>
                                {/* Summary stats */}
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10, padding: '6px 10px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.82em' }}>
                                    <span style={{ color: '#475569' }}>
                                        <strong>{totalSlots}</strong>/{totalTarget} total jam
                                    </span>
                                    <span style={{ color: '#059669' }}>
                                        <i className="bx bx-check-circle" style={{ marginRight: 2 }}></i>
                                        <strong>{countTerpenuhi}</strong> kelas terpenuhi
                                    </span>
                                    {countBelum > 0 && (
                                        <span style={{ color: '#dc2626' }}>
                                            <i className="bx bx-error-circle" style={{ marginRight: 2 }}></i>
                                            <strong>{countBelum}</strong> kelas belum terpenuhi
                                        </span>
                                    )}
                                </div>
                                {/* Per-rombel badges */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {rombelData.map(r => (
                                        <div key={r.rn} style={{
                                            padding: '4px 10px', borderRadius: 8, fontSize: 12,
                                            background: r.terpenuhi ? '#f0fdf4' : '#fef2f2',
                                            border: `1px solid ${r.terpenuhi ? '#bbf7d0' : '#fecaca'}`,
                                            color: r.terpenuhi ? '#166534' : '#991b1b',
                                            display: 'flex', alignItems: 'center', gap: 4,
                                        }}>
                                            <i className={`bx ${r.terpenuhi ? 'bx-check-circle' : 'bx-error-circle'}`}
                                                style={{ fontSize: 13 }}></i>
                                            <strong>{r.rn}</strong>:
                                            <span>{r.jam}/{r.target} jam</span>
                                            {!r.terpenuhi && r.kurang > 0 && (
                                                <span style={{ fontSize: '0.85em', color: '#dc2626', fontWeight: 600 }}>
                                                    (kurang {r.kurang})
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Unified Timetable Preview */}
                    {safeJadwalPreview.length > 0 && dayEntries.length > 0 && !generating && (
                        <div className="pc-timetable-section">
                            {/* Timetable Toolbar & Quick Move / Swap controls */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12, padding: '10px 14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <button
                                        type="button"
                                        className={`pc-btn pc-btn-sm ${step4_is_visual_swap ? 'pc-btn-primary' : 'pc-btn-secondary'}`}
                                        onClick={() => this.toggleStep4VisualSwap()}
                                        style={{ fontWeight: 600 }}
                                        title="Klik tombol ini untuk menukar atau memindahkan jadwal secara langsung di tabel"
                                    >
                                        <i className={`bx ${step4_is_visual_swap ? 'bx-check-circle' : 'bx-transfer-alt'}`}></i>
                                        {step4_is_visual_swap ? 'Mode Tukar Aktif' : 'Mode Tukar / Pindah Cepat'}
                                    </button>

                                    <button
                                        type="button"
                                        className="pc-btn pc-btn-sm pc-btn-secondary"
                                        onClick={() => this.copyGenerateToInput()}
                                        title="Salin hasil generate ini ke mode Input Jadwal Manual untuk diedit per sel di spreadsheet"
                                    >
                                        <i className="bx bx-edit-alt"></i> Salin ke Input Manual
                                    </button>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <small style={{ color: '#64748b', fontSize: 12 }}>
                                        <i className="bx bx-pointer" style={{ marginRight: 3 }}></i> Klik sel jadwal di bawah untuk pindahkan / edit langsung
                                    </small>
                                    {versi_draft_id && (
                                        <Link
                                            to={`/jadwal-pelajaran-v2/edit?versi=${versi_draft_id}`}
                                            target="_blank"
                                            className="pc-btn pc-btn-xs pc-btn-ghost"
                                            style={{ color: '#4f46e5', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                            title="Buka Editor Jadwal Lengkap di tab baru"
                                        >
                                            <i className="bx bx-link-external"></i> Editor Lengkap
                                        </Link>
                                    )}
                                </div>
                            </div>

                            {/* Visual Swap Banner if active */}
                            {step4_is_visual_swap && (
                                <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <i className="bx bx-info-circle" style={{ color: '#2563eb', fontSize: 20 }}></i>
                                        <span style={{ fontSize: 13, color: '#1e40af' }}>
                                            {step4_visual_swap_source ? (
                                                <>
                                                    <strong>Slot 1 dipilih:</strong> {step4_visual_swap_source.rombel_nama} - {this.renderHariLabel(step4_visual_swap_source.hari)} Jam ke-{step4_visual_swap_source.jam_ke} ({step4_visual_swap_source.mapel_nama || 'Kosong'}). <strong>Sekarang klik slot kedua (tujuan)</strong> di tabel di bawah!
                                                </>
                                            ) : (
                                                <>
                                                    <strong>Mode Tukar / Pindah Cepat Aktif:</strong> Silakan klik slot pertama di tabel di bawah sebagai sumber yang ingin dipindahkan/ditukar.
                                                </>
                                            )}
                                        </span>
                                    </div>
                                    <button className="pc-btn pc-btn-xs pc-btn-ghost" onClick={() => this.cancelStep4VisualSwap()} style={{ color: '#dc2626', fontWeight: 600 }}>
                                        <i className="bx bx-x"></i> Batalkan
                                    </button>
                                </div>
                            )}

                            {/* Day tabs */}
                            <div className="pc-timetable-tabs">
                                {effectiveHari.map(h => (
                                    <button key={h}
                                        className={`pc-timetable-tab ${currentHari === h ? 'pc-timetable-tab-active' : ''}`}
                                        onClick={() => this.setState({ preview_hari_tab: h })}>
                                        {this.renderHariLabel(h)}
                                    </button>
                                ))}
                            </div>

                            {/* The big table */}
                            <div className="pc-timetable-container">
                                <table className="pc-timetable">
                                    <thead>
                                        <tr>
                                            <th className="pc-tt-th-jam" style={{ minWidth: 70 }}>Jam</th>
                                            <th className="pc-tt-th-time" style={{ minWidth: 75 }}>Waktu</th>
                                            {rombelNames.map(rn => (
                                                <th key={rn} className="pc-tt-th-rombel">{rn}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            let istirahatCount = 0;
                                            return dayEntries.map((entry, ei) => {
                                                const isBreak = entry.is_istirahat || entry.tipe === 'istirahat' || entry.tipe === 'upacara' || entry.tipe === 'kegiatan';
                                                const timeStr = `${this.formatTime(entry.waktu_mulai)} - ${this.formatTime(entry.waktu_selesai)}`;

                                                if (isBreak) {
                                                    const breakTipe = entry.tipe || 'istirahat';
                                                    let breakLabel = entry.keterangan || breakTipe;
                                                    if (breakTipe === 'istirahat') {
                                                        istirahatCount++;
                                                        breakLabel = `Istirahat ${istirahatCount}`;
                                                    }
                                                    return (
                                                        <tr key={`break_${ei}`} className={`pc-tt-break-row pc-tt-break-${breakTipe}`}>
                                                            <td className="pc-tt-break-jam">
                                                                {breakTipe === 'upacara' ? <i className="bx bx-flag"></i> :
                                                                    breakTipe === 'kegiatan' ? <i className="bx bx-run"></i> :
                                                                        <i className="bx bx-coffee"></i>}
                                                            </td>
                                                            <td className="pc-tt-break-time">{timeStr}</td>
                                                            <td colSpan={rombelNames.length} className="pc-tt-break-label">
                                                                <span className={`pc-tt-break-badge pc-tt-break-${breakTipe}`}>
                                                                    {breakLabel}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                }

                                                return (
                                                    <tr key={`jam_${entry.jam_ke}_${ei}`} className="pc-tt-row">
                                                        <td className="pc-tt-jam">{entry.jam_ke}</td>
                                                        <td className="pc-tt-time">{timeStr}</td>
                                                        {rombelNames.map(rn => {
                                                            const cells = jadwalMap[rn] && jadwalMap[rn][entry.jam_ke];
                                                            const isFilled = cells && cells.length > 0;
                                                            const firstCell = isFilled ? cells[0] : null;
                                                            const isAgama = isFilled && cells.length > 1 && cells[0].keterangan === 'Pemisahan agama';
                                                            const guruConflict = isFilled && !isAgama && rombelNames.some(other =>
                                                                other !== rn && jadwalMap[other]?.[entry.jam_ke]?.[0]?.guru_nama === firstCell.guru_nama
                                                            );
                                                            const isSwapSource = step4_is_visual_swap && step4_visual_swap_source &&
                                                                step4_visual_swap_source.rombel_nama === rn &&
                                                                Number(step4_visual_swap_source.hari) === Number(currentHari) &&
                                                                Number(step4_visual_swap_source.jam_ke) === Number(entry.jam_ke);

                                                            const cellClass = [
                                                                'pc-tt-cell',
                                                                isFilled ? 'pc-tt-filled' : 'pc-tt-empty',
                                                                guruConflict ? 'pc-tt-conflict' : '',
                                                                'pc-tt-clickable',
                                                                isSwapSource ? 'pc-tt-selected-swap' : ''
                                                            ].filter(Boolean).join(' ');

                                                            return (
                                                                <td
                                                                    key={rn}
                                                                    className={cellClass}
                                                                    onClick={() => this.handleStep4CellClick(firstCell, rn, currentHari, entry.jam_ke, timeStr)}
                                                                    title={step4_is_visual_swap
                                                                        ? (step4_visual_swap_source ? "Klik untuk menukar ke slot ini" : "Klik untuk memilih slot pertama")
                                                                        : (isFilled ? "Klik untuk memindahkan, menukar, atau mengedit slot ini" : "Klik untuk mengisi slot kosong ini")}
                                                                >
                                                                    {isSwapSource && (
                                                                        <div style={{ position: 'absolute', top: 2, right: 2, background: '#2563eb', color: '#fff', fontSize: '9px', padding: '1px 4px', borderRadius: 3, fontWeight: 700 }}>
                                                                            Sumber
                                                                        </div>
                                                                    )}
                                                                    {!isFilled ? (
                                                                        <div className="pc-tt-empty-action">
                                                                            <span style={{ color: '#cbd5e1', fontSize: '0.75em' }}>—</span>
                                                                            <span className="pc-tt-empty-add-icon"><i className="bx bx-plus"></i></span>
                                                                        </div>
                                                                    ) : isAgama ? (
                                                                        <>
                                                                            <div className="pc-tt-mapel" style={{ fontSize: 11 }} title={cells.map(c => c.mapel_nama).join(' & ')}>
                                                                                {cells.map(c => c.mapel_nama).join(' & ')}
                                                                            </div>
                                                                            <div className="pc-tt-guru" style={{ fontSize: 10 }} title={cells.map(c => c.guru_nama).join(', ')}>
                                                                                {cells.map(c => c.guru_nama).join(', ')}
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <div className="pc-tt-mapel" title={firstCell.mapel_nama}>{firstCell.mapel_nama}</div>
                                                                            <div className="pc-tt-guru" title={firstCell.guru_nama}>{firstCell.guru_nama}</div>
                                                                        </>
                                                                    )}
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                );
                                            });
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Saved versions */}
                    {versi_list.length > 0 && (
                        <div className="pc-versi-section">
                            <h4><i className="bx bx-history"></i> Versi Tersimpan</h4>
                            <div className="pc-versi-list">
                                {versi_list.filter(v => !v.is_draft).map(v => (
                                    <div key={v.id} className={`pc-versi-card ${v.is_aktif ? 'pc-versi-active' : ''}`}>
                                        {rename_versi_id === v.id ? (
                                            <div className="pc-versi-rename">
                                                <input className="pc-input pc-input-sm" value={rename_versi_name}
                                                    onChange={(e) => this.setState({ rename_versi_name: e.target.value })}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') this.renameVersi(v.id, rename_versi_name); }}
                                                    autoFocus />
                                                <button className="pc-btn pc-btn-xs pc-btn-primary" onClick={() => this.renameVersi(v.id, rename_versi_name)}>
                                                    <i className="bx bx-check"></i>
                                                </button>
                                                <button className="pc-btn pc-btn-xs pc-btn-ghost" onClick={() => this.setState({ rename_versi_id: null })}>
                                                    <i className="bx bx-x"></i>
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="pc-versi-info">
                                                    <span className="pc-versi-name">{v.nama_versi}</span>
                                                    {v.is_aktif && <span className="pc-badge pc-badge-success">Aktif</span>}
                                                    <small className="pc-versi-date">{v.created_at ? new Date(v.created_at).toLocaleString('id-ID') : ''}</small>
                                                </div>
                                                <div className="pc-versi-actions">
                                                    <button className="pc-btn pc-btn-xs pc-btn-ghost" title="Lihat"
                                                        onClick={() => { this.setState({ versi_draft_id: v.id }); this.loadPreview(v.id); }}>
                                                        <i className="bx bx-show"></i>
                                                    </button>
                                                    <button className="pc-btn pc-btn-xs pc-btn-ghost" title="Rename"
                                                        onClick={() => this.setState({ rename_versi_id: v.id, rename_versi_name: v.nama_versi })}>
                                                        <i className="bx bx-edit"></i>
                                                    </button>
                                                    <button className="pc-btn pc-btn-xs pc-btn-danger" title="Hapus"
                                                        onClick={() => this.deleteVersi(v.id)}>
                                                        <i className="bx bx-trash"></i>
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>}

                {/* Save version modal - outside of tab blocks so it works from both tabs */}
                {show_save_versi_modal && (
                    <div className="pc-modal-overlay" onClick={() => this.setState({ show_save_versi_modal: false })}>
                        <div className="pc-modal-content pc-modal-sm" onClick={e => e.stopPropagation()}>
                            <h4><i className="bx bx-save"></i> Simpan Versi Jadwal</h4>
                            <p className="pc-wizard-step-desc">Beri nama untuk versi jadwal ini. Draft saat ini akan disalin sebagai versi tersimpan.</p>
                            <div className="pc-form-group">
                                <label>Nama Versi</label>
                                <input className="pc-input" value={new_versi_name} placeholder="Contoh: Jadwal Semester 1 Final"
                                    onChange={(e) => this.setState({ new_versi_name: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === 'Enter') this.saveVersi(); }}
                                    autoFocus />
                            </div>
                            <div className="pc-modal-actions">
                                <button className="pc-btn pc-btn-primary" disabled={!new_versi_name.trim() || saving} onClick={() => this.saveVersi()}>
                                    {saving ? <><i className="bx bx-loader-alt bx-spin"></i> Menyimpan...</> : <><i className="bx bx-save"></i> Simpan</>}
                                </button>
                                <button className="pc-btn pc-btn-ghost" onClick={() => this.setState({ show_save_versi_modal: false })}>Batal</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal: Atur / Pindahkan / Tukar / Isi Slot */}
                {step4_active_cell_modal && (() => {
                    const m = step4_active_cell_modal;
                    const isEditing = !m.is_empty;
                    const rombelPenugasan = safePenugasanList.filter(p => p.rombel_nama === m.rombel_nama);
                    const activeDays = this.getActiveDaysForRombel(m.rombel_nama);
                    const validPeriods = this.getLessonPeriodsForRombelAndHari(m.rombel_nama, m.target_hari);

                    // Find current occupant of target slot (if different slot)
                    const targetSlotOccupant = safeJadwalPreview.find(j =>
                        j.rombel_nama === m.rombel_nama &&
                        Number(j.hari) === Number(m.target_hari) &&
                        Number(j.jam_ke) === Number(m.target_jam_ke) &&
                        (Number(j.hari) !== Number(m.hari) || Number(j.jam_ke) !== Number(m.jam_ke))
                    );

                    // Other filled slots in this rombel for swap dropdown
                    const otherFilledSlots = safeJadwalPreview.filter(j =>
                        j.rombel_nama === m.rombel_nama &&
                        (Number(j.hari) !== Number(m.hari) || Number(j.jam_ke) !== Number(m.jam_ke))
                    ).sort((a, b) => {
                        if (Number(a.hari) !== Number(b.hari)) return Number(a.hari) - Number(b.hari);
                        return Number(a.jam_ke) - Number(b.jam_ke);
                    });

                    return (
                        <div className="pc-modal-overlay" onClick={() => this.setState({ step4_active_cell_modal: null })}>
                            <div className="pc-modal-content pc-modal-md" onClick={e => e.stopPropagation()}>
                                {/* Header */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                    <div>
                                        <h4 style={{ margin: '0 0 4px', fontSize: 16 }}>
                                            <i className={`bx ${isEditing ? 'bx-slider-alt' : 'bx-plus-circle'}`}></i>
                                            {isEditing ? 'Atur / Pindahkan Slot Jadwal' : 'Isi Slot Jadwal Kosong'}
                                        </h4>
                                        <div style={{ fontSize: 12.5, color: '#64748b' }}>
                                            Kelas/Rombel: <strong style={{ color: '#1e293b' }}>{m.rombel_nama}</strong> &bull; {this.renderHariLabel(m.hari)} Jam ke-{m.jam_ke} {m.waktuLabel ? `(${m.waktuLabel})` : ''}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="pc-btn pc-btn-xs pc-btn-ghost"
                                        onClick={() => this.setState({ step4_active_cell_modal: null })}
                                        style={{ color: '#94a3b8', fontSize: 18, padding: 4 }}
                                    >
                                        <i className="bx bx-x"></i>
                                    </button>
                                </div>

                                {/* Current slot info banner */}
                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#94a3b8', fontWeight: 700 }}>
                                                {isEditing ? 'Jadwal Saat Ini' : 'Status Slot'}
                                            </div>
                                            <div style={{ fontSize: 14, fontWeight: 700, color: isEditing ? '#1e293b' : '#64748b', marginTop: 2 }}>
                                                {isEditing ? m.mapel_nama : 'Slot Masih Kosong'}
                                            </div>
                                            {isEditing && (
                                                <div style={{ fontSize: 12, color: '#4f46e5', marginTop: 1 }}>
                                                    <i className="bx bx-user" style={{ marginRight: 3 }}></i> {m.guru_nama || '—'}
                                                </div>
                                            )}
                                        </div>
                                        <span className={`pc-badge ${isEditing ? 'pc-badge-info' : 'pc-badge-warning'}`}>
                                            {isEditing ? 'Terisi' : 'Kosong'}
                                        </span>
                                    </div>
                                </div>

                                {/* Tab Navigation (for filled slots) */}
                                {isEditing && (
                                    <div className="pc-modal-tab-nav">
                                        <button
                                            type="button"
                                            className={`pc-modal-tab-btn ${m.action_tab === 'pindah' ? 'active' : ''}`}
                                            onClick={() => this.setState(prev => ({ step4_active_cell_modal: { ...prev.step4_active_cell_modal, action_tab: 'pindah', bentrokan_error: null } }))}
                                        >
                                            <i className="bx bx-transfer"></i> Pindahkan Slot
                                        </button>
                                        <button
                                            type="button"
                                            className={`pc-modal-tab-btn ${m.action_tab === 'tukar' ? 'active' : ''}`}
                                            onClick={() => this.setState(prev => ({ step4_active_cell_modal: { ...prev.step4_active_cell_modal, action_tab: 'tukar', bentrokan_error: null } }))}
                                        >
                                            <i className="bx bx-transfer-alt"></i> Tukar Slot
                                        </button>
                                        <button
                                            type="button"
                                            className={`pc-modal-tab-btn ${m.action_tab === 'ganti' ? 'active' : ''}`}
                                            onClick={() => this.setState(prev => ({ step4_active_cell_modal: { ...prev.step4_active_cell_modal, action_tab: 'ganti', bentrokan_error: null } }))}
                                        >
                                            <i className="bx bx-edit-alt"></i> Ganti Mapel
                                        </button>
                                        <button
                                            type="button"
                                            className={`pc-modal-tab-btn ${m.action_tab === 'kosongkan' ? 'active' : ''}`}
                                            onClick={() => this.setState(prev => ({ step4_active_cell_modal: { ...prev.step4_active_cell_modal, action_tab: 'kosongkan', bentrokan_error: null } }))}
                                            style={{ color: m.action_tab === 'kosongkan' ? '#dc2626' : undefined }}
                                        >
                                            <i className="bx bx-trash"></i> Kosongkan
                                        </button>
                                    </div>
                                )}

                                {/* Conflict Alert (if any from previous execute attempt) */}
                                {m.bentrokan_error && (
                                    <div style={{ background: '#fef2f2', border: '1px solid #f87171', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#dc2626', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                                            <i className="bx bx-error-circle" style={{ fontSize: 18 }}></i>
                                            <span>{m.bentrokan_error.message}</span>
                                        </div>
                                        {m.bentrokan_error.bentrokan?.map((b, bi) => (
                                            <div key={bi} style={{ fontSize: 12, color: '#991b1b', marginLeft: 24, marginTop: 2 }}>
                                                &bull; {b.pesan || `${b.guru} bentrok di ${b.rombel}`}
                                            </div>
                                        ))}
                                        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                                            <button
                                                type="button"
                                                className="pc-btn pc-btn-xs"
                                                style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '5px 12px', fontWeight: 600 }}
                                                disabled={step4_cell_saving}
                                                onClick={() => {
                                                    if (m.action_tab === 'pindah') this.executeStep4Move(true);
                                                    else if (m.action_tab === 'ganti' || m.action_tab === 'isi') this.executeStep4FillSlot(true);
                                                }}
                                            >
                                                {step4_cell_saving ? <><i className="bx bx-loader-alt bx-spin"></i> Memproses...</> : 'Tetap Simpan (Force / Abaikan Bentrok)'}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* TAB 1: PINDAHKAN SLOT */}
                                {(m.action_tab === 'pindah') && (
                                    <div>
                                        <p style={{ fontSize: 12.5, color: '#64748b', margin: '0 0 12px' }}>
                                            Pilih hari dan jam tujuan baru untuk memindahkan jadwal <strong>{m.mapel_nama}</strong> ({m.guru_nama}) di rombel <strong>{m.rombel_nama}</strong>.
                                        </p>

                                        <div className="pc-form-row" style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
                                                    Hari Tujuan
                                                </label>
                                                <select
                                                    className="pc-input"
                                                    value={m.target_hari}
                                                    onChange={(e) => {
                                                        const newHari = Number(e.target.value);
                                                        const periods = this.getLessonPeriodsForRombelAndHari(m.rombel_nama, newHari);
                                                        const newJam = periods[0]?.jam_ke ? Number(periods[0].jam_ke) : 1;
                                                        this.setState(prev => ({
                                                            step4_active_cell_modal: {
                                                                ...prev.step4_active_cell_modal,
                                                                target_hari: newHari,
                                                                target_jam_ke: newJam,
                                                                bentrokan_error: null
                                                            }
                                                        }));
                                                    }}
                                                >
                                                    {activeDays.map(d => (
                                                        <option key={d} value={d}>{this.renderHariLabel(d)}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div style={{ flex: 1.5 }}>
                                                <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
                                                    Jam Ke Tujuan
                                                </label>
                                                <select
                                                    className="pc-input"
                                                    value={m.target_jam_ke}
                                                    onChange={(e) => {
                                                        const newJam = Number(e.target.value);
                                                        this.setState(prev => ({
                                                            step4_active_cell_modal: {
                                                                ...prev.step4_active_cell_modal,
                                                                target_jam_ke: newJam,
                                                                bentrokan_error: null
                                                            }
                                                        }));
                                                    }}
                                                >
                                                    {validPeriods.map(p => {
                                                        const occ = safeJadwalPreview.find(j =>
                                                            j.rombel_nama === m.rombel_nama &&
                                                            Number(j.hari) === Number(m.target_hari) &&
                                                            Number(j.jam_ke) === Number(p.jam_ke)
                                                        );
                                                        const isSameAsCurrent = Number(m.target_hari) === Number(m.hari) && Number(p.jam_ke) === Number(m.jam_ke);
                                                        const statusStr = isSameAsCurrent ? '(Posisi Saat Ini)' : occ ? `[Terisi: ${occ.mapel_nama}]` : '[Kosong]';
                                                        return (
                                                            <option key={p.jam_ke} value={p.jam_ke}>
                                                                Jam ke-{p.jam_ke} ({this.formatTime(p.waktu_mulai)} - {this.formatTime(p.waktu_selesai)}) {statusStr}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                        </div>

                                        {/* Status notification of target slot */}
                                        {targetSlotOccupant ? (
                                            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#92400e' }}>
                                                <i className="bx bx-info-circle" style={{ marginRight: 4 }}></i>
                                                <strong>Perhatian:</strong> Slot tujuan ({this.renderHariLabel(m.target_hari)} Jam ke-{m.target_jam_ke}) saat ini sudah terisi oleh <strong>{targetSlotOccupant.mapel_nama} ({targetSlotOccupant.guru_nama})</strong>. Jika Anda memindahkannya, slot tersebut akan ditimpa. Atau gunakan tab <strong>Tukar Slot</strong> agar posisinya saling berganti.
                                            </div>
                                        ) : (
                                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#166534' }}>
                                                <i className="bx bx-check-circle" style={{ marginRight: 4 }}></i>
                                                Slot tujuan ({this.renderHariLabel(m.target_hari)} Jam ke-{m.target_jam_ke}) <strong>kosong</strong> dan siap ditempati.
                                            </div>
                                        )}

                                        <div className="pc-modal-actions">
                                            <button
                                                type="button"
                                                className="pc-btn pc-btn-primary"
                                                disabled={step4_cell_saving || (Number(m.target_hari) === Number(m.hari) && Number(m.target_jam_ke) === Number(m.jam_ke))}
                                                onClick={() => this.executeStep4Move(false)}
                                            >
                                                {step4_cell_saving ? <><i className="bx bx-loader-alt bx-spin"></i> Memindahkan...</> : <><i className="bx bx-transfer"></i> Pindahkan ke Slot Ini</>}
                                            </button>
                                            <button
                                                type="button"
                                                className="pc-btn pc-btn-ghost"
                                                onClick={() => this.setState({ step4_active_cell_modal: null })}
                                            >
                                                Batal
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* TAB 2: TUKAR SLOT */}
                                {(m.action_tab === 'tukar') && (
                                    <div>
                                        <p style={{ fontSize: 12.5, color: '#64748b', margin: '0 0 12px' }}>
                                            Tukarkan posisi jadwal <strong>{m.mapel_nama}</strong> dengan jadwal lain di rombel <strong>{m.rombel_nama}</strong>.
                                        </p>

                                        {/* Visual mode trigger button */}
                                        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: '#0369a1', marginBottom: 4 }}>
                                                <i className="bx bx-pointer" style={{ marginRight: 4 }}></i> Tukar Langsung Secara Visual di Tabel
                                            </div>
                                            <p style={{ fontSize: 12, color: '#0284c7', margin: '0 0 10px' }}>
                                                Gunakan kursor untuk klik slot target secara langsung pada tabel timetable.
                                            </p>
                                            <button
                                                type="button"
                                                className="pc-btn pc-btn-sm pc-btn-primary"
                                                onClick={() => {
                                                    const src = {
                                                        id: m.id,
                                                        versi_id: m.versi_id,
                                                        rombel_nama: m.rombel_nama,
                                                        hari: m.hari,
                                                        jam_ke: m.jam_ke,
                                                        mapel_nama: m.mapel_nama,
                                                        guru_nama: m.guru_nama,
                                                        waktuLabel: m.waktuLabel
                                                    };
                                                    this.setState({
                                                        step4_active_cell_modal: null,
                                                        step4_is_visual_swap: true,
                                                        step4_visual_swap_source: src
                                                    });
                                                    this.showMessage(`Slot 1 (${m.rombel_nama} ${this.renderHariLabel(m.hari)} Jam ke-${m.jam_ke}) dipilih. Sekarang klik slot kedua di tabel untuk menukar!`, "info");
                                                }}
                                            >
                                                <i className="bx bx-transfer-alt"></i> Aktifkan Mode Tukar di Tabel
                                            </button>
                                        </div>

                                        {/* Dropdown swap select */}
                                        {otherFilledSlots.length > 0 && (
                                            <div style={{ marginTop: 12 }}>
                                                <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
                                                    Atau Pilih Jadwal Tujuan dari Daftar di Rombel Ini:
                                                </label>
                                                <select
                                                    className="pc-input"
                                                    value={m.swap_target_key || ''}
                                                    onChange={(e) => this.setState(prev => ({
                                                        step4_active_cell_modal: {
                                                            ...prev.step4_active_cell_modal,
                                                            swap_target_key: e.target.value
                                                        }
                                                    }))}
                                                >
                                                    <option value="">-- Pilih Slot Jadwal Lain --</option>
                                                    {otherFilledSlots.map(j => (
                                                        <option key={`${j.id || `${j.hari}_${j.jam_ke}`}`} value={`${j.hari}_${j.jam_ke}`}>
                                                            {this.renderHariLabel(j.hari)} Jam ke-{j.jam_ke} : {j.mapel_nama} ({j.guru_nama})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        <div className="pc-modal-actions" style={{ marginTop: 16 }}>
                                            {m.swap_target_key && (
                                                <button
                                                    type="button"
                                                    className="pc-btn pc-btn-primary"
                                                    onClick={() => {
                                                        const [h, jk] = m.swap_target_key.split('_').map(Number);
                                                        const targetSlot = otherFilledSlots.find(j => Number(j.hari) === h && Number(j.jam_ke) === jk);
                                                        if (targetSlot) {
                                                            const slotA = {
                                                                id: m.id,
                                                                versi_id: m.versi_id,
                                                                rombel_nama: m.rombel_nama,
                                                                hari: m.hari,
                                                                jam_ke: m.jam_ke,
                                                                mapel_nama: m.mapel_nama,
                                                                guru_nama: m.guru_nama,
                                                                waktuLabel: m.waktuLabel
                                                            };
                                                            const slotB = {
                                                                id: targetSlot.id,
                                                                versi_id: targetSlot.versi_id,
                                                                rombel_nama: targetSlot.rombel_nama,
                                                                hari: targetSlot.hari,
                                                                jam_ke: targetSlot.jam_ke,
                                                                mapel_nama: targetSlot.mapel_nama,
                                                                guru_nama: targetSlot.guru_nama,
                                                                waktuLabel: ''
                                                            };
                                                            this.setState({
                                                                step4_active_cell_modal: null,
                                                                step4_swap_confirm_modal: { slot_a: slotA, slot_b: slotB, bentrokan_error: null }
                                                            });
                                                        }
                                                    }}
                                                >
                                                    <i className="bx bx-transfer-alt"></i> Konfirmasi Tukar
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="pc-btn pc-btn-ghost"
                                                onClick={() => this.setState({ step4_active_cell_modal: null })}
                                            >
                                                Batal
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* TAB 3: GANTI MAPEL / GURU */}
                                {(m.action_tab === 'ganti') && (
                                    <div>
                                        <p style={{ fontSize: 12.5, color: '#64748b', margin: '0 0 12px' }}>
                                            Ubah mata pelajaran atau guru pengajar untuk slot pada <strong>{this.renderHariLabel(m.hari)} Jam ke-{m.jam_ke}</strong> ini.
                                        </p>

                                        <div className="pc-form-group" style={{ marginBottom: 14 }}>
                                            <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
                                                Pilih Mapel & Guru dari Penugasan Rombel {m.rombel_nama}:
                                            </label>
                                            <select
                                                className="pc-input"
                                                value={m.selected_penugasan}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    const [mapel, guru] = val.split('|||');
                                                    this.setState(prev => ({
                                                        step4_active_cell_modal: {
                                                            ...prev.step4_active_cell_modal,
                                                            selected_penugasan: val,
                                                            mapel_nama: mapel,
                                                            guru_nama: guru,
                                                            bentrokan_error: null
                                                        }
                                                    }));
                                                }}
                                            >
                                                {rombelPenugasan.map((p, pi) => (
                                                    <option key={pi} value={`${p.mapel_nama}|||${p.guru_nama}`}>
                                                        {p.mapel_nama} &bull; {p.guru_nama} ({p.jumlah_jam_per_minggu || 2} jam/mgg)
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="pc-modal-actions">
                                            <button
                                                type="button"
                                                className="pc-btn pc-btn-primary"
                                                disabled={step4_cell_saving || !m.mapel_nama || !m.guru_nama}
                                                onClick={() => this.executeStep4FillSlot(false)}
                                            >
                                                {step4_cell_saving ? <><i className="bx bx-loader-alt bx-spin"></i> Menyimpan...</> : <><i className="bx bx-save"></i> Simpan Perubahan</>}
                                            </button>
                                            <button
                                                type="button"
                                                className="pc-btn pc-btn-ghost"
                                                onClick={() => this.setState({ step4_active_cell_modal: null })}
                                            >
                                                Batal
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* TAB 4: KOSONGKAN SLOT */}
                                {(m.action_tab === 'kosongkan') && (
                                    <div>
                                        <div style={{ textAlign: 'center', padding: '16px 0' }}>
                                            <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: 26 }}>
                                                <i className="bx bx-trash"></i>
                                            </div>
                                            <h4 style={{ margin: '0 0 6px', fontSize: 16, color: '#1e293b' }}>Kosongkan Slot Jadwal Ini?</h4>
                                            <p style={{ fontSize: 13, color: '#64748b', maxWidth: 420, margin: '0 auto' }}>
                                                Jadwal <strong>{m.mapel_nama} ({m.guru_nama})</strong> pada <strong>{this.renderHariLabel(m.hari)} Jam ke-{m.jam_ke}</strong> di rombel <strong>{m.rombel_nama}</strong> akan dihapus dan slot akan menjadi kosong.
                                            </p>
                                        </div>

                                        <div className="pc-modal-actions">
                                            <button
                                                type="button"
                                                className="pc-btn pc-btn-danger"
                                                disabled={step4_cell_saving}
                                                onClick={() => this.executeStep4DeleteSlot()}
                                            >
                                                {step4_cell_saving ? <><i className="bx bx-loader-alt bx-spin"></i> Menghapus...</> : <><i className="bx bx-trash"></i> Ya, Kosongkan Slot</>}
                                            </button>
                                            <button
                                                type="button"
                                                className="pc-btn pc-btn-ghost"
                                                onClick={() => this.setState({ step4_active_cell_modal: null })}
                                            >
                                                Batal
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* TAB ISI: FOR EMPTY SLOT */}
                                {(!isEditing || m.action_tab === 'isi') && (
                                    <div>
                                        <p style={{ fontSize: 12.5, color: '#64748b', margin: '0 0 12px' }}>
                                            Pilih mata pelajaran dan guru pengajar untuk mengisi slot kosong ini.
                                        </p>

                                        <div className="pc-form-group" style={{ marginBottom: 14 }}>
                                            <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
                                                Pilih Mapel & Guru Penugasan ({m.rombel_nama}):
                                            </label>
                                            <select
                                                className="pc-input"
                                                value={m.selected_penugasan}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    const [mapel, guru] = val.split('|||');
                                                    this.setState(prev => ({
                                                        step4_active_cell_modal: {
                                                            ...prev.step4_active_cell_modal,
                                                            selected_penugasan: val,
                                                            mapel_nama: mapel,
                                                            guru_nama: guru,
                                                            bentrokan_error: null
                                                        }
                                                    }));
                                                }}
                                            >
                                                {rombelPenugasan.length === 0 ? (
                                                    <option value="">Tidak ada data penugasan untuk rombel ini</option>
                                                ) : (
                                                    rombelPenugasan.map((p, pi) => (
                                                        <option key={pi} value={`${p.mapel_nama}|||${p.guru_nama}`}>
                                                            {p.mapel_nama} &bull; {p.guru_nama} ({p.jumlah_jam_per_minggu || 2} jam/mgg)
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>

                                        <div className="pc-modal-actions">
                                            <button
                                                type="button"
                                                className="pc-btn pc-btn-primary"
                                                disabled={step4_cell_saving || !m.mapel_nama || !m.guru_nama}
                                                onClick={() => this.executeStep4FillSlot(false)}
                                            >
                                                {step4_cell_saving ? <><i className="bx bx-loader-alt bx-spin"></i> Menyimpan...</> : <><i className="bx bx-plus"></i> Isi Slot Jadwal</>}
                                            </button>
                                            <button
                                                type="button"
                                                className="pc-btn pc-btn-ghost"
                                                onClick={() => this.setState({ step4_active_cell_modal: null })}
                                            >
                                                Batal
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* Modal: Konfirmasi Swap / Tukar Posisi Cepat */}
                {step4_swap_confirm_modal && (() => {
                    const { slot_a, slot_b, bentrokan_error } = step4_swap_confirm_modal;
                    const isSlotBEmpty = !slot_b.id && !slot_b.mapel_nama;
                    const isSlotAEmpty = !slot_a.id && !slot_a.mapel_nama;

                    return (
                        <div className="pc-modal-overlay" onClick={() => this.setState({ step4_swap_confirm_modal: null })}>
                            <div className="pc-modal-content pc-modal-md" onClick={e => e.stopPropagation()}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                    <div>
                                        <h4 style={{ margin: '0 0 4px', fontSize: 16 }}>
                                            <i className="bx bx-transfer-alt"></i>
                                            {isSlotBEmpty || isSlotAEmpty ? 'Konfirmasi Pindahkan Jadwal' : 'Konfirmasi Tukar Posisi Jadwal'}
                                        </h4>
                                        <div style={{ fontSize: 12.5, color: '#64748b' }}>
                                            {isSlotBEmpty
                                                ? `Slot akan dipindahkan ke slot kosong tujuan.`
                                                : `Kedua slot di bawah ini akan saling bertukar posisi.`}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="pc-btn pc-btn-xs pc-btn-ghost"
                                        onClick={() => this.setState({ step4_swap_confirm_modal: null })}
                                        style={{ color: '#94a3b8', fontSize: 18, padding: 4 }}
                                    >
                                        <i className="bx bx-x"></i>
                                    </button>
                                </div>

                                {/* Side-by-side comparison cards */}
                                <div className="pc-swap-comparison-container">
                                    <div className="pc-swap-card">
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', marginBottom: 4 }}>
                                            Slot 1 (Sumber)
                                        </div>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                                            {slot_a.rombel_nama}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#64748b' }}>
                                            {this.renderHariLabel(slot_a.hari)}, Jam ke-{slot_a.jam_ke}
                                        </div>
                                        <div style={{ marginTop: 8, padding: '6px 8px', background: slot_a.mapel_nama ? '#ffffff' : '#f1f5f9', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: slot_a.mapel_nama ? '#1e293b' : '#94a3b8' }}>
                                                {slot_a.mapel_nama || '(Slot Kosong)'}
                                            </div>
                                            <div style={{ fontSize: 11, color: '#64748b' }}>
                                                {slot_a.guru_nama || '—'}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pc-swap-arrow">
                                        <i className="bx bx-transfer"></i>
                                    </div>

                                    <div className="pc-swap-card">
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#059669', textTransform: 'uppercase', marginBottom: 4 }}>
                                            Slot 2 (Tujuan)
                                        </div>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                                            {slot_b.rombel_nama}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#64748b' }}>
                                            {this.renderHariLabel(slot_b.hari)}, Jam ke-{slot_b.jam_ke}
                                        </div>
                                        <div style={{ marginTop: 8, padding: '6px 8px', background: slot_b.mapel_nama ? '#ffffff' : '#f1f5f9', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: slot_b.mapel_nama ? '#1e293b' : '#94a3b8' }}>
                                                {slot_b.mapel_nama || '(Slot Kosong)'}
                                            </div>
                                            <div style={{ fontSize: 11, color: '#64748b' }}>
                                                {slot_b.guru_nama || '—'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Conflict Alert (if any from backend check) */}
                                {bentrokan_error && (
                                    <div style={{ background: '#fef2f2', border: '1px solid #f87171', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#dc2626', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                                            <i className="bx bx-error-circle" style={{ fontSize: 18 }}></i>
                                            <span>{bentrokan_error.message}</span>
                                        </div>
                                        {bentrokan_error.bentrokan?.map((b, bi) => (
                                            <div key={bi} style={{ fontSize: 12, color: '#991b1b', marginLeft: 24, marginTop: 2 }}>
                                                &bull; {b.pesan || `${b.guru} bentrok di ${b.rombel}`}
                                            </div>
                                        ))}
                                        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                                            <button
                                                type="button"
                                                className="pc-btn pc-btn-xs"
                                                style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '5px 12px', fontWeight: 600 }}
                                                disabled={step4_cell_saving}
                                                onClick={() => this.executeStep4Swap(true)}
                                            >
                                                {step4_cell_saving ? <><i className="bx bx-loader-alt bx-spin"></i> Memproses...</> : 'Tetap Tukar (Force / Abaikan Bentrok)'}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="pc-modal-actions">
                                    <button
                                        type="button"
                                        className="pc-btn pc-btn-primary"
                                        disabled={step4_cell_saving}
                                        onClick={() => this.executeStep4Swap(false)}
                                    >
                                        {step4_cell_saving ? <><i className="bx bx-loader-alt bx-spin"></i> Menukar...</> : <><i className="bx bx-transfer-alt"></i> {isSlotBEmpty || isSlotAEmpty ? 'Pindahkan Sekarang' : 'Tukar Sekarang'}</>}
                                    </button>
                                    <button
                                        type="button"
                                        className="pc-btn pc-btn-ghost"
                                        onClick={() => this.setState({ step4_swap_confirm_modal: null })}
                                    >
                                        Batal
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>
        );
    }

    renderStep4Input(rombelNames, dayEntries, currentHari, effectiveHari) {
        const { input_jadwal, input_editing_cell, input_saving, penugasan_list,
            versi_draft_id, generate_result, jadwal_preview } = this.state;
        const hariList = effectiveHari || this.state.hari_aktif;

        // Build per-rombel mapel options, grouping agama into one package
        const rombelMapelMap = {};
        rombelNames.forEach(rn => {
            const penugasan = penugasan_list.filter(p => p.rombel_nama === rn);
            const mapelMap = {};
            let agamaGroup = null;
            penugasan.forEach(p => {
                if (p.kategori_mapel === 'agama') {
                    if (!agamaGroup) {
                        agamaGroup = { mapel_nama: 'Pendidikan Agama', mapel_kode: '', is_agama: true, gurus: [], agama_items: [] };
                    }
                    const itemExists = agamaGroup.agama_items.some(a => a.mapel_nama === p.mapel_nama && a.guru_nama === p.guru_nama);
                    if (!itemExists) {
                        agamaGroup.agama_items.push({ mapel_nama: p.mapel_nama, mapel_kode: p.mapel_kode, guru_nama: p.guru_nama, guru_nip: p.guru_nip });
                    }
                } else {
                    if (!mapelMap[p.mapel_nama]) {
                        mapelMap[p.mapel_nama] = { mapel_nama: p.mapel_nama, mapel_kode: p.mapel_kode, gurus: [] };
                    }
                    const guruExists = mapelMap[p.mapel_nama].gurus.some(g => g.guru_nama === p.guru_nama);
                    if (!guruExists) {
                        mapelMap[p.mapel_nama].gurus.push({ guru_nama: p.guru_nama, guru_nip: p.guru_nip });
                    }
                }
            });
            const options = Object.values(mapelMap);
            if (agamaGroup) options.unshift(agamaGroup);
            rombelMapelMap[rn] = options;
        });

        const conflicts = this.getInputConflicts();
        const totalFilled = Object.values(input_jadwal).reduce((sum, cells) => sum + Object.keys(cells).length, 0);

        // Editing state helpers
        const editRombel = input_editing_cell?.rombel;
        const editHari = input_editing_cell?.hari;
        const editJamKe = input_editing_cell?.jam_ke;
        const editStep = input_editing_cell?.step || 'mapel'; // 'mapel' or 'guru'
        const editMapel = input_editing_cell?.mapel_nama;

        return (
            <div className="pc-input-manual-section">
                {/* Toolbar */}
                <div className="pc-input-toolbar">
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>
                            <i className="bx bx-info-circle" style={{ marginRight: 4 }}></i>
                            Klik cell kosong untuk mengisi jadwal. Semua rombel ditampilkan sekaligus.
                        </div>
                        <div style={{ fontSize: 12, color: '#b45309' }}>
                            {totalFilled > 0 ? `${totalFilled} slot terisi di ${Object.keys(input_jadwal).filter(k => Object.keys(input_jadwal[k]).length > 0).length} rombel` : 'Belum ada slot yang diisi'}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                        <button className="pc-btn pc-btn-primary" disabled={input_saving || totalFilled === 0}
                            onClick={() => this.saveInputJadwal()}>
                            {input_saving ? <><i className="bx bx-loader-alt bx-spin"></i> Menyimpan...</>
                                : <><i className="bx bx-save"></i> Simpan Jadwal ({totalFilled})</>}
                        </button>
                        {versi_draft_id && jadwal_preview.length > 0 && (
                            <button onClick={() => this.setState({ show_save_versi_modal: true, new_versi_name: '' })}
                                className="pc-btn pc-btn-secondary">
                                <i className="bx bx-archive-in"></i> Simpan Versi
                            </button>
                        )}
                    </div>
                </div>

                {/* Conflicts */}
                {conflicts.length > 0 && (
                    <div className="pc-alert pc-alert-warning" style={{ marginTop: 12 }}>
                        <i className="bx bx-error"></i> <strong>{conflicts.length} bentrokan guru:</strong>
                        <ul className="pc-alert-list">
                            {conflicts.slice(0, 5).map((c, i) => (
                                <li key={i}><i className="bx bx-error-circle"></i> {c.guru} bentrok di {c.rombel_1} & {c.rombel_2}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Result alert */}
                {generate_result && (
                    <div className={`pc-alert ${generate_result.has_bentrokan ? "pc-alert-warning" : "pc-alert-success"}`} style={{ marginTop: 12 }}>
                        <i className={`bx ${generate_result.has_bentrokan ? "bx-error" : "bx-check-circle"}`}></i>
                        <strong>{generate_result.has_bentrokan ? `Disimpan dengan ${generate_result.bentrokan?.length || 0} catatan` : 'Jadwal berhasil disimpan!'}</strong>
                        <span style={{ marginLeft: 8 }}>{generate_result.total_jadwal} entri</span>
                    </div>
                )}

                {/* Day tabs */}
                <div className="pc-timetable-tabs" style={{ marginTop: 12 }}>
                    {hariList.map(h => (
                        <button key={h}
                            className={`pc-timetable-tab ${currentHari === h ? 'pc-timetable-tab-active' : ''}`}
                            onClick={() => this.setState({ preview_hari_tab: h, input_editing_cell: null })}>
                            {this.renderHariLabel(h)}
                        </button>
                    ))}
                </div>

                {/* Multi-Rombel Grid */}
                <div className="pc-timetable-container">
                    <table className="pc-timetable pc-input-table">
                        <thead>
                            <tr>
                                <th className="pc-tt-th-jam" style={{ minWidth: 50 }}>Jam</th>
                                <th className="pc-tt-th-time" style={{ minWidth: 75 }}>Waktu</th>
                                {rombelNames.map(rn => (
                                    <th key={rn} className="pc-tt-th-rombel">{rn}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {dayEntries.map((entry, ei) => {
                                const isBreak = entry.is_istirahat || entry.tipe === 'istirahat' || entry.tipe === 'upacara' || entry.tipe === 'kegiatan';
                                const timeStr = `${this.formatTime(entry.waktu_mulai)} - ${this.formatTime(entry.waktu_selesai)}`;
                                if (isBreak) {
                                    return (
                                        <tr key={`break_${ei}`} className={`pc-tt-break-row pc-tt-break-${entry.tipe || 'istirahat'}`}>
                                            <td className="pc-tt-break-jam"><i className="bx bx-coffee"></i></td>
                                            <td className="pc-tt-break-time">{timeStr}</td>
                                            <td colSpan={rombelNames.length} className="pc-tt-break-label">
                                                <span className={`pc-tt-break-badge pc-tt-break-${entry.tipe || 'istirahat'}`}>
                                                    {entry.keterangan || entry.tipe || 'Istirahat'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                }

                                return (
                                    <tr key={`jam_${entry.jam_ke}_${ei}`} className="pc-tt-row">
                                        <td className="pc-tt-jam">{entry.jam_ke}</td>
                                        <td className="pc-tt-time">{timeStr}</td>
                                        {rombelNames.map(rn => {
                                            const cellKey = `${currentHari}_${entry.jam_ke}`;
                                            const cellData = input_jadwal[rn]?.[cellKey];
                                            const isEditing = editRombel === rn && editHari === currentHari && editJamKe === entry.jam_ke;
                                            const mapelOptions = rombelMapelMap[rn] || [];

                                            return (
                                                <td key={rn} className={`pc-input-cell ${cellData ? 'pc-input-cell-filled' : 'pc-input-cell-empty'} ${isEditing ? 'pc-input-cell-editing' : ''}`}>
                                                    {isEditing ? (
                                                        <div className="pc-input-dropdown">
                                                            {editStep === 'mapel' ? (
                                                                /* Step 1: Pick Mapel */
                                                                <select className="pc-input pc-input-sm" autoFocus
                                                                    value={cellData?.is_agama ? '__agama__' : (cellData?.mapel_nama || '')}
                                                                    onChange={(e) => {
                                                                        if (e.target.value === '') {
                                                                            this.clearInputCell(rn, currentHari, entry.jam_ke);
                                                                            return;
                                                                        }
                                                                        if (e.target.value === '__agama__') {
                                                                            const agamaOpt = mapelOptions.find(m => m.is_agama);
                                                                            if (agamaOpt) this.setInputCellAgama(rn, currentHari, entry.jam_ke, agamaOpt.agama_items);
                                                                            return;
                                                                        }
                                                                        const mapel = mapelOptions.find(m => m.mapel_nama === e.target.value);
                                                                        if (!mapel) return;
                                                                        if (mapel.gurus.length === 1) {
                                                                            this.setInputCell(rn, currentHari, entry.jam_ke, mapel.mapel_nama, mapel.mapel_kode, mapel.gurus[0].guru_nama, mapel.gurus[0].guru_nip);
                                                                        } else {
                                                                            this.setState({
                                                                                input_editing_cell: { rombel: rn, hari: currentHari, jam_ke: entry.jam_ke, step: 'guru', mapel_nama: mapel.mapel_nama, mapel_kode: mapel.mapel_kode, guru_options: mapel.gurus }
                                                                            });
                                                                        }
                                                                    }}
                                                                    onBlur={() => setTimeout(() => { if (this.state.input_editing_cell?.step === 'mapel') this.setState({ input_editing_cell: null }); }, 150)}>
                                                                    <option value="">— Pilih Mapel —</option>
                                                                    {mapelOptions.map((m, i) => (
                                                                        <option key={i} value={m.is_agama ? '__agama__' : m.mapel_nama}>
                                                                            {m.is_agama ? `✡ Pendidikan Agama (${m.agama_items.length} agama)` : m.mapel_nama + (m.gurus.length > 1 ? ` (${m.gurus.length} guru)` : ` — ${m.gurus[0]?.guru_nama || ''}`)}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            ) : (
                                                                /* Step 2: Pick Guru */
                                                                <>
                                                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#4f46e5', marginBottom: 4, padding: '0 4px' }}>
                                                                        <i className="bx bx-book-open" style={{ marginRight: 3 }}></i>{editMapel}
                                                                    </div>
                                                                    <select className="pc-input pc-input-sm" autoFocus
                                                                        value=""
                                                                        onChange={(e) => {
                                                                            if (!e.target.value) return;
                                                                            const guru = input_editing_cell.guru_options?.find(g => g.guru_nama === e.target.value);
                                                                            if (guru) {
                                                                                this.setInputCell(rn, currentHari, entry.jam_ke, editMapel, input_editing_cell.mapel_kode, guru.guru_nama, guru.guru_nip);
                                                                            }
                                                                        }}
                                                                        onBlur={() => setTimeout(() => this.setState({ input_editing_cell: null }), 150)}>
                                                                        <option value="">— Pilih Guru —</option>
                                                                        {(input_editing_cell.guru_options || []).map((g, i) => (
                                                                            <option key={i} value={g.guru_nama}>{g.guru_nama}</option>
                                                                        ))}
                                                                    </select>
                                                                </>
                                                            )}
                                                        </div>
                                                    ) : cellData ? (
                                                        <div className={`pc-input-cell-content ${cellData.is_agama ? 'pc-input-cell-agama' : ''}`}
                                                            onClick={() => this.setState({ input_editing_cell: { rombel: rn, hari: currentHari, jam_ke: entry.jam_ke, step: 'mapel' } })}>
                                                            {cellData.is_agama ? (
                                                                <div className="pc-tt-agama-stack">
                                                                    {cellData.items.map((item, ai) => (
                                                                        <div key={ai} className="pc-tt-agama-item">
                                                                            <div className="pc-tt-mapel">{item.mapel_nama}</div>
                                                                            <div className="pc-tt-guru">{item.guru_nama}</div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <div className="pc-tt-mapel">{cellData.mapel_nama}</div>
                                                                    <div className="pc-tt-guru">{cellData.guru_nama}</div>
                                                                </>
                                                            )}
                                                            <button className="pc-input-cell-clear"
                                                                onClick={(e) => { e.stopPropagation(); this.clearInputCell(rn, currentHari, entry.jam_ke); }}
                                                                title="Hapus">
                                                                <i className="bx bx-x"></i>
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="pc-input-cell-placeholder"
                                                            onClick={() => this.setState({ input_editing_cell: { rombel: rn, hari: currentHari, jam_ke: entry.jam_ke, step: 'mapel' } })}>
                                                            <i className="bx bx-plus"></i>
                                                        </div>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    render() {
        const { currentStep, message, messageType, settings_locked } = this.state;
        const steps = [
            { num: 0, label: "Pengaturan", icon: "bx-cog" },
            { num: 1, label: "Waktu & Hari", icon: "bx-time-five" },
            { num: 2, label: "Mapel & Guru", icon: "bx-book-open" },
            { num: 3, label: "Ketersediaan Guru", icon: "bx-user-check" },
            { num: 4, label: "Buat Jadwal", icon: "bx-calendar-check" },
        ];

        return (
            <div className="pc-container">
                <div className="pc-header pc-header-glass">
                    <div className="pc-header-content">
                        <div className="pc-header-text">
                            <h1 className="pc-header-title"><i className="bx bx-magic-wand"></i> Wizard Penjadwalan</h1>
                            <p className="pc-header-subtitle">Buat jadwal pelajaran langkah demi langkah</p>
                        </div>
                        {settings_locked && (
                            <div className="pc-header-settings-badge">
                                <span><i className="bx bx-calendar"></i> {this.state.tahun_ajaran}</span>
                                <span><i className="bx bx-bookmarks"></i> Smt {this.state.semester}</span>
                                <span><i className="bx bx-book-alt"></i> {this.state.kurikulum === 'merdeka' ? 'Merdeka' : 'K13'}</span>
                                <span><i className="bx bx-group"></i> {this.state.selected_kelas.join(", ")}</span>
                                <button className="pc-header-edit-btn" onClick={() => this.unlockSettings()} title="Ubah Pengaturan">
                                    <i className="bx bx-edit"></i>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Message */}
                {message && (
                    <div className={`pc-toast ${messageType === "error" ? "pc-toast-error" : "pc-toast-success"}`}>
                        <i className={`bx ${messageType === "error" ? "bx-error" : "bx-check-circle"}`}></i>
                        {message}
                    </div>
                )}

                {/* Stepper */}
                <div className="pc-stepper">
                    {steps.map((s) => (
                        <div
                            key={s.num}
                            className={`pc-step ${currentStep === s.num ? "pc-step-active" : ""} ${currentStep > s.num ? "pc-step-done" : ""}`}
                            onClick={() => {
                                // Step 0: unlock settings to go back
                                if (s.num === 0 && settings_locked) {
                                    this.unlockSettings();
                                    return;
                                }
                                // Only allow steps 1-4 if settings are locked
                                if (s.num > 0 && !settings_locked) return;
                                this.setState({ currentStep: s.num }, () => sessionStorage.setItem('pc_wizard_step', s.num));
                            }}
                        >
                            <div className="pc-step-circle">
                                {currentStep > s.num ? <i className="bx bx-check"></i> : s.num}
                            </div>
                            <span className="pc-step-label">{s.label}</span>
                        </div>
                    ))}
                </div>

                {/* Content */}
                {currentStep === 0 && this.renderStep0()}
                {currentStep === 1 && this.renderStep1()}
                {currentStep === 2 && this.renderStep2()}
                {currentStep === 3 && this.renderStep3()}
                {currentStep === 4 && this.renderStep4()}

                {/* Navigation */}
                {currentStep > 0 && (
                    <div className="pc-wizard-nav">
                        {currentStep === 1 ? (
                            <button onClick={() => this.unlockSettings()} className="pc-btn pc-btn-outline">
                                <i className="bx bx-cog"></i> Kembali ke Pengaturan
                            </button>
                        ) : currentStep > 1 ? (
                            <button onClick={() => this.setState({ currentStep: currentStep - 1 }, () => sessionStorage.setItem('pc_wizard_step', currentStep - 1))} className="pc-btn pc-btn-outline">
                                <i className="bx bx-chevron-left"></i> Sebelumnya
                            </button>
                        ) : null}
                        <div className="pc-wizard-nav-spacer"></div>
                        {currentStep < 4 && (
                            <button onClick={() => this.setState({ currentStep: currentStep + 1 }, () => sessionStorage.setItem('pc_wizard_step', currentStep + 1))} className="pc-btn pc-btn-primary">
                                Selanjutnya <i className="bx bx-chevron-right"></i>
                            </button>
                        )}
                    </div>
                )}
                {/* Delete Selection Modal for multi-guru mapels */}
                {this.state.delete_modal && (() => {
                    const dm = this.state.delete_modal;
                    const selectedCount = dm.guru_list.filter(g => g.selected).length;
                    const allSelected = selectedCount === dm.guru_list.length;
                    return (
                        <div className="pc-modal-overlay" onClick={() => this.setState({ delete_modal: null })}
                            style={{ zIndex: 9999, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)' }}>
                            <div onClick={e => e.stopPropagation()} style={{
                                background: '#fff', borderRadius: 16, maxWidth: 440, width: '90%', margin: 'auto',
                                boxShadow: '0 25px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.05)',
                                overflow: 'hidden', animation: 'modalSlideIn 0.25s ease',
                            }}>
                                {/* Header */}
                                <div style={{
                                    background: 'linear-gradient(135deg, #fef2f2, #fff1f2)',
                                    padding: '20px 24px', borderBottom: '1px solid #fecaca',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div style={{
                                            width: 40, height: 40, borderRadius: 10, display: 'flex',
                                            alignItems: 'center', justifyContent: 'center',
                                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                                            color: '#fff', fontSize: 18,
                                        }}>
                                            <i className="bx bx-trash"></i>
                                        </div>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1e293b' }}>Hapus Penugasan</h3>
                                            <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>{dm.mapel_nama}</p>
                                        </div>
                                    </div>
                                    <button onClick={() => this.setState({ delete_modal: null })}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8', padding: 4 }}>
                                        <i className="bx bx-x"></i>
                                    </button>
                                </div>

                                {/* Body */}
                                <div style={{ padding: '20px 24px' }}>
                                    <p style={{ margin: '0 0 16px', fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
                                        {dm.rombel_nama ? <><strong>{dm.mapel_nama}</strong> di <strong>{dm.rombel_nama}</strong></> : <strong>{dm.mapel_nama}</strong>}
                                        {' '}memiliki <strong>{dm.guru_list.length} guru</strong> yang ditugaskan.
                                        Pilih guru yang ingin dihapus penugasannya:
                                    </p>

                                    {/* Select all */}
                                    <label style={{
                                        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                                        padding: '6px 0', fontSize: 12, color: '#64748b', cursor: 'pointer',
                                    }}>
                                        <input type="checkbox" checked={allSelected}
                                            onChange={() => {
                                                const newList = dm.guru_list.map(g => ({ ...g, selected: !allSelected }));
                                                this.setState({ delete_modal: { ...dm, guru_list: newList } });
                                            }}
                                            style={{ accentColor: '#ef4444' }}
                                        />
                                        <span style={{ fontWeight: 600 }}>Pilih Semua</span>
                                    </label>

                                    {/* Guru list */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
                                        {dm.guru_list.map((g, i) => (
                                            <label key={i} style={{
                                                display: 'flex', alignItems: 'center', gap: 10,
                                                padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                                                background: g.selected ? '#fef2f2' : '#f8fafc',
                                                border: g.selected ? '2px solid #fca5a5' : '2px solid transparent',
                                                boxShadow: g.selected ? '0 0 0 3px rgba(239,68,68,0.1)' : 'none',
                                                transition: 'all 0.15s ease',
                                            }}>
                                                <input type="checkbox" checked={g.selected || false}
                                                    onChange={() => {
                                                        const newList = [...dm.guru_list];
                                                        newList[i] = { ...newList[i], selected: !newList[i].selected };
                                                        this.setState({ delete_modal: { ...dm, guru_list: newList } });
                                                    }}
                                                    style={{ accentColor: '#ef4444' }}
                                                />
                                                <div style={{
                                                    width: 32, height: 32, borderRadius: 8, display: 'flex',
                                                    alignItems: 'center', justifyContent: 'center',
                                                    background: g.selected ? '#fee2e2' : '#e2e8f0',
                                                    color: g.selected ? '#dc2626' : '#64748b', fontSize: 14,
                                                }}>
                                                    <i className="bx bx-user"></i>
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{g.guru_nama}</span>
                                                    {g.mapel_detail && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{g.mapel_detail}</div>}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Footer */}
                                <div style={{
                                    padding: '16px 24px', borderTop: '1px solid #f1f5f9',
                                    display: 'flex', gap: 10, justifyContent: 'flex-end', background: '#fafafa',
                                }}>
                                    <button onClick={() => this.setState({ delete_modal: null })} style={{
                                        padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                                        border: '1px solid #e2e8f0', background: '#fff', color: '#475569',
                                        cursor: 'pointer', transition: 'all 0.15s',
                                    }}>
                                        Batal
                                    </button>
                                    <button
                                        onClick={() => {
                                            const selected = dm.guru_list.filter(g => g.selected);
                                            if (selected.length === 0) {
                                                // If none selected, delete all
                                                this.removePenugasan(dm.all_idxs);
                                            } else {
                                                this.removePenugasan(selected.map(g => g._idx));
                                            }
                                        }}
                                        disabled={selectedCount === 0}
                                        style={{
                                            padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                                            border: 'none', color: '#fff', cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
                                            background: selectedCount > 0 ? 'linear-gradient(135deg, #ef4444, #dc2626)' : '#d1d5db',
                                            boxShadow: selectedCount > 0 ? '0 4px 12px rgba(239,68,68,0.3)' : 'none',
                                            transition: 'all 0.15s',
                                        }}
                                    >
                                        <i className="bx bx-trash" style={{ marginRight: 4 }}></i>
                                        Hapus {selectedCount > 0 ? `(${selectedCount})` : ''}
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>
        );
    }
}